import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import SearchableSelect from '../components/ui/SearchableSelect';
import toast from 'react-hot-toast';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Constants ────────────────────────────────────────────────────
const ZONES        = ['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5'];
const UNIVERSITIES = ['APJ','POLY','ANNA','CALICUT','MG','KANNUR','KERALA','CUSAT','NACTET','Other'];
const DISTRICTS    = ['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam',
                      'Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode',
                      'Wayanad','Kannur','Kasaragod','Coimbatore','Kanyakumari','Other'];
const STREAMS      = ['Mechanical','Civil','Automobile','Chemical','Aeronautical','Electrical','Production','Mechatronics','Industrial'];
const EXAM_MODES   = ['Offline','Online','Nomination'];
const DELIVERY_METHODS = ['Courier','Direct Delivery','Email'];
const EVAL_LOCATIONS   = ['At College','At Office'];
const RESULT_MEDIUMS   = ['WhatsApp','Email','Visit','Phone'];
const COMM_TYPES       = ['WhatsApp','Call','Email','Visit'];
const JOIN_STATUS      = ['Joined','Not Joined','Interested','Follow Up'];
const TRAIN_STATUS     = ['Ongoing','Completed','Dropped','Not Started'];
const PACKAGES         = ['MP','CP','MP+PGDCEM-M','PGDCEM-M','DCEM-C','PGDCEM-C','Other'];
const COORD_ROLES      = ['HOD','Faculty','Principal','Placement Officer','Other'];
const ARMSTECH_STAFF   = ['SMITHA','AMIYA','RAKHI','ANEECIA','ASHNA','SANIKA','NEETHU','ARAVIND','JEETHU','ANJANA','ARSHA','NISHA'];
const VISIT_STAFF = ['Adhithyan','Ershad','Aravind','Anumon','Rizal','Arshad','Akhil','Sudheesh','Sanil George','Sameer'];
const YEARS = [2027,2026,2025,2024,2023].map(String);

// A college is "inactive" when deactivated (is_active=false) or Shutdown/Converted.
// Student/Winner college dropdowns list only active colleges that have a program in the selected year.
const isColActive = c => c.is_active !== false && !['Shutdown','Converted'].includes(c.college_status || 'Running');
const collegeFilterOptions = (colleges, programs, year, zone) => {
  const inYear = year ? new Set((programs||[]).filter(p=>String(p.year)===String(year)).map(p=>p.college_id)) : null;
  return colleges
    .filter(c => isColActive(c) && (!zone || c.zone===zone) && (!inYear || inYear.has(c.id)))
    .map(c => ({value:c.id, label:c.college_name}));
};

const STATUS_FLOW = ['Pending','Low Probability','Registration','QB Sent','Exam Done','Evaluation Done','Results Shared','Certificates Ready','Distributed'];
const STATUS_COLORS = {
  'Pending':'badge-gray','Low Probability':'badge-red','Registration':'badge-blue','QB Sent':'badge-yellow',
  'Exam Done':'badge-yellow','Evaluation Done':'badge-amber','Results Shared':'badge-purple',
  'Certificates Ready':'badge-orange','Distributed':'badge-green'
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
}

// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
// VISIT TRACKING TAB
// ════════════════════════════════════════════════════════════════
const VISIT_PURPOSES = ['Exam Conduct','Certificate Distribution','QB Handover','Answer Sheet Collection','Career Guidance & Exam Conduct','Career Guidance'];
const VISIT_STATUSES = ['Planned','Called','Completed','Partially Done','Rescheduled'];

function VisitTrackingTab({ colleges, supabase }) {
  const { canEdit } = useAuth();
  const [view, setView] = React.useState('plans'); // plans | new | report
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selPlan, setSelPlan] = React.useState(null);
  const [stops, setStops] = React.useState([]);
  const [rescheduledStops, setRescheduledStops] = React.useState([]);
  const [rescheduledPositions, setRescheduledPositions] = React.useState({}); // {stopId: position}
  const [saving, setSaving] = React.useState(false);
  const [historyCollege, setHistoryCollege] = React.useState('');
  const [nvZone, setNvZone] = React.useState('');
  const [reportFilter, setReportFilter] = React.useState(null); // {type, value}
  const [nvSearch, setNvSearch] = React.useState('');
  const [stopDistances, setStopDistances] = React.useState([]);
  const [localStops, setLocalStops] = React.useState({});
  const [savedIndicator, setSavedIndicator] = React.useState({}); // stopId -> true/false
  const [showNearby, setShowNearby] = React.useState(false);
  const [nearbyBase, setNearbyBase] = React.useState('');
  const [nearbyRadius, setNearbyRadius] = React.useState(30);
  const [nearbyResults, setNearbyResults] = React.useState([]);
  const [nearbyLoading, setNearbyLoading] = React.useState(false);
  const [nearbySelected, setNearbySelected] = React.useState(new Set());
  const [nearbyPurpose, setNearbyPurpose] = React.useState('');
  const [searchCount, setSearchCount] = React.useState(0);
  const [showTemplates, setShowTemplates] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  const [editingPlanId, setEditingPlanId] = React.useState(null);
  const [templateName, setTemplateName] = React.useState('');
  const [teams, setTeams] = React.useState([]);
  const [showTeamMgr, setShowTeamMgr] = React.useState(false);
  const [teamForm, setTeamForm] = React.useState({team_name:'',member_1:'',member_2:'',notes:''});
  const [editingTeam, setEditingTeam] = React.useState(null);

  // New plan form
  const emptyPlan = { plan_date:'', team_name:'', team_member_1:'', team_member_2:'', notes:'' };
  const [form, setForm] = React.useState(emptyPlan);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));
  const [selColleges, setSelColleges] = React.useState([]); // [{college_id, purposes:[], visit_order}]
  const [colSearch, setColSearch] = React.useState('');

  // Filters
  const [fStatus, setFStatus] = React.useState('');
  const [fFrom, setFFrom] = React.useState('');
  const [fTo, setFTo] = React.useState('');
  const [fTeam, setFTeam] = React.useState('');
  const [fCollege, setFCollege] = React.useState('');
  const [fZoneFilter, setFZoneFilter] = React.useState('');

  React.useEffect(() => { loadPlans(); loadTeams(); loadTemplates(); loadSearchCount(); }, []);
  useLiveRefresh(['visit_plans', 'visit_stops', 'visit_teams'], () => { loadPlans(true); loadTeams(); }, true, 2000);

  async function loadSearchCount() {
    const month = new Date().toISOString().slice(0,7);
    const { data } = await supabase.from('api_usage')
      .select('count').eq('api_name','distance_matrix').eq('month',month).single();
    setSearchCount(data?.count || 0);
  }

  async function loadTeams() {
    const { data } = await supabase.from('visit_teams').select('*').eq('is_active', true).order('team_name');
    setTeams(data || []);
  }

  async function loadTemplates() {
    const { data } = await supabase.from('visit_teams')
      .select('*').eq('is_active', false).like('team_name', 'TEMPLATE:%').order('team_name');
    setTemplates(data||[]);
  }

  async function saveAsTemplate() {
    if (!templateName) { toast.error('Enter template name'); return; }
    if (!selColleges.length) { toast.error('Add colleges first'); return; }
    setSavingTemplate(true);
    const collegeIds = selColleges.map(s=>s.college_id).join(',');
    const purposes = selColleges.map(s=>(s.purposes||[]).join('|')).join(';');
    await supabase.from('visit_teams').insert({
      team_name: 'TEMPLATE:'+templateName,
      member_1: collegeIds,
      member_2: purposes,
      is_active: false
    });
    toast.success('Template saved!');
    setTemplateName('');
    setSavingTemplate(false);
    loadTemplates();
  }

  async function loadTemplate(t) {
    const collegeIds = t.member_1?.split(',') || [];
    const purposesList = t.member_2?.split(';') || [];
    const toAdd = collegeIds.map((id,i) => ({
      college_id: id,
      purposes: purposesList[i]?.split('|').filter(Boolean) || [],
      visit_order: i+1
    })).filter(s=>colleges.find(c=>c.id===s.college_id));
    setSelColleges(toAdd);
    setShowTemplates(false);
    toast.success(`Loaded ${toAdd.length} colleges from template`);
  }

  async function deleteTemplate(id) {
    await supabase.from('visit_teams').delete().eq('id', id);
    loadTemplates();
  }

  async function saveTeam() {
    if (!teamForm.team_name) { toast.error('Enter team name'); return; }
    const payload = { team_name: teamForm.team_name, member_1: teamForm.member_1, member_2: teamForm.member_2, notes: teamForm.notes };
    if (editingTeam) {
      await supabase.from('visit_teams').update(payload).eq('id', editingTeam);
    } else {
      await supabase.from('visit_teams').insert(payload);
    }
    toast.success('Team saved');
    setTeamForm({team_name:'',member_1:'',member_2:'',notes:''});
    setEditingTeam(null);
    loadTeams();
  }

  async function deleteTeam(id) {
    if (!window.confirm('Delete this team?')) return;
    await supabase.from('visit_teams').update({is_active:false}).eq('id', id);
    loadTeams();
  }

  async function loadPlans(silent) {
    if (silent !== true) setLoading(true);
    const { data } = await supabase.from('visit_plans')
      .select('*, visit_stops(*, nactet_colleges(college_name, zone, district, latitude, longitude))')
      .order('plan_date', { ascending: false })
      .order('visit_order', {referencedTable:'visit_stops', ascending:true});
    setPlans(data || []);
    setLoading(false);
  }

  async function savePlan() {
    if (!form.plan_date) { toast.error('Select a date'); return; }
    if (!selColleges.length) { toast.error('Add at least one college'); return; }
    setSaving(true);
    if (editingPlanId) {
      // UPDATE existing plan header
      const { error } = await supabase.from('visit_plans')
        .update({ plan_date: form.plan_date, team_name: form.team_name,
          team_member_1: form.team_member_1, team_member_2: form.team_member_2,
          notes: form.notes })
        .eq('id', editingPlanId);
      if (error) { toast.error(error.message); setSaving(false); return; }
      // Update each existing stop's purposes and stream_times
      // Add new stops, keep existing ones
      const existingStopIds = selColleges.filter(s=>s.stop_id).map(s=>s.stop_id);
      // Delete stops removed by user
      const { data: currentStops } = await supabase.from('visit_stops')
        .select('id').eq('plan_id', editingPlanId);
      const toDelete = (currentStops||[]).filter(s=>!existingStopIds.includes(s.id));
      if (toDelete.length) await supabase.from('visit_stops').delete().in('id', toDelete.map(s=>s.id));
      // Update existing and insert new stops
      for (let i=0; i<selColleges.length; i++) {
        const s = selColleges[i];
        if (s.stop_id) {
          await supabase.from('visit_stops').update({
            visit_order: i+1, purposes: s.purposes, stream_times: s.stream_times||null
          }).eq('id', s.stop_id);
        } else {
          await supabase.from('visit_stops').insert({
            plan_id: editingPlanId, college_id: s.college_id,
            visit_order: i+1, purposes: s.purposes,
            visit_status: 'Planned', stream_times: s.stream_times||null
          });
        }
      }
      toast.success('Visit plan updated!');
      setEditingPlanId(null);
    } else {
      // INSERT new plan
      const { data: plan, error } = await supabase.from('visit_plans')
        .insert({ ...form, status: 'Planned' }).select().single();
      if (error) { toast.error(error.message); setSaving(false); return; }
      const stopRows = selColleges.map((s, i) => ({
        plan_id: plan.id, college_id: s.college_id,
        visit_order: i + 1, purposes: s.purposes,
        visit_status: 'Planned', stream_times: s.stream_times||null,
      }));
      await supabase.from('visit_stops').insert(stopRows);
      toast.success('Visit plan created!');
    }
    setForm(emptyPlan);
    setSelColleges([]);
    setView('plans');
    loadPlans();
    setSaving(false);
  }

  async function reoptimizeRoute(planId, fixedStartId=null) {
    if (!stops.length) return;
    toast.loading('Calculating road distances...', {id:'route'});
    // Fetch fresh data with coordinates directly from DB
    const { data: freshStops } = await supabase.from('visit_stops')
      .select('id, visit_order, nactet_colleges(college_name, latitude, longitude)')
      .eq('plan_id', planId).order('visit_order');
    if (!freshStops?.length) { toast.dismiss('route'); return; }
    const withCoords = freshStops.map(s => ({
      ...s, lat: Number(s.nactet_colleges?.latitude), lng: Number(s.nactet_colleges?.longitude)
    }));
    if (withCoords.some(s => !s.lat || !s.lng)) {
      toast.error('Some colleges missing coordinates', {id:'route'}); return;
    }
    // Build road distance matrix using OSRM (free, no API key)
    const n = withCoords.length;
    const coords = withCoords.map(s => `${s.lng},${s.lat}`).join(';');
    let distMatrix = null;
    try {
      const osrmUrl = `https://router.project-osrm.org/table/v1/driving/${coords}?annotations=distance`;
      const res = await fetch(osrmUrl);
      const data = await res.json();
      if (data.code === 'Ok') distMatrix = data.distances;
    } catch(e) { console.log('OSRM failed, using straight-line', e); }
    // Fallback to straight-line if OSRM fails
    const getRoadDist = (i, j) => {
      if (distMatrix) return distMatrix[i][j];
      const a = withCoords[i], b = withCoords[j];
      return Math.sqrt(Math.pow(a.lat-b.lat,2)+Math.pow(a.lng-b.lng,2));
    };
    // Index-based permutations - keep first stop fixed as start
    const nStops = withCoords.length;
    // Fix first stop, permute the rest
    const restIndices = withCoords.slice(1).map((_,i)=>i+1);
    function permIdx(arr) {
      if(arr.length<=1) return [arr];
      return arr.flatMap((v,i)=>permIdx([...arr.slice(0,i),...arr.slice(i+1)]).map(p=>[v,...p]));
    }
    const totalRoadDist = idxArr => idxArr.reduce((sum,idx,i)=>i===0?0:sum+getRoadDist(idxArr[i-1],idx),0);
    let bestRoute = null, bestDist = Infinity;
    // Find fixed start index if specified
    const allIndices = withCoords.map((_,i)=>i);
    const fixedStartIdx = fixedStartId ? withCoords.findIndex(s=>s.id===fixedStartId) : -1;
    function permIdx(arr) {
      if(arr.length<=1) return [arr];
      return arr.flatMap((v,i)=>permIdx([...arr.slice(0,i),...arr.slice(i+1)]).map(p=>[v,...p]));
    }
    let permsToCheck;
    if (fixedStartIdx >= 0) {
      // Fix start, permute the rest
      const restIdx = allIndices.filter(i=>i!==fixedStartIdx);
      permsToCheck = restIdx.length<=7
        ? permIdx(restIdx).map(p=>[fixedStartIdx,...p])
        : [restIdx.reduce((route,_)=>{
            const last=route[route.length-1]; let mi=0,md=Infinity;
            const unvisited=restIdx.filter(x=>!route.includes(x));
            unvisited.forEach((idx,j)=>{const d=getRoadDist(last,idx);if(d<md){md=d;mi=j;}});
            return [...route,unvisited[mi]];
          },[fixedStartIdx])];
    } else {
      // No fixed start - try all permutations
      permsToCheck = nStops<=8
        ? permIdx(allIndices)
        : allIndices.map(startI=>{
            const unvisited=[...allIndices.filter(x=>x!==startI)]; const route=[startI];
            while(unvisited.length){const last=route[route.length-1];let mi=0,md=Infinity;
              unvisited.forEach((idx,j)=>{const d=getRoadDist(last,idx);if(d<md){md=d;mi=j;}});
              route.push(unvisited.splice(mi,1)[0]);}
            return route;
          });
    }
    for (const idxArr of permsToCheck) {
      const d = totalRoadDist(idxArr);
      if (d < bestDist) { bestDist = d; bestRoute = idxArr.map(i=>withCoords[i]); }
    }
    // Update visit_order in DB sequentially
    for (let i=0; i<bestRoute.length; i++) {
      await supabase.from('visit_stops').update({visit_order:i+1}).eq('id',bestRoute[i].id);
    }
    toast.success('Route optimized using road distances!', {id:'route'});
    loadStops(planId);
  }

  async function moveStop(stopId, direction) {
    const idx = stops.findIndex(s => s.id === stopId);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === stops.length-1) return;
    const swapIdx = direction === 'up' ? idx-1 : idx+1;
    const newStops = [...stops];
    [newStops[idx], newStops[swapIdx]] = [newStops[swapIdx], newStops[idx]];
    setStops(newStops);
    // Update visit_order for regular stops only
    const regularUpdates = newStops
      .filter(s=>!s._rescheduled)
      .map((s,i) => supabase.from('visit_stops').update({visit_order:i+1}).eq('id',s.id));
    // Save rescheduled stop positions to current plan's extra_data
    const newPositions = {};
    newStops.forEach((s,i)=>{ if(s._rescheduled) newPositions[s.id]=i+1; });
    if(Object.keys(newPositions).length>0 && selPlan?.id && !String(selPlan.id).startsWith('virtual_')) {
      const currentExtra = selPlan?.extra_data||{};
      await supabase.from('visit_plans').update({
        extra_data: {...currentExtra, rescheduled_positions: newPositions}
      }).eq('id', selPlan.id);
      setRescheduledPositions(newPositions);
      // Update local plans state
      loadPlans();
    }
    await Promise.all(regularUpdates);
  }

  async function setStartCollege(stopId) {
    // Move selected stop to position 1, shift others down
    const idx = stops.findIndex(s => s.id === stopId);
    if (idx === 0) return;
    const newStops = [stops[idx], ...stops.slice(0,idx), ...stops.slice(idx+1)];
    setStops(newStops);
    for (let i=0; i<newStops.length; i++) {
      await supabase.from('visit_stops').update({visit_order:i+1}).eq('id',newStops[i].id);
    }
  }

  async function editPlan(plan) {
    setEditingPlanId(plan.id);
    setForm({ plan_date: plan.plan_date, team_name: plan.team_name||'',
      team_member_1: plan.team_member_1||'', team_member_2: plan.team_member_2||'',
      notes: plan.notes||'' });
    // Load existing stops with all saved fields - DO NOT DELETE
    const { data: existingStops } = await supabase.from('visit_stops')
      .select('id, college_id, purposes, visit_order, stream_times')
      .eq('plan_id', plan.id).order('visit_order');
    setSelColleges(existingStops?.map(s=>({
      college_id: s.college_id,
      stop_id: s.id,
      purposes: s.purposes||[],
      visit_order: s.visit_order,
      stream_times: s.stream_times||{},
    }))||[]);
    setView('new');
    toast('Edit plan details and save', {icon:'✏️'});
  }

  async function deletePlan(planId) {
    if (!window.confirm('Delete this visit plan and all its stops?')) return;
    await supabase.from('visit_stops').delete().eq('plan_id', planId);
    await supabase.from('visit_plans').delete().eq('id', planId);
    toast.success('Plan deleted');
    loadPlans();
  }

  async function findNearbyColleges() {
    if (!nearbyBase) { toast.error('Select a base college'); return; }
    setNearbyLoading(true);
    setNearbyResults([]);
    const baseCol = colleges.find(c => c.id === nearbyBase);
    if (!baseCol?.latitude || !baseCol?.longitude) {
      toast.error('Base college has no coordinates'); setNearbyLoading(false); return;
    }
    // Get colleges with coordinates
    const withCoords = colleges.filter(c => c.latitude && c.longitude && c.id !== nearbyBase);
    // Use OSRM table API - sends all colleges in one request
    const allPoints = [baseCol, ...withCoords];
    const coords = allPoints.map(c => `${c.longitude},${c.latitude}`).join(';');
    try {
      const res = await fetch(`https://router.project-osrm.org/table/v1/driving/${coords}?annotations=distance&sources=0`);
      const data = await res.json();
      if (data.code === 'Ok') {
        const distances = data.distances[0];
        // Validate: if more than 50% of distances are suspiciously low (<1km) for colleges that should be far, OSRM data is bad
        const results = withCoords.map((col, i) => {
          const d = distances[i+1];
          // Straight-line distance as sanity check
          const slat = Math.abs(col.latitude - baseCol.latitude);
          const slng = Math.abs(col.longitude - baseCol.longitude);
          const slKm = Math.sqrt(slat*slat + slng*slng) * 111; // rough km
          // If OSRM says <1km but straight-line is >5km, trust straight-line
          const roadKm = (d && d > 0)
            ? (d < 1000 && slKm > 5 ? slKm.toFixed(1) : (d/1000).toFixed(1))
            : slKm.toFixed(1);
          return { ...col, roadKm };
        })
        .filter(c => Number(c.roadKm) <= nearbyRadius && Number(c.roadKm) > 0.05)
        .sort((a, b) => Number(a.roadKm) - Number(b.roadKm));
        const visitedIds = new Set(plans.flatMap(p =>
          (p.visit_stops||[]).filter(s => ['Visited','Completed'].includes(s.visit_status)).map(s => s.college_id)
        ));
        const withVisited = results.map(c => ({...c, recentlyVisited: visitedIds.has(c.id)}));
        setNearbyResults(withVisited);
        // Track API usage in DB
        const month = new Date().toISOString().slice(0,7);
        await supabase.from('api_usage').upsert(
          { api_name:'nearby_search', month, count: searchCount+1, updated_at: new Date().toISOString() },
          { onConflict: 'api_name,month' }
        );
        setSearchCount(c => c+1);
        toast.success(`Found ${results.length} colleges within ${nearbyRadius}km`);
      } else { toast.error('Distance service unavailable'); }
    } catch(e) { toast.error('Failed to fetch distances'); }
    setNearbyLoading(false);
  }

  function addNearbyToplan() {
    const toAdd = nearbyResults.filter(c => nearbySelected.has(c.id));
    const existing = new Set(selColleges.map(s => s.college_id));
    const newOnes = toAdd.filter(c => !existing.has(c.id));
    setSelColleges(prev => [...prev, ...newOnes.map((c,i) => ({
      college_id: c.id, purposes: nearbyPurpose?[nearbyPurpose]:[], visit_order: prev.length+i+1
    }))]);
    setShowNearby(false);
    setNearbySelected(new Set());
    toast.success(`${newOnes.length} colleges added to plan`);
  }

  async function loadStops(planId) {
    // Handle virtual plan (rescheduled date with no real plan)
    // Virtual plan id format: 'virtual_DATE_ORIGINALPLANID'
    if(String(planId).startsWith('virtual_')) {
      const parts = String(planId).replace('virtual_','').split('_');
      const planDate = parts[0]; // first part is the date YYYY-MM-DD
      const originalPlanId = parts.slice(1).join('_'); // rest is the original plan id
      // Fetch stops from the original plan that have this rescheduled_date
      const { data: rd } = await supabase.from('visit_stops')
        .select('*, nactet_colleges(college_name, zone, district, latitude, longitude)')
        .eq('rescheduled_date', planDate)
        .eq('plan_id', originalPlanId);
      const rescheduled = (rd||[]).map(s=>({...s, _rescheduled: true}));
      setStops(rescheduled);
      setRescheduledStops([]);
      const local = {};
      rescheduled.forEach(s => { local[s.id] = {...s}; });
      setLocalStops(local);
      setStopDistances([]);
      return;
    }
    const { data } = await supabase.from('visit_stops')
      .select('*, nactet_colleges(college_name, zone, district, latitude, longitude)')
      .eq('plan_id', planId).order('visit_order');
    // Also fetch stops from OTHER plans with SAME TEAM rescheduled to this plan's date
    const currentPlan = plans.find(p=>p.id===planId);
    const planDate = currentPlan?.plan_date;
    const planTeam = currentPlan?.team_name;
    let rescheduled = [];
    if(planDate) {
      const { data: rd } = await supabase.from('visit_stops')
        .select('*, nactet_colleges(college_name, zone, district, latitude, longitude)')
        .eq('rescheduled_date', planDate)
        .neq('plan_id', planId);
      // Only include stops from plans with the same team
      const sameteamPlanIds = plans
        .filter(p=>p.team_name===planTeam && p.id!==planId)
        .map(p=>p.id);
      rescheduled = (rd||[])
        .filter(s=>sameteamPlanIds.includes(s.plan_id))
        .map(s=>({...s, _rescheduled: true}));
    }
    // Load saved rescheduled positions from plan's extra_data
    const savedPositions = currentPlan?.extra_data?.rescheduled_positions || {};
    const regularStops = data||[];
    const maxOrder = regularStops.reduce((m,s)=>Math.max(m,s.visit_order||0),0);
    const rescheduledWithOrder = rescheduled.map((s,i)=>({
      ...s,
      // Use saved position if available, otherwise append after regular stops
      visit_order: savedPositions[s.id] !== undefined ? savedPositions[s.id] : maxOrder+i+1
    }));
    setRescheduledPositions(savedPositions);
    // Merge all stops into one array sorted by visit_order
    const allMerged = [...regularStops, ...rescheduledWithOrder].sort((a,b)=>(a.visit_order||0)-(b.visit_order||0));
    setStops(allMerged);
    setRescheduledStops([]);
    // Init local state for smooth editing
    const local = {};
    allMerged.forEach(s => { local[s.id] = {...s}; });
    setLocalStops(local);
    // Fetch road distances between consecutive stops using OSRM
    if (data && data.length > 1) {
      const withCoords = data.filter(s => s.nactet_colleges?.latitude && s.nactet_colleges?.longitude);
      if (withCoords.length > 1) {
        try {
          const coords = withCoords.map(s => `${s.nactet_colleges.longitude},${s.nactet_colleges.latitude}`).join(';');
          const res = await fetch(`https://router.project-osrm.org/table/v1/driving/${coords}?annotations=distance`);
          const osrm = await res.json();
          if (osrm.code === 'Ok') {
            const dists = withCoords.slice(1).map((s, i) => ({
              from: withCoords[i].id, to: s.id,
              km: (osrm.distances[i][i+1] / 1000).toFixed(1)
            }));
            setStopDistances(dists);
          }
        } catch(e) { setStopDistances([]); }
      }
    } else setStopDistances([]);
  }

  async function updateStop(stopId, updates) {
    // Update local state immediately for instant UI feedback
    setStops(prev => prev.map(s => s.id===stopId ? {...s,...updates} : s));
    setLocalStops(p=>({...p,[stopId]:{...p[stopId],...updates}}));
    const { error } = await supabase.from('visit_stops').update(updates).eq('id', stopId);
    if (error) {
      console.error('updateStop error:', error);
      toast.error('Save failed: ' + error.message);
      // Revert local state on error
      setStops(prev => prev.map(s => s.id===stopId ? {...s,...Object.fromEntries(Object.keys(updates).map(k=>[k,s[k]]))} : s));
      return;
    }
    // Show saved indicator briefly
    setSavedIndicator(p=>({...p,[stopId]:true}));
    setTimeout(()=>setSavedIndicator(p=>({...p,[stopId]:false})), 2000);
    // Refresh plans list so front page badges stay in sync
    loadPlans();
  }

  function addCollege(colId) {
    if (selColleges.find(s => s.college_id === colId)) { toast.error('Already added'); return; }
    setSelColleges(prev => [...prev, { college_id: colId, purposes: [], visit_order: prev.length + 1 }]);
    setColSearch('');
  }

  function removeCollege(colId) {
    setSelColleges(prev => prev.filter(s => s.college_id !== colId).map((s,i) => ({...s, visit_order: i+1})));
  }

  function togglePurpose(colId, purpose) {
    setSelColleges(prev => prev.map(s => s.college_id === colId
      ? { ...s, purposes: s.purposes.includes(purpose) ? s.purposes.filter(p=>p!==purpose) : [...s.purposes, purpose] }
      : s
    ));
  }

  // Optimize route by proximity (nearest neighbor)
  function optimizeRoute() {
    const withCoords = selColleges.map(s => {
      const col = colleges.find(c => c.id === s.college_id);
      return { ...s, lat: col?.latitude, lng: col?.longitude };
    });
    if (withCoords.some(s => !s.lat || !s.lng)) {
      toast.error('Some colleges missing coordinates — add lat/lng first'); return;
    }
    const dist = (a, b) => Math.sqrt(Math.pow(a.lat-b.lat,2)+Math.pow(a.lng-b.lng,2));
    const totalDist = r => r.reduce((sum,s,i) => i===0?0:sum+dist(r[i-1],s), 0);
    function nnRoute(pts, startIdx) {
      const unvisited = [...pts];
      const route = [unvisited.splice(startIdx,1)[0]];
      while (unvisited.length) {
        const last = route[route.length-1];
        let minIdx=0, minD=Infinity;
        unvisited.forEach((s,i) => { const d=dist(last,s); if(d<minD){minD=d;minIdx=i;} });
        route.push(unvisited.splice(minIdx,1)[0]);
      }
      return route;
    }
    let bestRoute = null, bestDist = Infinity;
    for (let i=0; i<withCoords.length; i++) {
      const r = nnRoute(withCoords, i);
      const d = totalDist(r);
      if (d < bestDist) { bestDist = d; bestRoute = r; }
    }
    setSelColleges(bestRoute.map((s,i) => ({...s, visit_order: i+1})));
    toast.success('Route optimized by proximity!');
  }

  const filteredPlans = plans.filter(p =>
    (!fStatus || p.status === fStatus) &&
    (!fFrom || p.plan_date >= fFrom) &&
    (!fTo || p.plan_date <= fTo) &&
    (!fTeam || p.team_name === fTeam) &&
    (!fCollege || (p.visit_stops||[]).some(s=>s.college_id===fCollege)) &&
    (!fZoneFilter || (p.visit_stops||[]).some(s=>s.nactet_colleges?.zone===fZoneFilter))
  );

  // Map: plan_date -> rescheduled stops from OTHER plans, grouped by original plan
  const rescheduledStopsByDate = {}; // date -> stops (for existing plan cards)
  plans.forEach(p => {
    (p.visit_stops||[]).forEach(s => {
      if(s.rescheduled_date && s.rescheduled_date !== p.plan_date) {
        if(!rescheduledStopsByDate[s.rescheduled_date]) rescheduledStopsByDate[s.rescheduled_date]=[];
        rescheduledStopsByDate[s.rescheduled_date].push({...s, _originalPlanDate: p.plan_date, _originalPlanId: p.id, _originalPlan: p});
      }
    });
  });

  // Build virtual plan cards: one per (rescheduled_date + original_plan)
  // So different teams/zones don't get mixed into one card
  const existingPlanDates = new Set(plans.map(p=>p.plan_date));
  const virtualPlansMap = {}; // key: date_planId
  plans.forEach(p => {
    (p.visit_stops||[]).forEach(s => {
      if(s.rescheduled_date && s.rescheduled_date !== p.plan_date) {
        const key = s.rescheduled_date + '_' + p.id;
        if(!virtualPlansMap[key]) virtualPlansMap[key] = {
          id: 'virtual_' + key,
          plan_date: s.rescheduled_date,
          status: 'Rescheduled',
          team_name: p.team_name,
          team_member_1: p.team_member_1,
          team_member_2: p.team_member_2,
          notes: p.notes,
          _virtual: true,
          _originalPlanDate: p.plan_date,
          _originalPlanId: p.id,
          _rescheduledStops: []
        };
        virtualPlansMap[key]._rescheduledStops.push({...s, _originalPlanDate: p.plan_date, _originalPlanId: p.id});
      }
    });
  });
  // Show virtual cards when:
  // 1. No existing plan on that date at all, OR
  // 2. Existing plans on that date but none with matching team_name
  const virtualPlans = Object.values(virtualPlansMap).filter(v => {
    const plansOnDate = plans.filter(p=>p.plan_date===v.plan_date);
    if(plansOnDate.length===0) return true; // no plan on date
    const hasTeamMatch = plansOnDate.some(p=>p.team_name===v.team_name);
    return !hasTeamMatch; // show virtual if no matching team plan exists
  });

  const filteredCols = colleges.filter(c =>
    c.college_name?.toLowerCase().includes(colSearch.toLowerCase()) ||
    c.zone?.toLowerCase().includes(colSearch.toLowerCase()) ||
    c.district?.toLowerCase().includes(colSearch.toLowerCase())
  ).slice(0, 10);

  // Report stats
  const allStops = plans.flatMap(p => p.visit_stops || []);
  const totalVisited = allStops.filter(s => s.visit_status === 'Visited' || s.visit_status === 'Completed').length;
  const totalPending = allStops.filter(s => ['Planned','Called','Confirmed'].includes(s.visit_status)).length;
  const totalDeclined = allStops.filter(s => s.visit_status === 'Declined').length;
  const notDone = allStops.filter(s => s.visit_status === 'Visited' && !s.completed);

  return (
    <div>
      {/* Header */}
      <div className="card" style={{marginBottom:12,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:15}}>🗺️ College Visit Tracking</span>
        <button className={`btn btn-sm ${view==='plans'?'btn-primary':'btn-ghost'}`} onClick={()=>setView('plans')}>📋 All Plans</button>
        {canEdit('nactet_visits')&&<button className={`btn btn-sm ${view==='new'?'btn-primary':'btn-ghost'}`} onClick={()=>{setView('new');setForm(emptyPlan);setSelColleges([]);}}>➕ New Plan</button>}
        <button className={`btn btn-sm ${view==='report'?'btn-primary':'btn-ghost'}`} onClick={()=>setView('report')}>📊 Report</button>
        <button className={`btn btn-sm ${view==='history'?'btn-primary':'btn-ghost'}`} onClick={()=>setView('history')}>🏫 College History</button>
        {canEdit('nactet_visits')&&<button className="btn btn-ghost btn-sm" onClick={()=>setShowTeamMgr(true)}>👥 Manage Teams</button>}
      </div>

      {/* ALL PLANS VIEW */}
      {view==='plans'&&(
        <div>
          {/* Filters */}
          <div className="card" style={{marginBottom:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>
              <option value="">All Status</option>
              {VISIT_STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={fTeam} onChange={e=>setFTeam(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>
              <option value="">All Teams</option>
              {[...new Set(plans.map(p=>p.team_name).filter(Boolean))].sort().map(t=>(
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select value={fZoneFilter} onChange={e=>setFZoneFilter(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>
              <option value="">All Zones</option>
              {['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5'].map(z=>(
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
            <SearchableSelect value={fCollege} onChange={v=>setFCollege(v||'')}
              options={colleges.filter(c=>c.is_active&&c.college_status!=='Shutdown').map(c=>({value:c.id,label:c.college_name}))}
              placeholder="Filter by college..." searchPlaceholder="Search college..."
              style={{fontSize:12,minWidth:180}}/>
            <DateInput value={fFrom} onChange={e=>setFFrom(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}/>
            <span style={{fontSize:12}}>to</span>
            <DateInput value={fTo} onChange={e=>setFTo(e.target.value)} style={{fontSize:12,padding:'4px 8px'}}/>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setFStatus('');setFFrom('');setFTo('');setFTeam('');setFCollege('');setFZoneFilter('');}}>✕ Clear All</button>
            <span style={{fontSize:11,color:'var(--text3)',marginLeft:'auto'}}>{filteredPlans.length} plan(s)</span>
          </div>

          {loading?<div style={{textAlign:'center',padding:40}}>Loading...</div>:
          filteredPlans.length===0&&virtualPlans.length===0?<div className="card" style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No visit plans found</div>:
          [...filteredPlans, ...virtualPlans].sort((a,b)=>b.plan_date.localeCompare(a.plan_date)).map(plan=>(
            plan._virtual?(
              <div key={plan.id} className="card" style={{marginBottom:10,borderLeft:'3px solid var(--purple,#7c3aed)'}}>
                <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
                  <span style={{fontWeight:700,fontSize:14}}>📅 {new Date(plan.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                  <span className="badge" style={{background:'rgba(139,92,246,0.12)',color:'var(--purple,#7c3aed)'}}>🔄 Rescheduled</span>
                  {plan.team_name&&<span className="badge badge-blue">{plan.team_name}</span>}
                  {plan.team_member_1&&<span style={{fontSize:12,color:'var(--text2)'}}>👤 {plan.team_member_1}{plan.team_member_2?' & '+plan.team_member_2:''}</span>}
                  <span style={{fontSize:11,color:'var(--text3)'}}>from {plan._originalPlanDate}</span>
                  <span style={{fontSize:11,color:'var(--text3)',marginLeft:'auto'}}>{plan._rescheduledStops.length} college(s)</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setSelPlan(plan);loadStops(plan.id);setView('detail');}}>📋 Details</button>
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {plan._rescheduledStops.map(s=>(
                    <span key={'vr_'+s.id} style={{fontSize:11,padding:'2px 8px',borderRadius:4,
                      background:'rgba(139,92,246,0.12)',color:'var(--purple,#7c3aed)',
                      border:'1px solid rgba(139,92,246,0.3)'}}>
                      🔄 {s.nactet_colleges?.college_name||'?'} — {s.visit_status}
                      <span style={{fontSize:10,opacity:0.7}}> (orig: {s._originalPlanDate})</span>
                    </span>
                  ))}
                </div>
              </div>
            ):(
            <div key={plan.id} className="card" style={{marginBottom:10}}>
              <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
                <span style={{fontWeight:700,fontSize:14}}>📅 {new Date(plan.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                {plan.team_name&&<span className="badge badge-blue">{plan.team_name}</span>}
                {plan.team_member_1&&<span style={{fontSize:12,color:'var(--text2)'}}>👤 {plan.team_member_1}{plan.team_member_2?' & '+plan.team_member_2:''}</span>}
{(()=>{
                  const stops = plan.visit_stops||[];
                  const total = stops.length;
                  const completed = stops.filter(s=>s.visit_status==='Completed'||s.visit_status==='Partially Done').length;
                  const allDone = total>0 && stops.every(s=>s.visit_status==='Completed'||s.visit_status==='Partially Done'||s.visit_status==='Declined');
                  const derivedStatus = allDone ? 'Completed' : completed>0 ? 'Partially Done' : plan.status;
                  return <span className={`badge ${derivedStatus==='Completed'?'badge-green':derivedStatus==='Partially Done'?'badge-amber':derivedStatus==='Planned'?'badge-gray':'badge-blue'}`}>{derivedStatus}</span>;
                })()}
                <span style={{fontSize:11,color:'var(--text3)',marginLeft:'auto'}}>{(plan.visit_stops||[]).length} college(s)</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setSelPlan(plan);loadStops(plan.id);setView('detail');}}>📋 Details</button>
                {canEdit('nactet_visits')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>deletePlan(plan.id)}>🗑</button>}
              </div>
              {plan.notes&&<div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>📝 {plan.notes}</div>}
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {(plan.visit_stops||[]).slice().sort((a,b)=>(a.visit_order||0)-(b.visit_order||0)).map(s=>(
                  <span key={s.id} style={{fontSize:11,padding:'2px 8px',borderRadius:4,
                    background:s.visit_status==='Completed'?'var(--green-light)':s.visit_status==='Partially Done'?'rgba(245,158,11,0.1)':s.visit_status==='Rescheduled'?'rgba(139,92,246,0.1)':'var(--surface2)',
                    color:s.visit_status==='Completed'?'var(--green)':s.visit_status==='Partially Done'?'var(--amber)':s.visit_status==='Rescheduled'?'var(--purple)':'var(--text3)',
                    border:'1px solid var(--border)'}}>
                    {s.nactet_colleges?.college_name||'?'} — {s.visit_status}
                    {s.rescheduled_date&&s.rescheduled_date!==plan.plan_date&&<span style={{fontSize:10,opacity:0.8}}> 🔄→{s.rescheduled_date}</span>}
                  </span>
                ))}
                {/* Rescheduled stops: only show if original plan's team matches this plan's team */}
                {(rescheduledStopsByDate[plan.plan_date]||[]).filter(s=>
                  s._originalPlan?.team_name && plan.team_name &&
                  s._originalPlan.team_name === plan.team_name
                ).map(s=>(
                  <span key={'r_'+s.id} style={{fontSize:11,padding:'2px 8px',borderRadius:4,
                    background:s.visit_status==='Completed'?'var(--green-light)':s.visit_status==='Partially Done'?'rgba(245,158,11,0.1)':'rgba(139,92,246,0.12)',
                    color:s.visit_status==='Completed'?'var(--green)':s.visit_status==='Partially Done'?'var(--amber)':'var(--purple,#7c3aed)',
                    border:'1px solid var(--border)',display:'flex',alignItems:'center',gap:4}}>
                    🔄 {s.nactet_colleges?.college_name||'?'} — {s.visit_status}
                    <span style={{fontSize:10,opacity:0.7}}>(from {s._originalPlanDate})</span>
                  </span>
                ))}
              </div>
            </div>
            )
          ))}
        </div>
      )}

      {/* DETAIL VIEW */}
      {view==='detail'&&selPlan&&(
        <div>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setView('plans');setRescheduledStops([]);}}>← Back</button>
                        <button className="btn btn-ghost btn-sm" onClick={async()=>{
              const date = new Date(selPlan.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
              const team = selPlan.team_member_1+(selPlan.team_member_2?' & '+selPlan.team_member_2:'');
              const totalKm = stopDistances.reduce((s,d)=>s+Number(d.km),0).toFixed(1);
              const collegeIds = stops.map(s=>s.college_id);
              const [{ data: coords }, { data: progs }] = await Promise.all([
                supabase.from('nactet_college_coordinators').select('college_id,name,stream,role,phone').in('college_id',collegeIds).eq('is_current',true),
                supabase.from('nactet_programs').select('college_id,stream,total_students,year').in('college_id',collegeIds).order('year',{ascending:false})
              ]);
              let msg = `*Visit Plan - ${date}*\n`;
              msg += `Team: ${team}\n`;
              msg += `Route (${totalKm} km total):\n\n`;
              stops.forEach((s,i)=>{
                const dist = stopDistances.find(d=>d.to===s.id);
                const colCoords = (coords||[]).filter(c=>c.college_id===s.college_id);
                const colProgs = (progs||[]).filter(p=>p.college_id===s.college_id);
                const latestYear = colProgs[0]?.year;
                const yearProgs = colProgs.filter(p=>p.year===latestYear);
                msg += `${i+1}. *${s.nactet_colleges?.college_name}*\n`;
                msg += `   Purpose: ${(s.purposes||[]).join(', ')||'Visit'}\n`;
                const streamTimes = s.stream_times || localStops[s.id]?.stream_times || {};
                if(dist) msg += `   Distance from prev: ${dist.km} km\n`;
                const streams = [...new Set(colCoords.map(c=>c.stream).filter(Boolean))];
                if(streams.length>0) {
                  streams.forEach(stream=>{
                    const sc = colCoords.filter(c=>c.stream===stream);
                    const prog = yearProgs.find(p=>p.stream===stream);
                    msg += `   ${stream} Coordinator:\n`;
                    sc.forEach(c=>msg+=`     - ${c.name}${c.role?' ('+c.role+')':''}${c.phone?': '+c.phone:''}\n`);
                    if(streamTimes[stream]) msg += `   Time (${stream}): ${streamTimes[stream]}\n`;
                    if(prog?.total_students) msg += `   Total Students: ${prog.total_students}\n`;
                  });
                } else {
                  // No stream-wise, show all coordinators
                  colCoords.forEach(c=>msg+=`   Coordinator: ${c.name}${c.role?' ('+c.role+')':''}${c.phone?': '+c.phone:''}\n`);
                  const totalStudents = yearProgs.reduce((s,p)=>s+(p.total_students||0),0);
                  if(totalStudents>0) msg += `   Total Students: ${totalStudents}\n`;
                }
                if(s.remarks) msg += `   Remarks: ${s.remarks}\n`;
                if(s.alt_coordinator_name) msg += `   Alt Coordinator: ${s.alt_coordinator_name}${s.alt_coordinator_phone?' - '+s.alt_coordinator_phone:''}\n`;
                msg += '\n';
              });
              msg += '_Generated by Armstech ERP_';
              window.open('https://wa.me/?text='+encodeURIComponent(msg),'_blank');
            }}>📱 WhatsApp</button>
            {stops.length>1&&(
              <select style={{fontSize:11,padding:'3px 6px'}} defaultValue=''
                onChange={async e=>{
                  if(!e.target.value) return;
                  const stopId = e.target.value;
                  e.target.value='';
                  await setStartCollege(stopId);
                  await new Promise(r=>setTimeout(r,500));
                  await reoptimizeRoute(selPlan.id, stopId);
                }}>
                <option value=''>📍 Set Start & Optimize...</option>
                {stops.map((s,i)=><option key={s.id} value={s.id}>{i+1}. {s.nactet_colleges?.college_name}</option>)}
              </select>
            )}
          </div>
          <div className="card" style={{marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:6}}>
              📅 {new Date(selPlan.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
              {selPlan.team_name&&<span className="badge badge-blue" style={{marginLeft:8}}>{selPlan.team_name}</span>}
            </div>
            {selPlan.team_member_1&&<div style={{fontSize:12,marginBottom:4}}>👤 Team: {selPlan.team_member_1}{selPlan.team_member_2?' & '+selPlan.team_member_2:''}</div>}
            {selPlan.notes&&<div style={{fontSize:12,color:'var(--text3)'}}>📝 {selPlan.notes}</div>}
          </div>

          {stops.map((stop,i)=>(
            <React.Fragment key={stop.id}>
            {i>0&&stopDistances.find(d=>d.to===stop.id)&&(
              <div style={{textAlign:'center',fontSize:11,color:'var(--text3)',margin:'2px 0',display:'flex',alignItems:'center',gap:6,justifyContent:'center'}}>
                <span style={{flex:1,height:1,background:'var(--border)'}}></span>
                🚗 {stopDistances.find(d=>d.to===stop.id)?.km} km
                <span style={{flex:1,height:1,background:'var(--border)'}}></span>
              </div>
            )}
            <div className="card" style={{marginBottom:4}}>
              <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:8}}>
                <div style={{display:'flex',flexDirection:'column',gap:1}}>
                  <button className="btn btn-ghost" style={{padding:'0 4px',fontSize:10,lineHeight:1}} onClick={()=>moveStop(stop.id,'up')} disabled={i===0}>▲</button>
                  <button className="btn btn-ghost" style={{padding:'0 4px',fontSize:10,lineHeight:1}} onClick={()=>moveStop(stop.id,'down')} disabled={i===stops.length-1}>▼</button>
                </div>
                <span style={{fontWeight:600,fontSize:13}}>{i+1}. {stop.nactet_colleges?.college_name}</span>
                <span style={{fontSize:11,color:'var(--text3)'}}>{stop.nactet_colleges?.zone} | {stop.nactet_colleges?.district}</span>
                {(stop._rescheduled||(stop.rescheduled_date&&stop.visit_status==='Rescheduled'))&&(
                  <div style={{background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:6,padding:'4px 10px',fontSize:11,color:'var(--purple,#7c3aed)',marginBottom:8}}>
                    {stop._rescheduled
                      ? `🔄 Rescheduled to this date — originally from plan: ${plans.find(p=>p.id===stop.plan_id)?.plan_date||stop.plan_id}`
                      : `🔄 Rescheduled to: ${stop.rescheduled_date}`
                    }
                  </div>
                )}
                <select value={stop.visit_status} onChange={e=>updateStop(stop.id,{visit_status:e.target.value})}
                  style={{fontSize:11,padding:'2px 6px',marginLeft:'auto'}}>
                  {VISIT_STATUSES.map(s=><option key={s}>{s}</option>)}
                </select>
                {savedIndicator[stop.id]&&(
                  <span style={{fontSize:10,color:'var(--green)',fontWeight:600,
                    animation:'fadeIn 0.3s ease',whiteSpace:'nowrap'}}>✓ Saved</span>
                )}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                {(stop.purposes||[]).map(p=><span key={p} className="badge badge-blue" style={{fontSize:10}}>{p}</span>)}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:11,color:'var(--text3)',marginBottom:6,display:'block',fontWeight:600}}>📊 Stream-wise Status</label>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8}}>
                    {['Mechanical','Civil','Automobile','Electrical','Production','Mechatronics','Industrial'].map(stream=>{
                      const streamStatus = (localStops[stop.id]?.stream_status||{})[stream]||'';
                      return (
                        <div key={stream} style={{display:'flex',flexDirection:'column',gap:2,minWidth:120}}>
                          <label style={{fontSize:10,color:'var(--text3)'}}>{stream}</label>
                          <select value={streamStatus}
                            onChange={e=>{
                              const newStatus = {...(localStops[stop.id]?.stream_status||{}),[stream]:e.target.value};
                              setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],stream_status:newStatus}}));
                              updateStop(stop.id,{stream_status:newStatus});
                            }}
                            style={{fontSize:11,padding:'2px 4px',
                              background:streamStatus==='Completed'?'var(--green-light)':streamStatus==='Declined'?'rgba(239,68,68,0.1)':streamStatus==='Partially Done'?'rgba(245,158,11,0.1)':''}}>
                            <option value="">—</option>
                            <option value="Completed">✅ Completed</option>
                            <option value="Declined">✗ Declined</option>
                            <option value="Partially Done">⚡ Partially Done</option>
                            <option value="Rescheduled">🔄 Rescheduled</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:11,color:'var(--text3)',marginBottom:4,display:'block'}}>⏰ Visit Times (per stream)</label>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    {['Mechanical','Civil','Automobile','Electrical','Production','Mechatronics','Industrial'].map(stream=>(
                      <label key={stream} style={{fontSize:11,display:'flex',alignItems:'center',gap:4}}>
                        <span style={{minWidth:90,color:'var(--text2)'}}>{stream}:</span>
                        <input type="time"
                          value={(localStops[stop.id]?.stream_times||{})[stream]||''}
                          onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],stream_times:{...(p[stop.id]?.stream_times||{}),[stream]:e.target.value}}}))}
                          onBlur={e=>updateStop(stop.id,{stream_times:{...(localStops[stop.id]?.stream_times||{}),[stream]:e.target.value}})}
                          style={{fontSize:11,padding:'2px 4px',width:90}}/>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Call Date</label>
                  <DateInput value={localStops[stop.id]?.call_date||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],call_date:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{call_date:e.target.value})}
                    style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>

                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Call Notes</label>
                  <input value={localStops[stop.id]?.call_notes||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],call_notes:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{call_notes:e.target.value})}
                    placeholder="Notes from call..." style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>
                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Reschedule Date</label>
                  <DateInput value={localStops[stop.id]?.rescheduled_date||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],rescheduled_date:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{rescheduled_date:e.target.value})}
                    style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>
                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Actual Visit Date</label>
                  <DateInput value={localStops[stop.id]?.actual_visit_date||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],actual_visit_date:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{actual_visit_date:e.target.value})}
                    style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Visit Remarks</label>
                  <textarea value={localStops[stop.id]?.remarks||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],remarks:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{remarks:e.target.value})}
                    placeholder="What happened during visit..." rows={2}
                    style={{width:'100%',fontSize:11,padding:'3px 6px',resize:'vertical'}}/>
                </div>
                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Alt Coordinator Name</label>
                  <input value={localStops[stop.id]?.alt_coordinator_name||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],alt_coordinator_name:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{alt_coordinator_name:e.target.value})}
                    placeholder="If main coordinator absent..." style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>
                <div>
                  <label style={{fontSize:11,color:'var(--text3)'}}>Alt Coordinator Phone</label>
                  <input value={localStops[stop.id]?.alt_coordinator_phone||''}
                    onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],alt_coordinator_phone:e.target.value}}))}
                    onBlur={e=>updateStop(stop.id,{alt_coordinator_phone:e.target.value})}
                    placeholder="Phone number..." style={{width:'100%',fontSize:11,padding:'3px 6px'}}/>
                </div>
                {(stop.visit_status==='Visited'||stop.visit_status==='Completed')&&(
                  <div style={{gridColumn:'1/-1',display:'flex',gap:8,alignItems:'center'}}>
                    <label style={{fontSize:11}}>
                      <input type="checkbox" checked={localStops[stop.id]?.completed!==undefined ? !!localStops[stop.id].completed : !!stop.completed} onChange={e=>{setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],completed:e.target.checked}}));updateStop(stop.id,{completed:e.target.checked});}} style={{marginRight:4,width:16,height:16,cursor:'pointer'}}/>
                      Purpose completed
                    </label>
                    {!(localStops[stop.id]?.completed)&&<input value={localStops[stop.id]?.not_done_reason||''}
                      onChange={e=>setLocalStops(p=>({...p,[stop.id]:{...p[stop.id],not_done_reason:e.target.value}}))}
                      onBlur={e=>updateStop(stop.id,{not_done_reason:e.target.value})}
                      placeholder="Why not completed?" style={{flex:1,fontSize:11,padding:'3px 6px'}}/>}
                  </div>
                )}
              </div>
            </div>
            </React.Fragment>
          ))}
          {stopDistances.length>0&&(
            <div style={{textAlign:'right',fontSize:12,color:'var(--text3)',marginTop:8}}>
              Total route: 🚗 {stopDistances.reduce((s,d)=>s+Number(d.km),0).toFixed(1)} km
            </div>
          )}
        </div>
      )}

      {/* NEW PLAN VIEW */}
      {view==='new'&&(
        <div className="card">
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>➕ Create Visit Plan</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:12}}>
            <div className="form-group">
              <label>Visit Date *</label>
              <DateInput value={form.plan_date} onChange={e=>sf('plan_date',e.target.value)}/>
            </div>
            <div className="form-group">
              <label>Team Name</label>
              <select value={form.team_name}
                onChange={e=>{
                  const t = teams.find(t=>t.team_name===e.target.value);
                  sf('team_name',e.target.value);
                  if(t){ sf('team_member_1',t.member_1||''); sf('team_member_2',t.member_2||''); }
                }}>
                <option value="">Select Team...</option>
                          {teams.map(t=><option key={t.id} value={t.team_name}>{t.team_name}{t.member_1&&' ('+t.member_1+(t.member_2?' & '+t.member_2:'')+')'}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Member 1</label>
              <input value={form.team_member_1} onChange={e=>sf('team_member_1',e.target.value)}
                placeholder="Name" list="members-list"/>
              <datalist id="members-list">
                {[...new Set([...plans.map(p=>p.team_member_1),...plans.map(p=>p.team_member_2)].filter(Boolean))].map(m=>(
                  <option key={m} value={m}/>
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>Member 2</label>
              <input value={form.team_member_2} onChange={e=>sf('team_member_2',e.target.value)}
                placeholder="Name" list="members-list"/>
            </div>
            <div className="form-group" style={{gridColumn:'2/-1'}}>
              <label>Notes</label>
              <input value={form.notes} onChange={e=>sf('notes',e.target.value)} placeholder="Plan notes..."/>
            </div>
          </div>

          {/* College selector */}
          <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>Select Colleges</div>
          <div style={{display:'flex',gap:8,marginBottom:8}}>
            <input value={colSearch} onChange={e=>setColSearch(e.target.value)}
              placeholder="Search college by name/zone/district..." style={{flex:1,padding:'6px 10px',fontSize:12}}/>
            {selColleges.length>1&&<button className="btn btn-ghost btn-sm" onClick={optimizeRoute}>🗺️ Optimize Route</button>}
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowNearby(true)}>🎯 Suggest Nearby</button>
          </div>
          {/* Template save/load */}
          <div style={{display:'flex',gap:6,marginBottom:8,alignItems:'center',flexWrap:'wrap'}}>
            <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>{setShowTemplates(true);loadTemplates();}}>📋 Load Template</button>
            {selColleges.length>0&&(
              <>
                <input value={templateName} onChange={e=>setTemplateName(e.target.value)}
                  placeholder="Template name..." style={{fontSize:11,padding:'3px 8px',flex:1}}/>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}} disabled={savingTemplate} onClick={saveAsTemplate}>💾 Save as Template</button>
              </>
            )}
          </div>
          {colSearch&&filteredCols.length>0&&(
            <div style={{border:'1px solid var(--border)',borderRadius:6,marginBottom:8,maxHeight:200,overflowY:'auto'}}>
              {filteredCols.map(c=>(
                <div key={c.id} style={{padding:'6px 12px',cursor:'pointer',fontSize:12,borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}
                  onClick={()=>addCollege(c.id)}>
                  <span>{c.college_name}</span>
                  <span style={{fontSize:10,color:'var(--text3)'}}>{c.zone} | {c.district}</span>
                </div>
              ))}
            </div>
          )}

          {/* Selected colleges */}
          {selColleges.length>0&&(
            <div style={{marginBottom:12}}>
              {selColleges.map((s,i)=>{
                const col = colleges.find(c=>c.id===s.college_id);
                return (
                  <div key={s.college_id} style={{border:'1px solid var(--border)',borderRadius:6,padding:'8px 12px',marginBottom:6}}>
                    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                      <span style={{fontWeight:600,fontSize:12,color:'var(--accent)'}}>{i+1}.</span>
                      <span style={{fontWeight:500,fontSize:12}}>{col?.college_name}</span>
                      <span style={{fontSize:10,color:'var(--text3)'}}>{col?.zone} | {col?.district}</span>
                      {!col?.latitude&&<span style={{fontSize:10,color:'var(--amber)'}}>⚠️ No coords</span>}
                      <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto',color:'var(--red)',fontSize:10}} onClick={()=>removeCollege(s.college_id)}>✕</button>
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:6}}>
                      {VISIT_PURPOSES.map(p=>(
                        <label key={p} style={{fontSize:11,cursor:'pointer',display:'flex',gap:3,alignItems:'center',
                          padding:'2px 8px',borderRadius:4,border:'1px solid var(--border)',
                          background:s.purposes.includes(p)?'var(--accent)':'var(--surface)',
                          color:s.purposes.includes(p)?'#fff':'var(--text)'}}>
                          <input type="checkbox" checked={s.purposes.includes(p)} onChange={()=>togglePurpose(s.college_id,p)} style={{display:'none'}}/>
                          {p}
                        </label>
                      ))}
                    </div>
                    <div style={{marginTop:4}}>
                      <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>⏰ Visit Times (per stream):</div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {['Mechanical','Civil','Automobile','Electrical','Production','Mechatronics','Industrial'].map(stream=>(
                          <label key={stream} style={{fontSize:11,display:'flex',alignItems:'center',gap:4}}>
                            <span style={{minWidth:90,color:'var(--text2)'}}>{stream}:</span>
                            <input type="time" value={(s.stream_times||{})[stream]||''}
                              onChange={e=>setSelColleges(prev=>prev.map(sc=>sc.college_id===s.college_id
                                ?{...sc,stream_times:{...(sc.stream_times||{}),[stream]:e.target.value}}
                                :sc))}
                              style={{fontSize:11,padding:'2px 4px',width:90}}/>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setView('plans');setEditingPlanId(null);setSelColleges([]);setForm(emptyPlan);}}>Cancel</button>
            <button className="btn btn-primary" disabled={saving||!form.plan_date||!selColleges.length} onClick={savePlan}>
              {saving?'Saving...':editingPlanId?'💾 Update Plan':'💾 Save Plan'}
            </button>
          </div>
        </div>
      )}

      {/* TEMPLATES MODAL */}
      {showTemplates&&(
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowTemplates(false);}}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <span className="modal-title">📋 Visit Plan Templates</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowTemplates(false)}>✕</button>
            </div>
            <div style={{padding:16}}>
              {templates.length===0?
                <div style={{textAlign:'center',color:'var(--text3)',padding:30,fontSize:13}}>No templates saved yet.<br/>Add colleges to a plan and click "Save as Template".</div>:
                templates.map(t=>(
                  <div key={t.id} style={{display:'flex',gap:8,alignItems:'center',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:13}}>{t.team_name.replace('TEMPLATE:','')}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{(t.member_1||'').split(',').length} colleges</div>
                    </div>
                    <button className="btn btn-primary btn-sm" style={{fontSize:11}} onClick={()=>loadTemplate(t)}>Load</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--red)'}} onClick={()=>deleteTemplate(t.id)}>🗑</button>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* NEARBY COLLEGES MODAL */}
      {showNearby&&(
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowNearby(false);}}>
          <div className="modal" style={{maxWidth:800,maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
            <div className="modal-header">
              <span className="modal-title">🎯 Suggest Nearby Colleges</span>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <span style={{fontSize:11,padding:'2px 8px',borderRadius:4,
                  background:'var(--surface2)',border:'1px solid var(--border)',
                  color:searchCount>=130?'var(--red)':searchCount>=100?'var(--amber)':'var(--green)'}}>
                  📊 {searchCount}/149 searches this month
                </span>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowNearby(false)}>✕</button>
              </div>
            </div>
            <div style={{padding:16,overflowY:'auto',flex:1}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:12}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Base College (starting point)</label>
                  <SearchableSelect value={nearbyBase} onChange={v=>setNearbyBase(v||'')}
                    options={colleges.filter(c=>c.latitude).map(c=>({value:c.id,label:c.college_name}))}
                    placeholder="Select base college..." searchPlaceholder="Type college name to search..."
                    style={{width:'100%',minWidth:300}}/>
                </div>
                <div className="form-group">
                  <label>Radius (km)</label>
                  <select value={nearbyRadius} onChange={e=>setNearbyRadius(Number(e.target.value))}>
                    {[10,20,30,50,75,100].map(r=><option key={r} value={r}>{r} km</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Purpose (optional)</label>
                  <select value={nearbyPurpose} onChange={e=>setNearbyPurpose(e.target.value)}>
                    <option value=''>All Purposes</option>
                    {VISIT_PURPOSES.map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{display:'flex',alignItems:'flex-end'}}>
                  <button className="btn btn-primary btn-sm" onClick={findNearbyColleges} disabled={!nearbyBase||nearbyLoading}>
                    {nearbyLoading?'Searching...':'🔍 Find Colleges'}
                  </button>
                </div>
              </div>
              {nearbyResults.length>0&&(
                <>
                  <div style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                    <span style={{fontSize:12,fontWeight:600}}>{nearbyResults.length} colleges found</span>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                      onClick={()=>setNearbySelected(new Set(nearbyResults.filter(c=>!c.recentlyVisited).map(c=>c.id)))}>
                      Select Unvisited
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setNearbySelected(new Set())}>Clear</button>
                    <span style={{marginLeft:'auto',fontSize:11,color:'var(--text3)'}}>{nearbySelected.size} selected</span>
                  </div>
                  <div style={{maxHeight:380,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8,overflowX:'hidden'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'var(--accent-light)',position:'sticky',top:0}}>
                          <th style={{padding:'7px 10px',width:32,textAlign:'center'}}>
                            <input type="checkbox"
                              checked={nearbySelected.size===nearbyResults.filter(c=>!c.recentlyVisited).length&&nearbyResults.filter(c=>!c.recentlyVisited).length>0}
                              onChange={e=>e.target.checked?setNearbySelected(new Set(nearbyResults.filter(c=>!c.recentlyVisited).map(c=>c.id))):setNearbySelected(new Set())}/>
                          </th>
                          <th style={{padding:'7px 10px',textAlign:'left'}}>#</th>
                          <th style={{padding:'7px 10px',textAlign:'left'}}>College</th>
                          <th style={{padding:'7px 10px',textAlign:'left'}}>Zone / District</th>
                          <th style={{padding:'7px 10px',textAlign:'right'}}>Distance</th>
                          <th style={{padding:'7px 10px',textAlign:'center'}}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                      {nearbyResults.map((c,idx)=>(
                        <tr key={c.id}
                          style={{borderBottom:'1px solid var(--border)',
                            background:nearbySelected.has(c.id)?'var(--accent-light)':idx%2===0?'':' var(--surface2)',
                            opacity:c.recentlyVisited?0.6:1,cursor:'pointer'}}
                          onClick={()=>{if(c.recentlyVisited)return;const n=new Set(nearbySelected);n.has(c.id)?n.delete(c.id):n.add(c.id);setNearbySelected(n);}}>
                          <td style={{padding:'6px 10px',textAlign:'center'}}>
                            <input type="checkbox" checked={nearbySelected.has(c.id)}
                              disabled={c.recentlyVisited} readOnly/>
                          </td>
                          <td style={{padding:'6px 10px',color:'var(--text3)',fontWeight:600}}>{idx+1}</td>
                          <td style={{padding:'6px 10px',fontWeight:500}}>{c.college_name}</td>
                          <td style={{padding:'6px 10px',color:'var(--text3)'}}>{c.zone||'—'} | {c.district||'—'}</td>
                          <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700,color:'var(--accent)'}}>
                            🚗 {c.roadKm} km
                          </td>
                          <td style={{padding:'6px 10px',textAlign:'center'}}>
                            {c.recentlyVisited
                              ?<span style={{fontSize:10,background:'var(--green-light)',color:'var(--green)',padding:'2px 6px',borderRadius:4}}>✅ Visited</span>
                              :<span style={{fontSize:10,background:'var(--surface2)',color:'var(--text3)',padding:'2px 6px',borderRadius:4}}>Pending</span>}
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowNearby(false)}>Cancel</button>
                    <button className="btn btn-primary" disabled={!nearbySelected.size} onClick={addNearbyToplan}>
                      ➕ Add {nearbySelected.size} College(s) to Plan
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TEAM MANAGER MODAL */}
      {showTeamMgr&&(
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowTeamMgr(false);}}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <span className="modal-title">👥 Manage Visit Teams</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setShowTeamMgr(false);setEditingTeam(null);setTeamForm({team_name:'',member_1:'',member_2:'',notes:''});}}>✕</button>
            </div>
            <div style={{padding:16}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Team Name *</label>
                  <input value={teamForm.team_name} onChange={e=>setTeamForm(f=>({...f,team_name:e.target.value}))} placeholder="e.g. EKM TEAM"/>
                </div>
                <div className="form-group">
                  <label>Member 1</label>
                  <input value={teamForm.member_1} onChange={e=>setTeamForm(f=>({...f,member_1:e.target.value}))} placeholder="Name"/>
                </div>
                <div className="form-group">
                  <label>Member 2</label>
                  <input value={teamForm.member_2} onChange={e=>setTeamForm(f=>({...f,member_2:e.target.value}))} placeholder="Name"/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <input value={teamForm.notes} onChange={e=>setTeamForm(f=>({...f,notes:e.target.value}))} placeholder="e.g. TVM Zone team"/>
                </div>
              </div>
              <div style={{display:'flex',gap:8,marginBottom:16}}>
                <button className="btn btn-primary btn-sm" onClick={saveTeam}>{editingTeam?'Update Team':'Add Team'}</button>
                {editingTeam&&<button className="btn btn-ghost btn-sm" onClick={()=>{setEditingTeam(null);setTeamForm({team_name:'',member_1:'',member_2:'',notes:''});}}>Cancel</button>}
              </div>
              <div style={{borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div style={{fontWeight:600,fontSize:12,marginBottom:8}}>Existing Teams</div>
                {teams.length===0?<div style={{color:'var(--text3)',fontSize:12}}>No teams yet</div>:
                teams.map(t=>(
                  <div key={t.id} style={{display:'flex',gap:8,alignItems:'center',padding:'6px 0',borderBottom:'1px solid var(--border)'}}>
                    <div style={{flex:1}}>
                      <span style={{fontWeight:600,fontSize:13}}>{t.team_name}</span>
                      {(t.member_1||t.member_2)&&<span style={{fontSize:11,color:'var(--text3)',marginLeft:8}}>👤 {t.member_1}{t.member_2?' & '+t.member_2:''}</span>}
                      {t.notes&&<div style={{fontSize:11,color:'var(--text3)'}}>{t.notes}</div>}
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>{setEditingTeam(t.id);setTeamForm({team_name:t.team_name,member_1:t.member_1||'',member_2:t.member_2||'',notes:t.notes||''});}}>✏️</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--red)'}} onClick={()=>deleteTeam(t.id)}>🗑</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COLLEGE HISTORY VIEW */}
      {view==='history'&&(
        <div>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setView('plans');setRescheduledStops([]);}}>← Back</button>
            <span style={{fontWeight:700,fontSize:14}}>🏫 College Visit History</span>
          </div>
          <div className="card" style={{marginBottom:12}}>
            <SearchableSelect
              value={historyCollege} onChange={v=>setHistoryCollege(v||'')}
              options={colleges.filter(c=>c.is_active&&c.college_status!=='Shutdown').map(c=>({value:c.id,label:c.college_name}))}
              placeholder="Search college..." searchPlaceholder="Type college name..."
              style={{minWidth:300}}/>
          </div>
          {historyCollege&&(()=>{
            const colStops = plans.flatMap(p=>
              (p.visit_stops||[]).filter(s=>s.college_id===historyCollege).map(s=>({...s,plan:p}))
            ).sort((a,b)=>b.plan.plan_date.localeCompare(a.plan.plan_date));
            const colName = colleges.find(c=>c.id===historyCollege)?.college_name||'';
            return (
              <div>
                <div style={{fontWeight:600,fontSize:13,marginBottom:8,display:'flex',gap:10,alignItems:'center'}}>
                  <span>{colName}</span>
                  <span className="badge badge-blue">{colStops.length} visit plan(s)</span>
                  <span className="badge badge-green">{colStops.filter(s=>s.visit_status==='Completed'||s.visit_status==='Visited').length} visited</span>
                  <span className="badge badge-red">{colStops.filter(s=>s.visit_status==='Declined').length} declined</span>
                </div>
                {colStops.length===0?
                  <div className="card" style={{textAlign:'center',color:'var(--text3)',padding:30}}>No visits planned for this college yet</div>:
                  colStops.map(s=>(
                    <div key={s.id} className="card" style={{marginBottom:8,padding:'10px 14px'}}>
                      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                        <span style={{fontWeight:700}}>📅 {new Date(s.plan.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                        {s.plan.team_name&&<span className="badge badge-blue">{s.plan.team_name}</span>}
                        {s.plan.team_member_1&&<span style={{fontSize:12}}>👤 {s.plan.team_member_1}{s.plan.team_member_2?' & '+s.plan.team_member_2:''}</span>}
                        <span className={`badge ${s.visit_status==='Completed'?'badge-green':s.visit_status==='Declined'?'badge-red':s.visit_status==='Visited'?'badge-blue':'badge-amber'}`}>{s.visit_status}</span>
                      </div>
                      {(s.purposes||[]).length>0&&<div style={{marginTop:4,display:'flex',gap:4,flexWrap:'wrap'}}>{(s.purposes||[]).map(p=><span key={p} style={{fontSize:11,padding:'1px 6px',borderRadius:3,background:'var(--surface2)',border:'1px solid var(--border)'}}>{p}</span>)}</div>}
                      {s.call_notes&&<div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>📞 {s.call_notes}</div>}
                      {s.remarks&&<div style={{fontSize:11,color:'var(--text2)',marginTop:4}}>📝 {s.remarks}</div>}
                      {s.rescheduled_date&&<div style={{fontSize:11,color:'var(--amber)',marginTop:4}}>🔄 Rescheduled to: {fmtDMY(s.rescheduled_date)}</div>}
                    </div>
                  ))
                }
              </div>
            );
          })()}
        </div>
      )}

      {/* REPORT VIEW */}
      {view==='report'&&(
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
            {[
              {label:'Total Plans',val:plans.length,color:'var(--accent)',filter:'all'},
              {label:'Colleges Visited',val:totalVisited,color:'var(--green)',filter:'visited'},
              {label:'Pending Visits',val:totalPending,color:'var(--amber)',filter:'pending'},
              {label:'Declined',val:totalDeclined,color:'var(--red)',filter:'declined'},
            ].map(c=>(
              <div key={c.label} className="card" style={{textAlign:'center',padding:'12px 16px',cursor:'pointer',
                border:reportFilter?.type===c.filter?'2px solid var(--accent)':'2px solid transparent'}}
                onClick={()=>setReportFilter(reportFilter?.type===c.filter?null:{type:c.filter})}>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>{c.label}</div>
                <div style={{fontSize:24,fontWeight:800,color:c.color}}>{c.val}</div>
                <div style={{fontSize:10,color:'var(--accent)',marginTop:2}}>Click to filter ↓</div>
              </div>
            ))}
          </div>

          {/* Purpose breakdown */}
          <div className="card" style={{marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Purpose Breakdown</div>
            {(()=>{
              const allPurposes = [...new Set(allStops.flatMap(s=>s.purposes||[]))];
              if(!allPurposes.length) return <div style={{fontSize:12,color:'var(--text3)'}}>No purposes assigned yet</div>;
              return allPurposes.map(purpose=>{
                const total = allStops.filter(s=>(s.purposes||[]).includes(purpose)).length;
                const done = allStops.filter(s=>(s.purposes||[]).includes(purpose)&&['Visited','Completed'].includes(s.visit_status)).length;
                return (
                  <div key={purpose} style={{display:'flex',gap:10,alignItems:'center',marginBottom:8,fontSize:12}}>
                    <span style={{minWidth:220}}>{purpose}</span>
                    <div style={{flex:1,height:8,background:'var(--border)',borderRadius:4}}>
                      <div style={{height:'100%',width:`${total>0?(done/total*100):0}%`,background:'var(--green)',borderRadius:4}}/>
                    </div>
                    <span style={{minWidth:60,textAlign:'right',color:'var(--text3)'}}>{done}/{total}</span>
                  </div>
                );
              });
            })()}
          </div>

          {/* Filtered plans list */}
          {reportFilter&&(()=>{
            const filtered = plans.filter(p=>{
              const stops = p.visit_stops||[];
              if(reportFilter.type==='all') return true;
              if(reportFilter.type==='visited') return stops.some(s=>['Visited','Completed'].includes(s.visit_status));
              if(reportFilter.type==='pending') return stops.some(s=>['Planned','Called','Confirmed'].includes(s.visit_status));
              if(reportFilter.type==='declined') return stops.some(s=>s.visit_status==='Declined');
              if(reportFilter.type==='team') return p.team_name===reportFilter.value;
              return true;
            });
            return (
              <div className="card" style={{marginBottom:12,borderLeft:'4px solid var(--accent)'}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:8,display:'flex',justifyContent:'space-between'}}>
                  <span>📋 {reportFilter.value||reportFilter.type.charAt(0).toUpperCase()+reportFilter.type.slice(1)} Plans ({filtered.length})</span>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setReportFilter(null)}>✕ Clear</button>
                </div>
                {filtered.map(p=>(
                  <div key={p.id} style={{padding:'6px 0',borderBottom:'1px solid var(--border)',fontSize:12,display:'flex',gap:8,alignItems:'center',cursor:'pointer'}}
                    onClick={()=>{setSelPlan(p);loadStops(p.id);setView('detail');}}>
                    <span style={{fontWeight:600}}>📅 {new Date(p.plan_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                    {p.team_name&&<span className="badge badge-blue">{p.team_name}</span>}
                    <span style={{color:'var(--text3)'}}>{(p.visit_stops||[]).length} colleges</span>
                    <span style={{marginLeft:'auto',fontSize:10,color:'var(--accent)'}}>View →</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Monthly Target */}
          {(()=>{
            const now = new Date();
            const thisMonth = now.toISOString().slice(0,7);
            const monthPlans = plans.filter(p=>p.plan_date?.startsWith(thisMonth));
            const monthStops = monthPlans.flatMap(p=>p.visit_stops||[]);
            const monthVisited = monthStops.filter(s=>['Visited','Completed'].includes(s.visit_status)).length;
            const monthTotal = monthStops.length;
            const pct = monthTotal>0?Math.round(monthVisited/monthTotal*100):0;
            return (
              <div className="card" style={{marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>📅 This Month — {now.toLocaleString('en-IN',{month:'long',year:'numeric'})}</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
                  <div style={{textAlign:'center'}}><div style={{fontSize:22,fontWeight:800,color:'var(--accent)'}}>{monthPlans.length}</div><div style={{fontSize:11,color:'var(--text3)'}}>Plans</div></div>
                  <div style={{textAlign:'center'}}><div style={{fontSize:22,fontWeight:800,color:'var(--blue)'}}>{monthTotal}</div><div style={{fontSize:11,color:'var(--text3)'}}>Colleges Planned</div></div>
                  <div style={{textAlign:'center'}}><div style={{fontSize:22,fontWeight:800,color:'var(--green)'}}>{monthVisited}</div><div style={{fontSize:11,color:'var(--text3)'}}>Visited</div></div>
                </div>
                <div style={{height:8,background:'var(--border)',borderRadius:4}}>
                  <div style={{height:'100%',width:pct+'%',background:'var(--green)',borderRadius:4,transition:'width 0.5s'}}/>
                </div>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:4,textAlign:'right'}}>{pct}% visit completion rate</div>
              </div>
            );
          })()}

          {/* Team Summary */}
          <div className="card" style={{marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>👥 Team Summary</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'var(--accent-light)'}}>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Team</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Members</th>
                <th style={{padding:'6px 10px',textAlign:'center'}}>Plans</th>
                <th style={{padding:'6px 10px',textAlign:'center'}}>Colleges Planned</th>
                <th style={{padding:'6px 10px',textAlign:'center'}}>Visited</th>
                <th style={{padding:'6px 10px',textAlign:'center'}}>Completed</th>
                <th style={{padding:'6px 10px',textAlign:'center'}}>Declined</th>
              </tr></thead>
              <tbody>
              {(()=>{
                const teamMap = {};
                plans.forEach(p=>{
                  const key = p.team_name||'No Team';
                  if(!teamMap[key]) teamMap[key]={name:key,members:new Set(),plans:0,planned:0,visited:0,completed:0,declined:0};
                  teamMap[key].plans++;
                  if(p.team_member_1) teamMap[key].members.add(p.team_member_1);
                  if(p.team_member_2) teamMap[key].members.add(p.team_member_2);
                  (p.visit_stops||[]).forEach(s=>{
                    teamMap[key].planned++;
                    if(s.visit_status==='Visited') teamMap[key].visited++;
                    if(s.visit_status==='Completed') teamMap[key].completed++;
                    if(s.visit_status==='Declined') teamMap[key].declined++;
                  });
                });
                return Object.values(teamMap).sort((a,b)=>b.planned-a.planned).map(t=>(
                  <tr key={t.name} style={{borderBottom:'1px solid var(--border)',cursor:'pointer',
                    background:reportFilter?.value===t.name?'var(--accent-light)':''}}
                    onClick={()=>setReportFilter(reportFilter?.value===t.name?null:{type:'team',value:t.name})}>
                    <td style={{padding:'7px 10px',fontWeight:600,color:'var(--accent)'}}>{t.name} ↗</td>
                    <td style={{padding:'7px 10px',color:'var(--text2)'}}>{[...t.members].join(' & ')||'—'}</td>
                    <td style={{padding:'7px 10px',textAlign:'center'}}>{t.plans}</td>
                    <td style={{padding:'7px 10px',textAlign:'center'}}>{t.planned}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--accent)'}}>{t.visited}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--green)'}}>{t.completed}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--red)'}}>{t.declined}</td>
                  </tr>
                ));
              })()}
              </tbody>
            </table>
          </div>

          {/* Not completed visits */}
          {notDone.length>0&&(
            <div className="card" style={{marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'var(--amber)'}}>⚠️ Visited But Not Completed ({notDone.length})</div>
              {notDone.map(s=>(
                <div key={s.id} style={{padding:'6px 0',borderBottom:'1px solid var(--border)',fontSize:12}}>
                  <span style={{fontWeight:500}}>{s.nactet_colleges?.college_name}</span>
                  {s.not_done_reason&&<span style={{color:'var(--text3)',marginLeft:8}}>— {s.not_done_reason}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Follow-up Alerts - Declined/Rescheduled */}
          {(()=>{
            const needFollowup = allStops.filter(s=>['Declined','Rescheduled'].includes(s.visit_status));
            if(!needFollowup.length) return null;
            return (
              <div className="card" style={{marginBottom:12,borderLeft:'4px solid var(--red)'}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'var(--red)'}}>🔔 Follow-up Required ({needFollowup.length})</div>
                {needFollowup.map(s=>(
                  <div key={s.id} style={{padding:'6px 0',borderBottom:'1px solid var(--border)',fontSize:12,display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{fontWeight:500,flex:1}}>{s.nactet_colleges?.college_name}</span>
                    <span className={`badge ${s.visit_status==='Declined'?'badge-red':'badge-amber'}`}>{s.visit_status}</span>
                    {s.rescheduled_date&&<span style={{fontSize:11,color:'var(--text3)'}}>→ {fmtDMY(s.rescheduled_date)}</span>}
                    {s.visit_status==='Declined'&&!s.rescheduled_date&&(
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,marginTop:4}} onClick={()=>{
                        const d = prompt('Enter reschedule date (YYYY-MM-DD):');
                        if(d) supabase.from('visit_stops').update({rescheduled_date:d,visit_status:'Rescheduled'}).eq('id',s.id).then(()=>loadPlans());
                      }}>🔄 Reschedule</button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Never Visited Colleges */}
          {(()=>{
            const visitedCollegeIds = new Set(allStops.filter(s=>['Visited','Completed'].includes(s.visit_status)).map(s=>s.college_id));
            const neverVisited = colleges.filter(c=>!visitedCollegeIds.has(c.id));
            const filtered = neverVisited.filter(c=>
              (!nvZone||c.zone===nvZone) &&
              (!nvSearch||c.college_name?.toLowerCase().includes(nvSearch.toLowerCase()))
            );
            return (
              <div className="card" style={{marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>🏫 Never Visited ({neverVisited.length} colleges)</div>
                <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
                  <span style={{fontSize:11,color:'var(--text3)'}}>Filter by zone:</span>
                  <span onClick={()=>setNvZone('')} className={`badge ${!nvZone?'badge-blue':'badge-gray'}`} style={{cursor:'pointer'}}>All ({neverVisited.length})</span>
                  {['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5'].map(z=>{
                    const cnt = neverVisited.filter(c=>c.zone===z).length;
                    return cnt>0?(
                      <span key={z} onClick={()=>setNvZone(nvZone===z?'':z)}
                        className={`badge ${nvZone===z?'badge-blue':'badge-gray'}`} style={{cursor:'pointer'}}>
                        {z}: {cnt}
                      </span>
                    ):null;
                  })}
                </div>
                <input value={nvSearch} onChange={e=>setNvSearch(e.target.value)}
                  placeholder="Search college name..." style={{width:'100%',fontSize:11,padding:'5px 10px',marginBottom:8}}/>
                <div style={{maxHeight:250,overflowY:'auto'}}>
                  {filtered.length===0?<div style={{fontSize:11,color:'var(--text3)',padding:8,textAlign:'center'}}>No colleges found</div>:
                  filtered.map(c=>(
                    <div key={c.id} style={{fontSize:11,padding:'5px 0',borderBottom:'1px solid var(--border)',display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{flex:1}}>{c.college_name}</span>
                      <span style={{color:'var(--text3)',fontSize:10}}>{c.zone} | {c.district}</span>
                    </div>
                  ))}
                  {filtered.length>0&&<div style={{fontSize:11,color:'var(--text3)',padding:'4px 0',textAlign:'right'}}>Showing {filtered.length} colleges</div>}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLEGE NOTES & REMINDERS  (table: nactet_college_notes)
// Things learnt on calls: "no final-year Mechanical batch until 2028",
// class strength, not participating this year, etc. A note with a
// remind-on date shows up as a reminder on that day until marked done.
// ════════════════════════════════════════════════════════════════
const NOTE_TYPES = ['Batch Availability','Class Strength','Not Participating','Contact / Policy Info','Other'];
const NOTE_TYPE_BADGE = {'Batch Availability':'badge-blue','Class Strength':'badge-cyan','Not Participating':'badge-red','Contact / Policy Info':'badge-purple','Other':'badge-gray'};
const NOTES_SETUP_SQL = `create table if not exists nactet_college_notes (
  id uuid primary key default gen_random_uuid(),
  college_id text not null,
  note_type text, stream text, batch_year int, class_strength int,
  note text, remind_on date,
  reminder_status text default 'Open',
  done_at timestamptz, done_by text,
  created_by text, created_at timestamptz default now()
);
alter table nactet_college_notes enable row level security;
create policy "notes_all" on nactet_college_notes for all to authenticated using (true) with check (true);`;
const todayISO = () => new Date().toISOString().split('T')[0];
const noteWho = staff => staff?.full_name || staff?.name || staff?.staff_name || staff?.email || '';

function CollegeNotes({ college, canEditNotes, staff }) {
  const [notes, setNotes] = useState([]);
  const [missing, setMissing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { id:null, note_type:'Batch Availability', stream:'', batch_year:'', class_strength:'', note:'', remind_on:'' };
  const [form, setForm] = useState(empty);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));
  const colStreams = (() => { try { const s = Array.isArray(college.streams)?college.streams:JSON.parse(college.streams||'[]'); return s.length?s:STREAMS; } catch(e){ return STREAMS; } })();

  async function load() {
    const { data, error } = await supabase.from('nactet_college_notes').select('*')
      .eq('college_id', String(college.id)).order('created_at', { ascending:false });
    if (error) { setMissing(true); setNotes([]); return; }
    setMissing(false); setNotes(data||[]);
  }
  useEffect(() => { load(); }, [college.id]); // eslint-disable-line
  useLiveRefresh(['nactet_college_notes'], () => load());

  function openNew() { setForm(empty); setShowModal(true); }
  function openEdit(n) {
    setForm({ id:n.id, note_type:n.note_type||'Other', stream:n.stream||'',
      batch_year:n.batch_year!=null?String(n.batch_year):'', class_strength:n.class_strength!=null?String(n.class_strength):'',
      note:n.note||'', remind_on:n.remind_on||'' });
    setShowModal(true);
  }
  // choosing a batch year suggests a reminder on 1 Jan of that year (editable)
  function setBatchYear(v) {
    setForm(f => ({ ...f, batch_year:v, remind_on: (!f.remind_on || f.remind_on===`${f.batch_year}-01-01`) && /^\d{4}$/.test(v) ? `${v}-01-01` : f.remind_on }));
  }
  async function save() {
    if (!form.note.trim() && !form.class_strength && !form.batch_year) { toast.error('Enter a note, year or strength'); return; }
    setSaving(true);
    const payload = {
      college_id: String(college.id), note_type: form.note_type, stream: form.stream||null,
      batch_year: form.batch_year ? Number(form.batch_year) : null,
      class_strength: form.class_strength ? Number(form.class_strength) : null,
      note: form.note.trim()||null, remind_on: form.remind_on||null,
      reminder_status: form.remind_on ? 'Open' : null,
    };
    const { error } = form.id
      ? await supabase.from('nactet_college_notes').update(payload).eq('id', form.id)
      : await supabase.from('nactet_college_notes').insert({ ...payload, created_by: noteWho(staff) });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Note saved'); setShowModal(false); load();
  }
  async function markDone(n) {
    const { error } = await supabase.from('nactet_college_notes')
      .update({ reminder_status:'Done', done_at:new Date().toISOString(), done_by:noteWho(staff) }).eq('id', n.id);
    if (error) toast.error(error.message); else load();
  }
  async function remove(n) {
    if (!window.confirm('Delete this note?')) return;
    const { error } = await supabase.from('nactet_college_notes').delete().eq('id', n.id);
    if (error) toast.error(error.message); else load();
  }

  return (
    <div style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:12}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
        <span style={{fontWeight:600,fontSize:14}}>🧠 Notes & Reminders</span>
        {canEditNotes && !missing && <button className="btn btn-primary btn-sm" onClick={openNew}>+ Note</button>}
      </div>
      {missing && (
        <div style={{fontSize:12,color:'var(--amber)'}}>
          Notes table not set up yet. Run this once in Supabase → SQL Editor:
          <pre style={{whiteSpace:'pre-wrap',fontSize:10,background:'var(--bg3)',padding:8,borderRadius:6,marginTop:6}}>{NOTES_SETUP_SQL}</pre>
        </div>
      )}
      {!missing && notes.length===0 && <p style={{color:'var(--text3)',fontSize:12}}>No notes yet. Add things learnt on calls, e.g. "no final-year Mechanical batch until 2028".</p>}
      {notes.map(n => {
        const open = n.reminder_status==='Open' && n.remind_on;
        const due = open && n.remind_on <= todayISO();
        return (
          <div key={n.id} style={{padding:'6px 8px',borderBottom:'1px solid var(--border)',fontSize:12,background:due?'var(--amber-light)':'transparent',borderRadius:due?6:0}}>
            <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
              <span className={`badge ${NOTE_TYPE_BADGE[n.note_type]||'badge-gray'}`} style={{fontSize:9}}>{n.note_type}</span>
              {n.stream && <span style={{fontWeight:600}}>{n.stream}</span>}
              {n.batch_year && <span>{n.note_type==='Batch Availability'?'final-year available':'year'} <b>{n.batch_year}</b></span>}
              {n.class_strength!=null && <span>strength <b>{n.class_strength}</b></span>}
              <span style={{color:'var(--text3)',marginLeft:'auto',fontSize:10}}>{n.created_by||''} · {fmtDate((n.created_at||'').slice(0,10))}</span>
            </div>
            {n.note && <div style={{marginTop:2,color:'var(--text2)'}}>{n.note}</div>}
            {n.remind_on && (
              <div style={{fontSize:11,marginTop:2,color:n.reminder_status==='Done'?'var(--green)':due?'var(--red)':'var(--amber)'}}>
                {n.reminder_status==='Done' ? `✅ Reminder done${n.done_by?' by '+n.done_by:''}` : `${due?'🔔 Due':'⏰ Remind on'} ${fmtDate(n.remind_on)}`}
              </div>
            )}
            {canEditNotes && <div style={{display:'flex',gap:4,marginTop:4}}>
              {open && <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>markDone(n)}>✔ Done</button>}
              <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>openEdit(n)}>✏️</button>
              <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)'}} onClick={()=>remove(n)}>🗑</button>
            </div>}
          </div>
        );
      })}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-header">
              <span className="modal-title">{form.id?'Edit':'Add'} Note — {college.college_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label>Type</label>
                <select value={form.note_type} onChange={e=>sf('note_type',e.target.value)}>
                  {NOTE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Stream (optional)</label>
                <select value={form.stream} onChange={e=>sf('stream',e.target.value)}>
                  <option value="">— All / not specific —</option>
                  {colStreams.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {(form.note_type==='Batch Availability'||form.note_type==='Class Strength'||form.note_type==='Not Participating') && (
                <div className="form-group">
                  <label>{form.note_type==='Batch Availability'?'Final-year students available in (year)':'Year'}</label>
                  <input type="number" min="2020" max="2040" value={form.batch_year} onChange={e=>setBatchYear(e.target.value)} placeholder="e.g. 2028"/>
                </div>
              )}
              {form.note_type==='Class Strength' && (
                <div className="form-group"><label>Class strength (students)</label>
                  <input type="number" min="0" value={form.class_strength} onChange={e=>sf('class_strength',e.target.value)}/>
                </div>
              )}
              <div className="form-group"><label>Note</label>
                <textarea rows={3} value={form.note} onChange={e=>sf('note',e.target.value)} placeholder="What did the college say? Who told you?"/>
              </div>
              <div className="form-group"><label>Remind me on (optional)</label>
                <DateInput value={form.remind_on} onChange={e=>sf('remind_on',e.target.value)}/>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>Shows in 🔔 Follow-up and the banner on this page from that date until marked Done.</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Due reminders across all colleges (used in the Follow-up tab)
function NoteReminders({ colleges }) {
  const [rows, setRows] = useState([]);
  const [missing, setMissing] = useState(false);
  async function load() {
    const { data, error } = await supabase.from('nactet_college_notes').select('*')
      .eq('reminder_status','Open').not('remind_on','is',null).lte('remind_on', todayISO()).order('remind_on');
    if (error) { setMissing(true); return; }
    setMissing(false); setRows(data||[]);
  }
  useEffect(() => { load(); }, []);
  useLiveRefresh(['nactet_college_notes'], () => load());
  async function done(n) {
    await supabase.from('nactet_college_notes').update({ reminder_status:'Done', done_at:new Date().toISOString() }).eq('id', n.id);
    load();
  }
  async function snooze(n, days) {
    const d = new Date(Date.now()+days*86400000).toISOString().split('T')[0];
    await supabase.from('nactet_college_notes').update({ remind_on:d }).eq('id', n.id);
    load();
  }
  if (missing || rows.length===0) return null;
  return (
    <div className="card" style={{marginBottom:12,borderLeft:'3px solid var(--amber)'}}>
      <div style={{fontWeight:700,fontSize:14,marginBottom:8}}>🧠 College note reminders due ({rows.length})</div>
      {rows.map(n => {
        const col = colleges.find(c=>String(c.id)===String(n.college_id));
        return (
          <div key={n.id} style={{padding:'6px 0',borderBottom:'1px solid var(--border)',fontSize:12}}>
            <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
              <b>{col?.college_name||'College'}</b>
              <span className={`badge ${NOTE_TYPE_BADGE[n.note_type]||'badge-gray'}`} style={{fontSize:9}}>{n.note_type}</span>
              {n.stream && <span>{n.stream}</span>}
              {n.batch_year && <span>· {n.batch_year}</span>}
              {n.class_strength!=null && <span>· strength {n.class_strength}</span>}
              <span style={{color:'var(--red)',fontSize:11}}>due {fmtDate(n.remind_on)}</span>
            </div>
            {n.note && <div style={{color:'var(--text2)',marginTop:2}}>{n.note}</div>}
            <div style={{display:'flex',gap:4,marginTop:4}}>
              <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>done(n)}>✔ Done</button>
              <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>snooze(n,7)}>Snooze 1 week</button>
              <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>snooze(n,30)}>Snooze 1 month</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Amber banner on the NACTET Scholarship page when reminders are due
function NoteDueBanner({ onGo }) {
  const [n, setN] = useState(0);
  const refreshN = () => supabase.from('nactet_college_notes').select('id', { count:'exact', head:true })
    .eq('reminder_status','Open').not('remind_on','is',null).lte('remind_on', todayISO())
    .then(({ count, error }) => { if (!error) setN(count||0); });
  useEffect(() => { refreshN(); }, []); // eslint-disable-line
  useLiveRefresh(['nactet_college_notes'], () => refreshN());
  if (!n) return null;
  return (
    <div onClick={onGo} style={{cursor:'pointer',marginBottom:12,padding:'8px 12px',borderRadius:8,background:'var(--amber-light)',color:'var(--amber)',fontSize:12,fontWeight:600}}>
      🔔 {n} college note reminder{n!==1?'s':''} due — click to view
    </div>
  );
}

export default function NACTETScholarship() {
  const { isEditor, canEdit, canView, isAdmin, staff } = useAuth();
  const canSeeCoordDetails = isAdmin || isEditor;
  // Zone assignment per user email
  const USER_ZONE_MAP = {
    'smithaarmstech@gmail.com': 'Zone 1',
    'rakhiarmstech@gmail.com': 'Zone 2',
    'jeethuarmstech@gmail.com': 'Zone 3',
  };
  const userDefaultZone = USER_ZONE_MAP[staff?.email] || '';
 // hide coordinator contacts from basic staff
  const [tab, setTab] = useState('dashboard');
  const [programFilter, setProgramFilter] = useState({});
  const [colleges, setColleges] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);
  useLiveRefresh(['nactet_colleges', 'nactet_programs'], () => loadAll(true), true, 1500);

  async function loadAll(silent) {
    if (silent !== true) setLoading(true);
    const [{ data: cols }, { data: progs }] = await Promise.all([
      supabase.from('nactet_colleges').select('*').order('college_name'),
      supabase.from('nactet_programs').select('*').order('year', { ascending: false }).limit(2000),
    ]);
    setColleges(cols || []);
    setPrograms(progs || []);
    setLoading(false);
  }

  const TABS = [
    { key:'dashboard', label:'📊 Dashboard', pkey:'nactet_dashboard' },
    { key:'colleges',  label:'🏫 Colleges',  pkey:'nactet_colleges'  },
    { key:'programs',  label:'📋 Programs',  pkey:'nactet_programs'  },
    { key:'students',  label:'👥 Students',  pkey:'nactet_students'  },
    { key:'winners',   label:'🏆 Winners',   pkey:'nactet_winners'   },
    { key:'followup',  label:'🔔 Follow-up', pkey:'nactet_followup'  },
    { key:'reports',   label:'📈 Reports',   pkey:'nactet_reports'   },
    { key:'visits',    label:'🗺️ College Visits', pkey:'nactet_visits' },
  ].filter(t => canView(t.pkey));

  return (
    <div>
      <div style={{marginBottom:16}}>
        <h1 className="page-title">NACTET Scholarship</h1>
        <p className="page-subtitle">Scholarship program tracking — colleges, exams, evaluation & winner follow-up</p>
      </div>
      {TABS.some(t=>t.key==='followup') && <NoteDueBanner onGo={()=>setTab('followup')}/>}
      <div className="tabs" style={{marginBottom:20,flexWrap:'wrap'}}>
        {TABS.map(t=>(
          <div key={t.key} className={`tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)}>
            {t.label}
          </div>
        ))}
      </div>
      {tab==='dashboard' && <DashboardTab colleges={colleges} programs={programs} userDefaultZone={userDefaultZone} onGo={(t,filter)=>{if(filter)setProgramFilter(filter);setTab(t);}}/>}
      {tab==='colleges' && <CollegesTab colleges={colleges} programs={programs} onRefresh={loadAll} canSeeCoordDetails={canSeeCoordDetails} isEditor={isEditor} userDefaultZone={userDefaultZone}/>}
      {tab==='programs' && <ProgramsTab colleges={colleges} programs={programs} onRefresh={loadAll} isEditor={isEditor} initFilter={programFilter} onFilterUsed={()=>setProgramFilter({})} userDefaultZone={userDefaultZone}/>}
      {tab==='students' && <StudentsTab colleges={colleges} programs={programs} isEditor={isEditor} userDefaultZone={userDefaultZone} onRefresh={loadAll}/>}
      {tab==='winners'  && <WinnersTab  colleges={colleges} programs={programs} isEditor={isEditor} userDefaultZone={userDefaultZone}/>}
      {tab==='followup' && <FollowUpTab colleges={colleges} programs={programs}/>}
      {tab==='visits'   && <VisitTrackingTab colleges={colleges} supabase={supabase}/>}
      {tab==='reports'  && <ReportsTab  colleges={colleges} programs={programs} onRefresh={loadAll}/>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ════════════════════════════════════════════════════════════════
function DashboardTab({ colleges, programs, onGo, userDefaultZone='' }) {
  const [selYear, setSelYear] = React.useState('2027');
  const [pendingOpen, setPendingOpen] = React.useState(false);
  const [dZone, setDZone] = React.useState(userDefaultZone);
  const [dUniv, setDUniv] = React.useState('');
  const [dCollege, setDCollege] = React.useState('');
  const [dStream, setDStream] = React.useState('');
  const [dStatus, setDStatus] = React.useState('');
  const availYears = [...new Set(programs.map(p=>p.year?.toString()))].sort((a,b)=>b-a);
  const availZones = [...new Set(colleges.map(c=>c.zone).filter(Boolean))].sort();
  const availUnivs = [...new Set(colleges.map(c=>c.university).filter(Boolean))].sort();
  const filteredColleges = colleges.filter(c=>(!dZone||c.zone===dZone)&&(!dUniv||c.university===dUniv));
  const filteredCollegeIds = filteredColleges.map(c=>c.id);
  const thisYear = programs.filter(p=>
    p.year==selYear &&
    ((!dZone && !dUniv) || filteredCollegeIds.includes(p.college_id)) &&
    (!dCollege || p.college_id===dCollege) &&
    (!dStream || p.stream===dStream) &&
    (!dStatus || p.status===dStatus)
  );
  const examDone = thisYear.filter(p=>['Exam Done','Evaluation Done','Results Shared','Certificates Ready','Distributed'].includes(p.status)).length;
  const distributed = thisYear.filter(p=>p.status==='Distributed').length;
  // Count unique colleges in filtered programs
  const totalColleges = new Set(thisYear.map(p=>p.college_id)).size;
  const allCollegesCount = colleges.length;

  // Pending alerts
  const alerts = [];
  thisYear.forEach(p => {
    const col = colleges.find(c=>c.id===p.college_id);
    const name = col?.college_name || '—';
    if (p.status==='QB Sent' && !p.exam_photo_taken) alerts.push({ type:'exam', msg: name+' ('+p.stream+') — QB sent, exam pending', college_id: p.college_id });
    if (p.status==='Exam Done' && !p.evaluation_done) alerts.push({ type:'eval', msg: name+' ('+p.stream+') — Evaluation pending', college_id: p.college_id });
    if (p.status==='Evaluation Done' && !p.result_communicated) alerts.push({ type:'result', msg: name+' ('+p.stream+') — Results not shared', college_id: p.college_id });
    if (p.status==='Results Shared' && !p.certificates_prepared) alerts.push({ type:'cert', msg: name+' ('+p.stream+') — Certificates not prepared', college_id: p.college_id });
    if (p.status==='Certificates Ready' && !p.certificate_distributed) alerts.push({ type:'dist', msg: name+' ('+p.stream+') — Distribution pending', college_id: p.college_id });
  });

  function clearFilters(){setDZone('');setDUniv('');setDCollege('');setDStream('');setDStatus('');}

  return (
    <div>
      {/* Year selector */}
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12,flexWrap:'wrap',width:'fit-content'}}>
        <span style={{fontWeight:600,fontSize:14}}>📊 Dashboard</span>
        <select value={selYear} onChange={e=>setSelYear(e.target.value)}
          style={{padding:'4px 10px',borderRadius:6,border:'1px solid var(--border)',fontWeight:600,color:'var(--accent)'}}>
          {availYears.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={dZone} onChange={e=>{setDZone(e.target.value);setDCollege('');}} style={{fontSize:12,maxWidth:100}}>
          <option value="">All Zones</option>
          {availZones.map(z=><option key={z} value={z}>{z}</option>)}
        </select>
        <select value={dUniv} onChange={e=>{setDUniv(e.target.value);setDCollege('');}} style={{fontSize:12,maxWidth:140}}>
          <option value="">All Universities</option>
          {availUnivs.map(u=><option key={u} value={u}>{u}</option>)}
        </select>
        <SearchableSelect
          value={dCollege}
          onChange={v=>setDCollege(v||'')}
          options={filteredColleges.sort((a,b)=>a.college_name.localeCompare(b.college_name)).map(col=>({value:col.id,label:col.college_name}))}
          placeholder="All Colleges"
          searchPlaceholder="Search college..."
          style={{fontSize:12,minWidth:150,maxWidth:180}}
        />
        <select value={dStream} onChange={e=>setDStream(e.target.value)} style={{fontSize:12,maxWidth:110}}>
          <option value="">All Streams</option>
          {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select value={dStatus} onChange={e=>setDStatus(e.target.value)} style={{fontSize:12,maxWidth:120}}>
          <option value="">All Status</option>
          {STATUS_FLOW.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={()=>clearFilters()} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>✕ Clear</button>
        <span style={{fontSize:12,color:'var(--text3)'}}>{thisYear.length} programs</span>
      </div>

      {/* Stat cards - clickable */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          ['🏫 Colleges ('+selYear+')', totalColleges, 'var(--accent)', 'colleges', {}],
          ['📋 Programs '+selYear, thisYear.length, 'var(--blue)', 'programs', {year:selYear}],
          ['✓ Exam Done', examDone, 'var(--green)', 'programs', {year:selYear, status:'Exam Done'}],
          ['🎓 Distributed', distributed, 'var(--purple)', 'programs', {year:selYear, status:'Distributed'}],
        ].map(([l,v,col,tab,filter])=>(
          <div key={l} className="card" style={{textAlign:'center',padding:16,cursor:'pointer',transition:'transform 0.1s'}}
            onClick={()=>onGo(tab,filter)}
            onMouseEnter={e=>e.currentTarget.style.transform='scale(1.02)'}
            onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
            <div style={{fontSize:22,fontWeight:800,color:col}}>{v}</div>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>{l}</div>
            <div style={{fontSize:10,color:'var(--accent)',marginTop:4}}>Click to view →</div>
            {l.includes('Colleges') && <div style={{fontSize:9,color:'var(--text3)',marginTop:2}}>Total in NACTET: {allCollegesCount}</div>}
          </div>
        ))}
      </div>

      {/* Pending alerts - collapsible */}
      {alerts.length>0 && (
        <div className="card" style={{marginBottom:16,borderLeft:'4px solid var(--red)',padding:0}}>
          <div onClick={()=>setPendingOpen(o=>!o)}
            style={{display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'10px 14px',cursor:'pointer',userSelect:'none'}}>
            <div style={{fontWeight:700,fontSize:14,color:'var(--red)'}}>
              ⚠️ Pending Actions
              <span style={{marginLeft:8,background:'var(--red)',color:'#fff',
                borderRadius:10,padding:'1px 8px',fontSize:11}}>{alerts.length}</span>
            </div>
            <span style={{fontSize:12,color:'var(--text3)'}}>{pendingOpen?'▲ Hide':'▼ Show'}</span>
          </div>
          {pendingOpen && (
            <div style={{borderTop:'1px solid var(--border)',padding:'4px 8px 8px'}}>
              {alerts.map((a,i)=>(
                <div key={i} onClick={()=>onGo('programs')}
                  style={{fontSize:12,padding:'6px 4px',borderBottom:'1px solid var(--border)',display:'flex',gap:8,cursor:'pointer',alignItems:'center'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                  onMouseLeave={e=>e.currentTarget.style.background=''}>
                  <span style={{color:a.type==='exam'?'var(--blue)':a.type==='eval'?'var(--amber)':a.type==='result'?'var(--purple)':a.type==='cert'?'var(--orange)':'var(--green)',fontSize:16}}>{
                    a.type==='exam'?'📝':a.type==='eval'?'📊':a.type==='result'?'📢':a.type==='cert'?'📜':'🎓'
                  }</span>
                  <span style={{flex:1}}>{a.msg}</span>
                  <span style={{fontSize:10,color:'var(--accent)'}}>→</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Status flow */}
      <div className="card">
        <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>Program Status — {selYear}</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {STATUS_FLOW.map(s=>{
            const cnt = thisYear.filter(p=>p.status===s).length;
            return (
              <div key={s} onClick={()=>cnt>0&&onGo('programs',{status:s,year:selYear})}
                style={{flex:1,minWidth:100,textAlign:'center',padding:'10px 4px',background:'var(--surface2)',
                  borderRadius:8,cursor:cnt>0?'pointer':'default',border:cnt>0?'1px solid var(--accent-light)':'1px solid transparent'}}
                onMouseEnter={e=>cnt>0&&(e.currentTarget.style.background='var(--accent-light)')}
                onMouseLeave={e=>e.currentTarget.style.background='var(--surface2)'}>
                <div style={{fontSize:20,fontWeight:800,color:cnt>0?'var(--accent)':'var(--text3)'}}>{cnt}</div>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>{s}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Year comparison */}
      {availYears.length > 1 && (
        <div className="card" style={{marginTop:12}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>📈 Year Comparison</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--accent-light)'}}>
                {['Year','Programs','Distributed','Pending','Exam Done %'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:h==='Year'?'left':'center'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {availYears.map((y,i)=>{
                const yProgs = programs.filter(p=>p.year==y);
                const yDist = yProgs.filter(p=>p.status==='Distributed').length;
                const yPend = yProgs.filter(p=>p.status==='Pending').length;
                const yExam = yProgs.filter(p=>['Exam Done','Evaluation Done','Results Shared','Certificates Ready','Distributed'].includes(p.status)).length;
                const pct = yProgs.length>0?((yExam/yProgs.length)*100).toFixed(0):0;
                return (
                  <tr key={y} style={{borderBottom:'1px solid var(--border)',background:y===selYear?'var(--accent-light)':'',cursor:'pointer'}}
                    onClick={()=>setSelYear(y)}>
                    <td style={{padding:'8px 10px',fontWeight:700,color:'var(--accent)'}}>{y}</td>
                    <td style={{padding:'8px 10px',textAlign:'center'}}>{yProgs.length}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{yDist}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',color:'var(--red)',fontWeight:600}}>{yPend}</td>
                    <td style={{padding:'8px 10px',textAlign:'center'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                        <div style={{width:50,height:4,background:'#e5e7eb',borderRadius:2,overflow:'hidden'}}>
                          <div style={{width:pct+'%',height:'100%',background:'var(--green)',borderRadius:2}}/>
                        </div>
                        {pct}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
// ════════════════════════════════════════════════════════════════
function CollegesTab({
  colleges, programs=[], onRefresh, canSeeCoordDetails, isEditor , userDefaultZone=''}) {
  const { canEdit, staff } = useAuth();
  const [search, setSearch] = useState('');
  const [fZone, setFZone] = useState(userDefaultZone);
  const [fUniv, setFUniv] = useState('');
  const [fDist, setFDist] = useState('');
  const [fStream, setFStream] = useState('');
  const [fColStatus, setFColStatus] = useState('');
  const [fYear, setFYear] = useState('2027');
  const [fParticipation, setFParticipation] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selCollege, setSelCollege] = useState(null);
  const [coordinators, setCoordinators] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [classReps, setClassReps] = useState([]);
  const [showRepModal, setShowRepModal] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [repForm, setRepForm] = useState({stream:'',rep_name:'',contact:''});
  const [showRepContact, setShowRepContact] = useState(false);
  const [selRep, setSelRep] = useState(null);
  const [repContactForm, setRepContactForm] = useState({contacted_date:new Date().toISOString().split('T')[0],contacted_by:'',remarks:''});
  const [showCoordModal, setShowCoordModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [editingCoord, setEditingCoord] = useState(null);
  const [savingCoord, setSavingCoord] = useState(false);
  const [masterColleges, setMasterColleges] = useState([]);
  useEffect(()=>{
    supabase.from('colleges').select('id,college_name,district,university,state').order('college_name')
      .then(({data})=>setMasterColleges(data||[]));
    // Auto-sync: subscribe to new colleges added in master
    const sub = supabase.channel('master-colleges-watch')
      .on('postgres_changes', {event:'INSERT', schema:'public', table:'colleges'}, async (payload)=>{
        const c = payload.new;
        const {data: existing} = await supabase.from('nactet_colleges')
          .select('id').eq('college_id', c.id).limit(1);
        if (!existing?.length) {
          await supabase.from('nactet_colleges').insert({
            college_id: c.id,
            college_name: c.college_name,
            district: c.district||'',
            state: c.state||'Kerala',
            zone: '', university: c.university||'',
            streams: [], college_status: 'Running', is_active: true
          });
          onRefresh();
        }
      }).subscribe();
    return ()=>supabase.removeChannel(sub);
  },[]);

  const emptyCol = {
    college_id:'', college_name:'', zone:'', university:'', district:'',
    state:'Kerala', streams:[], college_rating:'', college_status:'Running', remarks:''
  };
  const [form, setForm] = useState(emptyCol);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const emptyCoord = { name:'', phone:'', email:'', role:'', stream:'', valid_from:'', valid_to:'', is_current:true, notes:'' };
  const [coordForm, setCoordForm] = useState(emptyCoord);

  const emptyComm = { comm_date: new Date().toISOString().split('T')[0], comm_by:'', comm_type:'', summary:'', next_followup_date:'', editId: null };
  const [commForm, setCommForm] = useState(emptyComm);

  useEffect(() => {
    supabase.from('colleges').select('id,college_name').eq('is_active',true).order('college_name')
      .then(({data})=>setMasterColleges(data||[]));
  }, []);

  // another staff member changed this college's coordinators / communications / class reps
  useLiveRefresh(['nactet_college_coordinators', 'nactet_communications', 'nactet_class_reps', 'nactet_rep_contacts'], async () => {
    if (!selCollege) return;
    const [{ data: coords }, { data: comms }, { data: reps }] = await Promise.all([
      supabase.from('nactet_college_coordinators').select('*').eq('college_id', selCollege.id).order('valid_from', { ascending: false }),
      supabase.from('nactet_communications').select('*').eq('college_id', selCollege.id).order('comm_date', { ascending: false }),
      supabase.from('nactet_class_reps').select('*, nactet_rep_contacts(*)').eq('college_id', selCollege.id).order('stream'),
    ]);
    setCoordinators(coords||[]);
    setCommunications(comms||[]);
    setClassReps(reps||[]);
  }, !!selCollege, 1500);

  async function loadCollegeDetail(col) {
    setSelCollege({...col}); // spread to force re-render
    const [{ data: coords }, { data: comms }, { data: reps }] = await Promise.all([
      supabase.from('nactet_college_coordinators').select('*').eq('college_id', col.id).order('valid_from', { ascending: false }),
      supabase.from('nactet_communications').select('*').eq('college_id', col.id).order('comm_date', { ascending: false }),
      supabase.from('nactet_class_reps').select('*, nactet_rep_contacts(*)').eq('college_id', col.id).order('stream'),
    ]);
    setCoordinators(coords||[]);
    setCommunications(comms||[]);
    setClassReps(reps||[]);
  }

  async function syncFromMaster() {
    if (!window.confirm('Import all master colleges not yet in NACTET?')) return;
    const { data: existing } = await supabase.from('nactet_colleges').select('college_id, college_name');
    const existingIds = new Set((existing||[]).map(c=>c.college_id).filter(Boolean));
    const existingNames = new Set((existing||[]).map(c=>c.college_name?.trim().toLowerCase()).filter(Boolean));
    const toImport = masterColleges.filter(c=>!existingIds.has(c.id) && !existingNames.has(c.college_name?.trim().toLowerCase()));
    if (toImport.length===0) { toast.info('All master colleges already imported!'); return; }
    let count = 0;
    for (const c of toImport) {
      const { error } = await supabase.from('nactet_colleges').insert({
        college_id: c.id,
        college_name: c.college_name,
        district: c.district||'',
        state: c.state||'Kerala',
        zone: '',
        university: '',
        streams: [],
        college_status: 'Running',
        is_active: true
      });
      if (!error) count++;
    }
    toast.success(`Imported ${count} colleges!`);
    onRefresh();
  }

  async function saveCollege() {
    setSaving(true);
    const payload = { ...form, streams: form.streams || [],
      college_rating: form.college_rating ? Number(form.college_rating) : null,
    };
    // Remove fields that don't exist in nactet_colleges
    delete payload.total_mech_students;
    delete payload.total_civil_students;
    const { error } = editing
      ? await supabase.from('nactet_colleges').update(payload).eq('id', editing)
      : await supabase.from('nactet_colleges').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('College saved'); setShowModal(false); setEditing(null); setForm(emptyCol); onRefresh(); }
    setSaving(false);
  }

  async function deleteCollege(id) {
    const { error } = await supabase.from('nactet_colleges').update({is_active:false}).eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('College deactivated (hidden from active list)'); if(selCollege?.id===id) setSelCollege(null); setTimeout(()=>onRefresh(), 500); }
  }

  async function saveCoordinator() {
    if (!selCollege) return;
    if (!coordForm.stream) { toast.error('Please select a stream'); return; }
    setSavingCoord(true);
    if (coordForm.is_current && coordForm.stream) {
      // ONLY mark same stream as not current - never touch other streams
      let upd = supabase.from('nactet_college_coordinators')
        .update({ is_current: false })
        .eq('college_id', selCollege.id)
        .eq('stream', coordForm.stream);
      if (editingCoord) upd = upd.neq('id', editingCoord);
      await upd;
    }
    const payload = { ...coordForm, college_id: selCollege.id, is_current: coordForm.is_current === true || coordForm.is_current === 'true' };
    const { error } = editingCoord
      ? await supabase.from('nactet_college_coordinators').update(payload).eq('id', editingCoord)
      : await supabase.from('nactet_college_coordinators').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Coordinator saved'); setShowCoordModal(false); setEditingCoord(null); setCoordForm(emptyCoord); await loadCollegeDetail(selCollege); }
    setSavingCoord(false);
  }

  async function saveRep() {
    if (!repForm.rep_name||!repForm.stream) { toast.error('Name and stream required'); return; }
    const payload = {...repForm, college_id: selCollege.id};
    const {error} = editingRep
      ? await supabase.from('nactet_class_reps').update(payload).eq('id',editingRep)
      : await supabase.from('nactet_class_reps').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Saved!'); setShowRepModal(false); loadCollegeDetail(selCollege); }
  }

  async function saveRepContact() {
    if (!repContactForm.remarks) { toast.error('Enter remarks'); return; }
    const {error} = await supabase.from('nactet_rep_contacts').insert({...repContactForm, rep_id: selRep.id});
    if (error) toast.error(error.message);
    else { toast.success('Logged!'); setShowRepContact(false); loadCollegeDetail(selCollege); }
  }

  async function saveComm() {
    if (!selCollege) return;
    const { editId: commEditId, ...commData } = commForm;
    const payload = { ...commData, college_id: selCollege.id };
    const { error } = commEditId
      ? await supabase.from('nactet_communications').update(payload).eq('id', commEditId)
      : await supabase.from('nactet_communications').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(commEditId ? 'Updated' : 'Communication logged'); setShowCommModal(false); setCommForm(emptyComm); loadCollegeDetail(selCollege); }
  }

  const yearPrograms = fYear ? programs.filter(p=>String(p.year)===String(fYear) && colleges.find(c=>c.id===p.college_id)?.college_status!=='Shutdown') : [];
  const yearCollegeIds = new Set(yearPrograms.map(p=>p.college_id));
  const collegeYearStatus = {};
  yearPrograms.forEach(p=>{ if(!collegeYearStatus[p.college_id]) collegeYearStatus[p.college_id]=p.status; });
  const filtered = colleges.filter(c => {
    if (fUniv && c.university !== fUniv) return false;
    const inYear = fYear ? yearCollegeIds.has(c.id) : true;
    const matchPart = !fParticipation ||
      (fParticipation==='active' && inYear && !['Shutdown','Converted'].includes(c.college_status||'Running')) ||
      (fParticipation==='not_added' && fYear && !inYear && !['Shutdown','Converted'].includes(c.college_status||'Running')) ||
      (fParticipation==='inactive' && ['Shutdown','Converted'].includes(c.college_status||'Running'));
    return (
      (!search || c.college_name?.toLowerCase().includes(search.toLowerCase())) &&
      matchPart &&
      (!fZone || c.zone === fZone) &&
      (!fDist || c.district?.toLowerCase() === fDist?.toLowerCase()) &&
      (!fStream || (Array.isArray(c.streams)?c.streams:JSON.parse(c.streams||'[]')).includes(fStream)) &&
      (!fColStatus || (c.college_status||'Running') === fColStatus)
    );
  });

  function clearFilters(){setSearch('');setFZone('');setFUniv('');setFDist('');setFStream('');setFColStatus('');setFParticipation('');}

  return (
    <div style={{display:'grid',gridTemplateColumns:selCollege?'340px 1fr':'1fr',gap:16}}>
      {/* College List */}
      <div>
        <div className="card" style={{marginBottom:12}}>
          <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search college..." style={{flex:1,minWidth:120}}/>
            {canEdit('nactet') && <button className="btn btn-primary btn-sm" onClick={()=>{setEditing(null);setForm(emptyCol);setShowModal(true);}}>+ Add</button>}
          {canEdit('nactet')&&<button className="btn btn-ghost btn-sm" onClick={syncFromMaster}>🔄 Sync from Master</button>}
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:4,width:'fit-content'}}>
            <select value={fYear} onChange={e=>setFYear(e.target.value)} style={{fontSize:11,fontWeight:600,color:'var(--accent)',maxWidth:80}}>
              <option value="">All Years</option>
              {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            <select value={fParticipation} onChange={e=>setFParticipation(e.target.value)} style={{fontSize:11,maxWidth:160}}>
              <option value="">All Colleges</option>
              <option value="active">Has {fYear||'Selected'} Program</option>
              <option value="not_added">No {fYear||'Selected'} Program (needs contact)</option>
              <option value="inactive">Shutdown/Converted</option>
            </select>
            <select value={fZone} onChange={e=>setFZone(e.target.value)} style={{fontSize:11,maxWidth:120}}>
              <option value="">All Zones</option>
              {ZONES.map(z=><option key={z} value={z}>{z}</option>)}
            </select>
            <select value={fUniv} onChange={e=>setFUniv(e.target.value)} style={{fontSize:11,maxWidth:120}}>
              <option value="">All Universities</option>
              {[...new Set(colleges.map(c=>c.university).filter(Boolean))].sort().map(u=>(
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
            <select value={fDist} onChange={e=>setFDist(e.target.value)} style={{fontSize:11,maxWidth:120}}>
              <option value="">All Districts</option>
              {[...new Set(colleges.map(c=>c.district).filter(Boolean))].sort().map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            <select value={fStream} onChange={e=>setFStream(e.target.value)} style={{fontSize:11,maxWidth:120}}>
              <option value="">All Streams</option>
              {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <select value={fColStatus} onChange={e=>setFColStatus(e.target.value)} style={{fontSize:11,width:'auto',borderColor:'var(--amber)',color:'var(--amber)'}}>
              <option value="">All Status</option>
              {['Running','Shutdown','Reopen','Converted'].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
        <button onClick={()=>clearFilters()} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>✕ Clear</button>
          </div>
        </div>
        <div className="card" style={{padding:0,maxHeight:'70vh',overflowY:'auto'}}>
          <div style={{padding:'8px 12px',fontWeight:600,fontSize:13,borderBottom:'1px solid var(--border)',background:'var(--accent-light)'}}>
            {filtered.length} Colleges
          </div>
          {filtered.map(c=>(
                        <div key={c.id}
              style={{padding:'10px 12px',borderBottom:'1px solid var(--border)',
                background:selCollege?.id===c.id?'var(--accent-light)':''}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:4}}>
                <div style={{flex:1,cursor:'pointer',minWidth:0}} onClick={()=>loadCollegeDetail(c)}>
                  <div style={{fontWeight:600,fontSize:13}}>{c.college_name}</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                    {c.district} · {c.zone} · {c.university}
                    {fYear && <span style={{marginLeft:6,padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:600,
                      background:yearCollegeIds.has(c.id)?'var(--green-light)':'var(--red-light)',
                      color:yearCollegeIds.has(c.id)?'var(--green)':'var(--red)'}}>
                      {yearCollegeIds.has(c.id)?(collegeYearStatus[c.id]||'Active'):'Not in '+fYear}
                    </span>}
                  </div>
                  <div style={{fontSize:11,marginTop:2,display:'flex',gap:6,flexWrap:'wrap'}}>
                    {(Array.isArray(c.streams)?c.streams:JSON.parse(c.streams||'[]')).map(s=><span key={s} className="badge badge-blue" style={{fontSize:9}}>{s}</span>)}
                    {c.college_rating && <span style={{color:'var(--amber)'}}>{'★'.repeat(c.college_rating)}</span>}
                    {c.college_status && c.college_status!=='Running' && <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,
                      background:c.college_status==='Shutdown'?'var(--red-light)':c.college_status==='Reopen'?'var(--amber-light)':'var(--purple-light)',
                      color:c.college_status==='Shutdown'?'var(--red)':c.college_status==='Reopen'?'var(--amber)':'var(--purple)'}}>
                      {c.college_status}
                    </span>}
                  </div>
                </div>
                <div style={{display:'flex',gap:2,flexShrink:0,marginTop:2}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 6px'}}
                    onClick={e=>{e.stopPropagation();loadCollegeDetail(c);}}>👁</button>
                  {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 6px'}}
                    onClick={e=>{e.stopPropagation();setEditing(c.id);setForm({...c});setShowModal(true);}}>✏️</button>}
                  {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 6px',color:'var(--red)'}}
                    onClick={e=>{e.stopPropagation();if(window.confirm('Deactivate '+c.college_name+'? It will be hidden but not permanently deleted.'))deleteCollege(c.id);}}>🗑</button>}
                </div>
              </div>
            </div>
          ))}
          {filtered.length===0 && <div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No colleges found</div>}
        </div>
      </div>

      {/* College Detail */}
      {selCollege && (
        <div>
          <div className="card" style={{marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:16}}>{selCollege.college_name}</div>
                <div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>{selCollege.district} · {selCollege.zone} · {selCollege.university}</div>
                <div style={{marginTop:6,display:'flex',gap:6,flexWrap:'wrap'}}>
                  {(Array.isArray(selCollege.streams)?selCollege.streams:JSON.parse(selCollege.streams||'[]')).map(s=><span key={s} className="badge badge-blue">{s}</span>)}
                </div>
              </div>
              <div style={{display:'flex',gap:6}}>
                {canEdit('nactet') && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(selCollege.id);setForm({...selCollege, college_id: selCollege.college_id||''});setShowModal(true);}}>Edit</button>}
                <button className="btn btn-ghost btn-sm" onClick={()=>setSelCollege(null)}>✕</button>
              </div>
            </div>
            {/* Year-wise program summary */}
            {(()=>{
              const colProgs = selCollege?.college_status==='Shutdown' ? [] : programs.filter(p=>p.college_id===selCollege.id);
              if (!colProgs.length) return <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>No programs yet</div>;
              const byYear = {};
              colProgs.forEach(p=>{ if(!byYear[p.year]) byYear[p.year]=[]; byYear[p.year].push(p); });
              return (
                <div style={{marginTop:12,overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr style={{background:'var(--accent-light)'}}>
                        {['Year','Stream','Batches','Students','Status'].map(h=>(
                          <th key={h} style={{padding:'5px 8px',textAlign:'left',fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(byYear).sort((a,b)=>b-a).map(year=>
                        byYear[year].map((p,i)=>(
                          <tr key={p.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                            <td style={{padding:'5px 8px',fontWeight:i===0?700:400,color:'var(--accent)'}}>{i===0?year:''}</td>
                            <td style={{padding:'5px 8px'}}><span className={`badge ${p.stream==='Mechanical'?'badge-blue':'badge-green'}`} style={{fontSize:9}}>{p.stream}{p.batch_label?' — Batch '+p.batch_label:''}</span></td>
                            <td style={{padding:'5px 8px',textAlign:'center'}}>{p.num_batches||'-'}</td>
                            <td style={{padding:'5px 8px',textAlign:'center'}}>{p.registered_students||p.students_attended||'-'}</td>
                            <td style={{padding:'5px 8px'}}><span className={`badge ${STATUS_COLORS[p.status]||'badge-gray'}`} style={{fontSize:9}}>{p.status}</span></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {/* Coordinators */}
          {canSeeCoordDetails && (
            <div className="card" style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                <span style={{fontWeight:600,fontSize:14}}>👤 Coordinators</span>
                {canEdit('nactet') && <button className="btn btn-primary btn-sm" onClick={()=>{setEditingCoord(null);setCoordForm(emptyCoord);setShowCoordModal(true);}}>+ Add</button>}
              </div>
              {coordinators.length===0 && <p style={{color:'var(--text3)',fontSize:12}}>No coordinators added yet.</p>}
              {/* Current coordinators by stream */}
              {[...new Set([...STREAMS, ...coordinators.filter(c=>c.is_current).map(c=>c.stream)].filter(Boolean))].map(stream => {
                const curr = coordinators.filter(co=>co.is_current && (co.stream===stream || co.stream==='Both'));
                if (!curr.length) return null;
                return <div key={stream}>
                  <div style={{fontSize:11,fontWeight:600,color:'var(--accent)',marginBottom:4,marginTop:6}}>
                    {stream==='Mechanical'?'⚙️':'🏗️'} {stream} — Current ({curr[0]?.valid_to ? new Date(curr[0].valid_to).getFullYear() : new Date().getFullYear()})
                  </div>
                  {curr.map(co=>(
                <div key={co.id} style={{padding:'8px 10px',borderRadius:6,background:'#f0fdf4',marginBottom:6,border:'1px solid #86efac'}}>
                  <div style={{display:'flex',justifyContent:'space-between'}}>
                    <div style={{fontWeight:600,fontSize:13}}>{co.name} <span className="badge badge-green" style={{fontSize:9}}>Current {co.valid_to ? new Date(co.valid_to).getFullYear() : new Date().getFullYear()}</span></div>
                    <div style={{display:'flex',gap:4}}>
                      {canEdit('nactet') && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingCoord(co.id);setCoordForm({...co});setShowCoordModal(true);}}>Edit</button>}
                      {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm(`Delete coordinator ${co.name} (${co.stream} stream)? This will only delete this specific coordinator.`))return;const{error}=await supabase.from('nactet_college_coordinators').delete().eq('id',co.id);if(error)toast.error(error.message);else loadCollegeDetail(selCollege);}}>🗑</button>}
                    </div>
                  </div>
                  <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>
                    {co.role} · {co.stream}
                  </div>
                  <div style={{fontSize:11,marginTop:2}}>📞 {co.phone||'—'} · ✉️ {co.email||'—'}</div>
                </div>
              ))}</div>;})}
              {/* Historical coordinators */}
              {coordinators.filter(co=>!co.is_current).length > 0 && (
                <div style={{marginTop:8}}>
                  <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',marginBottom:4}}>📅 Previous Coordinators</div>
                  {coordinators.filter(co=>!co.is_current).map(co=>(
                    <div key={co.id} style={{padding:'8px 10px',borderRadius:6,background:'var(--surface2)',marginBottom:4,border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <div style={{fontWeight:500,fontSize:12}}>{co.name} {co.valid_from && <span style={{fontSize:10,color:'var(--text3)',marginLeft:6}}>{new Date(co.valid_from).getFullYear()}{co.valid_to?' → '+new Date(co.valid_to).getFullYear():''}</span>}</div>
                        <div style={{display:'flex',gap:4}}>
                        {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>{setEditingCoord(co.id);setCoordForm({...co});setShowCoordModal(true);}}>Edit</button>}
                        </div>
                      {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)',fontSize:10}} onClick={async()=>{if(!window.confirm(`Delete ${co.name} (${co.stream}) from history?`))return;const{error}=await supabase.from('nactet_college_coordinators').delete().eq('id',co.id);if(error)toast.error(error.message);else loadCollegeDetail(selCollege);}}>🗑</button>}              </div>
                      <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                        {co.role} · {co.phone||'—'} · {co.email||'—'}
                      </div>
                      {co.notes && <div style={{fontSize:10,color:'var(--text3)',marginTop:2,fontStyle:'italic'}}>Notes: {co.notes.substring(0,80)}{co.notes.length>80?'...':''}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Communication Log */}
              <div style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                  <span style={{fontWeight:600,fontSize:14}}>💬 Communication Log</span>
                  {canEdit('nactet') && <button className="btn btn-primary btn-sm" onClick={()=>{setCommForm(emptyComm);setShowCommModal(true);}}>+ Log</button>}
                </div>
                {communications.length===0 && <p style={{color:'var(--text3)',fontSize:12}}>No communications logged yet.</p>}
                {communications.map(cm=>(
                  <div key={cm.id} style={{padding:'6px 8px',borderBottom:'1px solid var(--border)',fontSize:12}}>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontWeight:600}}>{fmtDate(cm.comm_date)}</span>
                      <span className="badge badge-blue" style={{fontSize:9}}>{cm.comm_type}</span>
                      <span style={{color:'var(--text3)'}}>{cm.comm_by}</span>
                    </div>
                    <div style={{marginTop:2,color:'var(--text2)'}}>{cm.summary}</div>
                    {cm.next_followup_date && <div style={{fontSize:11,color:'var(--amber)',marginTop:2}}>Follow up: {fmtDate(cm.next_followup_date)}</div>}
                    {canEdit('nactet') && <div style={{display:'flex',gap:4,marginTop:4}}>
                      <button className='btn btn-ghost btn-sm' style={{fontSize:10}} onClick={()=>{setCommForm({comm_date:cm.comm_date||'',comm_type:cm.comm_type||'',comm_by:cm.comm_by||'',summary:cm.summary||'',next_followup_date:cm.next_followup_date||'',editId:cm.id});setShowCommModal(true);}}>✏️</button>
                      <button className='btn btn-ghost btn-sm' style={{fontSize:10,color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete this log?'))return;await supabase.from('nactet_communications').delete().eq('id',cm.id);loadCollegeDetail(selCollege);}}>🗑</button>
                    </div>}
                  </div>
                ))}
              </div>
              <CollegeNotes college={selCollege} canEditNotes={canEdit('nactet')} staff={staff}/>
            </div>
          )}

          {/* Class Representatives */}
          <div className="card" style={{marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <span style={{fontWeight:600,fontSize:14}}>🎓 Class Representatives</span>
              {canEdit('nactet')&&<button className="btn btn-primary btn-sm" onClick={()=>{setEditingRep(null);setRepForm({stream:'',rep_name:'',contact:''});setShowRepModal(true);}}>+ Add Rep</button>}
            </div>
            {classReps.length===0
              ?<div style={{color:'var(--text3)',fontSize:13}}>No class representatives added yet</div>
              :<table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'var(--accent-light)'}}><th style={{padding:'5px 8px',textAlign:'left'}}>Stream</th><th style={{padding:'5px 8px',textAlign:'left'}}>Name</th><th style={{padding:'5px 8px',textAlign:'left'}}>Contact</th><th style={{padding:'5px 8px',textAlign:'left'}}>Last Contact</th><th style={{padding:'5px 8px'}}>Actions</th></tr></thead>
                <tbody>{classReps.map(r=>{
                  const contacts=r.nactet_rep_contacts||[];
                  const last=contacts[0];
                  return (
                    <tr key={r.id} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={{padding:'5px 8px'}}><span className="badge badge-blue">{r.stream}</span></td>
                      <td style={{padding:'5px 8px',fontWeight:500}}>{r.rep_name}</td>
                      <td style={{padding:'5px 8px',fontFamily:'monospace'}}>{r.contact||'—'}</td>
                      <td style={{padding:'5px 8px',fontSize:11,color:'var(--text3)'}}>{last?`${last.contacted_date} — ${last.remarks||''}`:'Not contacted yet'}</td>
                      <td style={{padding:'5px 8px'}}><div style={{display:'flex',gap:4}}>
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>{setSelRep(r);setRepContactForm({contacted_date:new Date().toISOString().split('T')[0],contacted_by:'',remarks:''});setShowRepContact(true);}}>📞 Log</button>
                        {canEdit('nactet')&&<button className="btn btn-ghost btn-sm" onClick={()=>{setEditingRep(r.id);setRepForm({stream:r.stream,rep_name:r.rep_name,contact:r.contact||''});setShowRepModal(true);}}><span style={{fontSize:11}}>✏️</span></button>}
                        {canEdit('nactet')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete?'))return;await supabase.from('nactet_rep_contacts').delete().eq('rep_id',r.id);
const{error}=await supabase.from('nactet_class_reps').delete().eq('id',r.id);
if(error)toast.error(error.message);else loadCollegeDetail(selCollege);}}>🗑</button>}
                      </div></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            }
          </div>

        </div>
      )}

      {/* College Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} College</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group full" style={{gridColumn:'1/-1'}}>
                  <label>College Name * <span style={{fontSize:11,color:'var(--text3)',fontWeight:400}}>(select from master list)</span></label>
                  <SearchableSelect
                    options={masterColleges.map(c=>({value:c.id, label:c.college_name}))}
                    value={form.college_id||''}
                    onChange={v=>{
                      const mc = masterColleges.find(c=>c.id===v);
                      if (mc) {
                        sf('college_id', mc.id);
                        sf('college_name', mc.college_name);
                        if (!form.district && mc.district) sf('district', mc.district);
                        if (!form.university && mc.university) sf('university', mc.university);
                        if (!form.state && mc.state) sf('state', mc.state);
                      }
                    }}
                    placeholder="Search and select college..."
                    searchPlaceholder="Type college name..."
                  />
                  {form.college_name && <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Selected: {form.college_name}</div>}
                  {!form.college_id && form.college_name && (
                    <div style={{fontSize:11,color:'var(--amber)',marginTop:2}}>
                      ⚠️ Not linked to master colleges — please select from dropdown above
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Zone</label>
                  <select value={form.zone} onChange={e=>sf('zone',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {ZONES.map(z=><option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>University</label>
                  <select value={form.university} onChange={e=>sf('university',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {UNIVERSITIES.map(u=><option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select value={form.district} onChange={e=>sf('district',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {DISTRICTS.map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select value={form.state||''} onChange={e=>sf('state',e.target.value)}>
                    <option value=''>Select State</option>
                    {['Kerala','Tamil Nadu','Karnataka','Andhra Pradesh','Telangana','Maharashtra','Gujarat','Rajasthan','Delhi','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Streams</label>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
                    {STREAMS.map(s=>(
                      <label key={s} style={{fontSize:12,display:'flex',gap:4,alignItems:'center',cursor:'pointer'}}>
                        <input type="checkbox" checked={(form.streams||[]).includes(s)}
                          onChange={e=>sf('streams', e.target.checked ? [...(form.streams||[]),s] : (form.streams||[]).filter(x=>x!==s))}/>
                        {s}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>College Rating (1-5)</label>
                  <select value={form.college_rating||''} onChange={e=>sf('college_rating',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Not rated</option>
                    {[1,2,3,4,5].map(n=><option key={n} value={n}>{'★'.repeat(n)} ({n})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>College Status</label>
                  <select value={form.college_status||'Running'} onChange={e=>sf('college_status',e.target.value)}
                    style={{borderColor: form.college_status==='Shutdown'?'var(--red)':form.college_status==='Reopen'?'var(--amber)':form.college_status==='Converted'?'var(--purple)':'var(--border)'}}>
                    <option value="Running">🟢 Running</option>
                    <option value="Shutdown">🔴 Shutdown</option>
                    <option value="Reopen">🟡 Reopen</option>
                    <option value="Converted">🟣 Converted</option>
                  </select>
                </div>

                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Remarks</label>
                  <textarea value={form.remarks||''} onChange={e=>sf('remarks',e.target.value)} rows={2}/>
                </div>
              </div>
            </div>
            {/* Location coordinates for route optimization */}
            <div style={{borderTop:'1px solid var(--border)',paddingTop:12,marginTop:4}}>
              <div style={{fontWeight:600,fontSize:12,marginBottom:8,color:'var(--text2)'}}>📍 Location Coordinates (for route optimization)</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Latitude</label>
                  <input type="number" step="0.0000001" value={form.latitude||''} onChange={e=>sf('latitude',e.target.value)} placeholder="e.g. 10.8505"/>
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input type="number" step="0.0000001" value={form.longitude||''} onChange={e=>sf('longitude',e.target.value)} placeholder="e.g. 76.2711"/>
                </div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center',marginTop:6}}>
                <div style={{fontSize:11,color:'var(--text3)',flex:1}}>💡 Right-click on Google Maps → copy lat/lng, or click Auto-fetch below</div>
                <button type="button" className="btn btn-ghost btn-sm" style={{fontSize:11}}
                  onClick={async()=>{
                    if(!form.college_name){toast.error('Enter college name first');return;}
                    toast.loading('Fetching coordinates...',{id:'geo'});
                    try{
                      const q=encodeURIComponent(form.college_name+', '+(form.district||'')+'Kerala, India');
                      const r=await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=in`,{headers:{'User-Agent':'ArmsERP/1.0'}});
                      const d=await r.json();
                      if(d[0]){sf('latitude',parseFloat(d[0].lat).toFixed(7));sf('longitude',parseFloat(d[0].lon).toFixed(7));toast.success('Coordinates fetched!',{id:'geo'});}
                      else toast.error('Location not found — enter manually',{id:'geo'});
                    }catch(e){toast.error('Failed to fetch',{id:'geo'});}
                  }}>🌐 Auto-fetch</button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!form.college_name||saving} onClick={saveCollege}>
                {saving?'Saving...':'Save College'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coordinator Modal */}
      {/* Class Rep Modal */}
      {showRepModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowRepModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">{editingRep?'Edit':'Add'} Class Representative</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowRepModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Stream *</label><select value={repForm.stream} onChange={e=>setRepForm(f=>({...f,stream:e.target.value}))}><option value="">Select</option>{STREAMS.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group"><label>Rep Name *</label><input value={repForm.rep_name} onChange={e=>setRepForm(f=>({...f,rep_name:e.target.value}))} placeholder="Class representative name"/></div>
                <div className="form-group"><label>Contact Number</label><input value={repForm.contact||''} onChange={e=>setRepForm(f=>({...f,contact:e.target.value}))} placeholder="Phone number"/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowRepModal(false)}>Cancel</button><button className="btn btn-primary" onClick={saveRep}>Save</button></div>
          </div>
        </div>
      )}

      {/* Rep Contact Log Modal */}
      {showRepContact&&selRep&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowRepContact(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">📞 Log Contact — {selRep.rep_name} ({selRep.stream})</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowRepContact(false)}>✕</button></div>
            <div className="modal-body">
              {(selRep.nactet_rep_contacts||[]).length>0&&(
                <div style={{marginBottom:12,maxHeight:150,overflowY:'auto'}}>
                  <div style={{fontWeight:600,fontSize:12,marginBottom:6}}>Contact History</div>
                  {(selRep.nactet_rep_contacts||[]).map((c,i)=>(
                    <div key={i} style={{padding:'4px 0',borderBottom:'1px solid var(--border)',fontSize:12}}>
                      <span style={{color:'var(--text3)'}}>{fmtDMY(c.contacted_date)}</span> · <strong>{c.contacted_by||'—'}</strong> · {c.remarks}
                    </div>
                  ))}
                </div>
              )}
              <div className="form-grid">
                <div className="form-group"><label>Date</label><DateInput value={repContactForm.contacted_date} onChange={e=>setRepContactForm(f=>({...f,contacted_date:e.target.value}))}/></div>
                <div className="form-group"><label>Contacted By</label><input value={repContactForm.contacted_by||''} onChange={e=>setRepContactForm(f=>({...f,contacted_by:e.target.value}))} placeholder="Staff name"/></div>
                <div className="form-group full"><label>Remarks *</label><textarea rows={2} value={repContactForm.remarks||''} onChange={e=>setRepContactForm(f=>({...f,remarks:e.target.value}))} placeholder="What was discussed, outcome..."/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowRepContact(false)}>Cancel</button><button className="btn btn-primary" onClick={saveRepContact}>Save</button></div>
          </div>
        </div>
      )}

      {showCoordModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCoordModal(false)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header">
              <span className="modal-title">{editingCoord?'Edit':'Add'} Coordinator</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowCoordModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Name *</label>
                  <input value={coordForm.name} onChange={e=>setCoordForm(f=>({...f,name:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input value={coordForm.phone||''} onChange={e=>setCoordForm(f=>({...f,phone:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input value={coordForm.email||''} onChange={e=>setCoordForm(f=>({...f,email:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={coordForm.role||''} onChange={e=>setCoordForm(f=>({...f,role:e.target.value}))}>
                    <option value="">Select</option>
                    {COORD_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stream</label>
                  <select value={coordForm.stream||''} onChange={e=>setCoordForm(f=>({...f,stream:e.target.value}))}>
                    <option value="">Select</option>
                    {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Valid From</label>
                  <DateInput value={coordForm.valid_from||''} onChange={e=>setCoordForm(f=>({...f,valid_from:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Valid To</label>
                  <DateInput value={coordForm.valid_to||''} onChange={e=>setCoordForm(f=>({...f,valid_to:e.target.value}))}/>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="isCurrent" checked={coordForm.is_current} onChange={e=>setCoordForm(f=>({...f,is_current:e.target.checked}))}/>
                  <label htmlFor="isCurrent" style={{marginBottom:0,cursor:'pointer'}}>Current Coordinator</label>
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <textarea value={coordForm.notes||''} onChange={e=>setCoordForm(f=>({...f,notes:e.target.value}))} rows={2}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowCoordModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!coordForm.name||savingCoord} onClick={saveCoordinator}>
                {savingCoord?'Saving...':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Communication Modal */}
      {showCommModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCommModal(false)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">Log Communication</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowCommModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Date</label>
                <DateInput value={commForm.comm_date} onChange={e=>setCommForm(f=>({...f,comm_date:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>By (Armstech Staff)</label>
                <select value={commForm.comm_by} onChange={e=>setCommForm(f=>({...f,comm_by:e.target.value}))}>
                  <option value="">Select</option>
                  {ARMSTECH_STAFF.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={commForm.comm_type} onChange={e=>setCommForm(f=>({...f,comm_type:e.target.value}))}>
                  <option value="">Select</option>
                  {COMM_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Summary</label>
                <textarea value={commForm.summary||''} onChange={e=>setCommForm(f=>({...f,summary:e.target.value}))} rows={3}/>
              </div>
              <div className="form-group">
                <label>Next Follow-up Date</label>
                <DateInput value={commForm.next_followup_date||''} onChange={e=>setCommForm(f=>({...f,next_followup_date:e.target.value}))}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowCommModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveComm}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function copyWhatsApp(p, col) {
  const examDate = p.actual_exam_date || p.proposed_exam_date || 'TBD';
  const msg = `*NACTET Scholarship ${p.year}*\n`+
    `College: ${col?.college_name||''}\n`+
    `Stream: ${p.stream}\n`+
    `Exam Date: ${examDate}\n`+
    `Registered Students: ${p.registered_students||0}\n`+
    `Status: ${p.status}\n`+
    `\n_Armstech Engineers Pvt Ltd_`;
  navigator.clipboard.writeText(msg).then(()=>toast.success('Message copied!'));
}

// ════════════════════════════════════════════════════════════════
// PROGRAMS TAB
// ════════════════════════════════════════════════════════════════
function ProgramsTab({
  colleges, programs, onRefresh, isEditor, initFilter={}, userDefaultZone='', onFilterUsed }) {
  const { canEdit } = useAuth();
  const [teams, setTeams] = useState([]);
  React.useEffect(()=>{ supabase.from('visit_teams').select('*').eq('is_active',true).order('team_name').then(({data})=>setTeams(data||[])); },[]);
  const [fCollege, setFCollege] = useState('');
  const [fYear, setFYear] = useState('2027');
  const [fStream, setFStream] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fMarksPending, setFMarksPending] = useState(false);
  // How many students have been entered for each program (marks pending = exam done but nobody entered yet)
  const [entered, setEntered] = useState({});
  React.useEffect(()=>{
    let dead = false;
    (async()=>{
      try {
        let all = [], from = 0;
        while (true) {
          const { data, error } = await supabase.from('nactet_students').select('program_id,marks').order('id').range(from, from+999);
          if (error || !data) break;
          all = all.concat(data);
          if (data.length < 1000) break;
          from += 1000;
        }
        const m = {};
        all.forEach(r=>{ if(!r.program_id) return; const e = m[r.program_id] || (m[r.program_id]={total:0,withMarks:0}); e.total++; if(r.marks!=null) e.withMarks++; });
        if (!dead) setEntered(m);
      } catch(e) { /* leave empty — falls back to the attended count */ }
    })();
    return ()=>{ dead = true; };
  },[programs]);
  const marksPending = p => ['Exam Done','Evaluation Done'].includes(p.status) && (p.students_attended||0)===0 && !(entered[p.id]?.total>0);
  const attendedOf = p => p.students_attended || entered[p.id]?.withMarks || 0;
  const [fZone, setFZone] = useState(userDefaultZone);
  const [fUniv, setFUniv] = useState('');
  const availUnivs = [...new Set(colleges.map(c=>c.university).filter(Boolean))].sort();
  React.useEffect(()=>{
    if(initFilter && Object.keys(initFilter).length>0){
      if(initFilter.status) setFStatus(initFilter.status);
      if(initFilter.year) setFYear(initFilter.year);
      if(onFilterUsed) onFilterUsed();
    }
  },[]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkYear, setBulkYear] = useState('2027');
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const [bulkEditColleges, setBulkEditColleges] = useState([]);
  const [bulkEditYear, setBulkEditYear] = useState('2027');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkColSearch, setBulkColSearch] = useState('');

  async function bulkAddNew() {
    // Load exclusions for this year
    const {data: exclusions} = await supabase.from('nactet_bulk_exclusions').select('college_id,stream').eq('year', year);
    const excludedKeys = new Set((exclusions||[]).map(e=>e.college_id+'_'+e.stream));
    const year = Number(bulkYear);
    if (!year) { toast.error('Select a year'); return; }
    const existing = programs.filter(p=>Number(p.year)===year);
    const existingKeys = new Set(existing.map(p=>`${p.college_id}-${p.stream}`));
    const toInsert = [];
    for (const col of colleges) {
      const streams = Array.isArray(col.streams) ? col.streams : JSON.parse(col.streams||'[]');
      for (const stream of streams) {
        const key = `${col.id}-${stream}`;
        if (!existingKeys.has(key) && !excludedKeys.has(col.id+'_'+stream)) {
          toInsert.push({ college_id: col.id, year, stream, status:'Pending',
            exam_mode:'Offline', reg_status:'Not Completed',
            registered_students:0, students_attended:0,
            evaluation_done:false, certificate_distributed:false, certificates_prepared:false,
            qb_received_confirmed:false });
        }
      }
    }
    if (toInsert.length===0) { toast.info(`All ${year} programs already exist!`); return; }
    if (!window.confirm(`Add ${toInsert.length} new programs for ${year}?`)) return;
    setBulkAdding(true);
    // Insert in chunks of 100
    for (let i=0; i<toInsert.length; i+=100) {
      const { error } = await supabase.from('nactet_programs').insert(toInsert.slice(i,i+100));
      if (error) { toast.error(error.message); setBulkAdding(false); return; }
    }
    setBulkAdding(false);
    toast.success(`✅ Added ${toInsert.length} programs for ${year}!`);
    onRefresh();
  }

  function openBulkEdit() {
    if (!bulkEditColleges.length) { toast.error('Select at least one college'); return; }
    const rows = programs.filter(p =>
      bulkEditColleges.includes(p.college_id) && String(p.year)===String(bulkEditYear)
    );
    if (rows.length===0) { toast.info('No programs found for selected colleges/year'); return; }
    setBulkRows(rows.map(r=>({...r})));
    toast.success(`Loaded ${rows.length} programs`);
  }

  function updateBulkRow(id, field, value) {
    setBulkRows(rows=>rows.map(r=>r.id===id?{...r,[field]:value}:r));
  }

  async function saveBulkEdit() {
    setBulkSaving(true);
    for (const row of bulkRows) {
      const { error } = await supabase.from('nactet_programs').update({
        status: row.status, exam_mode: row.exam_mode,
        reg_status: row.reg_status,
        registered_students: row.registered_students!==''&&row.registered_students!=null?Number(row.registered_students):null,
        total_students: row.total_students!==''&&row.total_students!=null?Number(row.total_students):null,
        students_attended: row.students_attended!==''&&row.students_attended!=null?Number(row.students_attended):null,
        proposed_exam_date: row.proposed_exam_date||null,
        actual_exam_date: row.actual_exam_date||null,
        exam_visit_by: row.exam_visit_by||null,
        exam_photo_taken: row.exam_photo_taken||false,
        exam_photo_date: row.exam_photo_date||null,
        qb_required: row.qb_required||false,
        qb_sent_date: row.qb_sent_date||null,
        qb_received_confirmed: row.qb_received_confirmed||false,
        qb_returned: row.qb_returned||false,
        answer_sheets_received: row.answer_sheets_received||false,
        evaluation_done: row.evaluation_done||false,
        evaluation_date: row.evaluation_date||null,
        result_communicated: row.result_communicated||false,
        result_communicated_date: row.result_communicated_date||null,
        result_medium: row.result_medium||null,
        certificates_to_prepare: Number(row.certificates_to_prepare)||0,
        certificates_prepared: row.certificates_prepared||false,
        certificates_prepared_date: row.certificates_prepared_date||null,
        certificate_distributed: row.certificate_distributed||false,
        certificate_distribution_date: row.certificate_distribution_date||null,
        certificate_photo_taken: row.certificate_photo_taken||false,
        cert_visit_by: row.cert_visit_by||null,
        notes: row.notes||''
      }).eq('id', row.id);
      if (error) { toast.error('Error: '+error.message); setBulkSaving(false); return; }
    }
    setBulkSaving(false);
    setShowBulkEdit(false);
    toast.success(`✅ Saved ${bulkRows.length} programs!`);
    onRefresh();
  }

  const emptyProg = {
    college_id:'', year: new Date().getFullYear().toString(), stream:'Mechanical',
    proposed_exam_date:'', actual_exam_date:'', exam_mode:'Offline',
    qb_required:false, qb_sent_date:'', qb_delivery_method:'', qb_courier_tracking:'',
    qb_received_confirmed:false, qb_returned:false,
    reg_status:'', registered_students:0, total_students:0,
    students_attended:0, exam_photo_taken:false, exam_photo_date:'',
    evaluation_location:'', answer_sheets_received:false, evaluation_done:false, evaluation_date:'',
    result_communicated:false, result_communicated_date:'', result_medium:'',
    certificates_to_prepare:0, certificates_prepared:false, certificates_prepared_date:'',
    certificate_distributed:false, certificate_distribution_date:'', certificate_photo_taken:false,
    exam_visit_by:'', cert_visit_by:'',
    status:'Pending', notes:'', batch_label:'', num_batches:1
  };
  const [form, setForm] = useState(emptyProg);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  async function saveProg() {
    setSaving(true);
    const DATE_FIELDS = ['proposed_exam_date','actual_exam_date','qb_sent_date','exam_photo_date',
      'evaluation_date','result_communicated_date','certificates_prepared_date',
      'certificate_distribution_date','qb_received_date'];
    const numBatches = Number(form.num_batches)||1;
    const toInt = v => (v!==''&&v!=null&&v!==undefined)?Number(v):null;
    const basePayload = { ...form, year: Number(form.year),
      registered_students: toInt(form.registered_students),
      total_students: toInt(form.total_students),
      students_attended: toInt(form.students_attended),
      certificates_to_prepare: toInt(form.certificates_to_prepare),
      num_batches: toInt(form.num_batches),
      batch_students_a: toInt(form.batch_students_a),
      batch_students_b: toInt(form.batch_students_b),
      batch_students_c: toInt(form.batch_students_c),
      batch_attended_a: toInt(form.batch_attended_a),
      batch_attended_b: toInt(form.batch_attended_b),
      batch_attended_c: toInt(form.batch_attended_c),
    };
    // Remove UI-only fields not in DB
    delete basePayload.num_batches_display;
    // Remove batch-specific sub-fields
    ['a','b','c','d','e'].forEach(b=>{ delete basePayload['batch_students_'+b]; delete basePayload['batch_attended_'+b]; });
    DATE_FIELDS.forEach(f => { if (basePayload[f] === '') basePayload[f] = null; });
    if (editing) {
      // Check if we need to split into batch rows
      const existingProg = programs.find(p=>p.id===editing);
      if (numBatches > 1 && !existingProg?.batch_label) {
        // Split: update current row as Batch A, create new row for Batch B+
        const batchLetters = ['A','B','C','D','E'].slice(0, numBatches);
        // Update existing as Batch A
        const payloadA = {...basePayload, batch_label:'A',
          total_students: Number(form['batch_students_a'])||0,
          students_attended: Number(form['batch_attended_a'])||0};
        const {error:eA} = await supabase.from('nactet_programs').update(payloadA).eq('id', editing);
        if(eA){toast.error(eA.message);setSaving(false);return;}
        // Create remaining batch rows (remove id to let DB generate new ones)
        // Build clean rows without id/created_at for new batch rows
        const newRows = batchLetters.slice(1).map(b=>{
          const row = {...basePayload};
          delete row.id; delete row.created_at;
          row.batch_label = b;
          row.total_students = Number(form['batch_students_'+b.toLowerCase()])||0;
          row.students_attended = Number(form['batch_attended_'+b.toLowerCase()])||0;
          return row;
        });
        console.log('Creating batch rows:', newRows);
        if(newRows.length){
          const {data:newData, error:eNew} = await supabase.from('nactet_programs').insert(newRows).select();
          console.log('Batch insert result:', newData, eNew);
          if(eNew){toast.error('Batch B error: '+eNew.message);setSaving(false);return;}
        }
      } else {
        // Normal single update
        const { error } = await supabase.from('nactet_programs').update(basePayload).eq('id', editing);
        if (error) { toast.error(error.message); setSaving(false); return; }
      }
    } else if (numBatches > 1) {
      // Create one row per batch
      const batchLabels = ['A','B','C','D','E'].slice(0, numBatches);
      const rows = batchLabels.map(b => ({
        ...basePayload,
        batch_label: b,
        total_students: form['batch_students_'+b.toLowerCase()]!==''?Number(form['batch_students_'+b.toLowerCase()])||null:null,
        students_attended: Number(form['batch_attended_'+b.toLowerCase()])||0,
      }));
      const { error } = await supabase.from('nactet_programs').insert(rows);
      if (error) { toast.error(error.message); setSaving(false); return; }
    } else {
      // Single batch program
      const { error } = await supabase.from('nactet_programs').insert({...basePayload, batch_label:''});
      if (error) { toast.error(error.message); setSaving(false); return; }
    }
    toast.success('Program saved'); setShowModal(false); setEditing(null); setForm(emptyProg); onRefresh();
    setSaving(false);
  }

  const zoneCollegeIds = (fZone||fUniv) ? colleges.filter(c=>(!fZone||c.zone===fZone)&&(!fUniv||c.university===fUniv)).map(c=>c.id) : null;
  const filtered = programs.filter(p =>
    (!fCollege || p.college_id === fCollege) &&
    (!fYear || p.year == fYear) &&
    (!fZone || (zoneCollegeIds&&zoneCollegeIds.includes(p.college_id))) &&
    (!fUniv || (zoneCollegeIds&&zoneCollegeIds.includes(p.college_id))) &&
    (!fStream || p.stream === fStream) &&
    (!fStatus || p.status === fStatus)
  
    && (!fMarksPending || marksPending(p))
  );

  function clearFilters(){setFCollege('');setFStream('');setFStatus('');setFZone('');setFUniv('');}

  return (
    <div>
      <div className="card" style={{marginBottom:12,display:'flex',gap:8,width:'fit-content',flexWrap:'wrap',alignItems:'center'}}>
        <SearchableSelect value={fCollege} onChange={v=>setFCollege(v||'')}
          options={colleges.filter(c=>programs.some(p=>p.college_id===c.id)).map(c=>({value:c.id,label:c.college_name}))}
          placeholder="All Colleges" searchPlaceholder="Search..." style={{minWidth:200}}/>
        <select value={fYear} onChange={e=>setFYear(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Years</option>
          {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={fZone} onChange={e=>{setFZone(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>          <option value="">All Zones</option>
          {[...new Set(colleges.map(c=>c.zone).filter(Boolean))].sort().map(z=><option key={z} value={z}>{z}</option>)}
        </select>
        <select value={fUniv} onChange={e=>{setFUniv(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Universities</option>
          {[...new Set(colleges.map(c=>c.university).filter(Boolean))].sort().map(u=><option key={u} value={u}>{u}</option>)}
        </select>
        <select value={fStream} onChange={e=>setFStream(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Streams</option>
          {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Status</option>
          {STATUS_FLOW.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:fMarksPending?'var(--red)':'var(--text2)',background:fMarksPending?'var(--red-light)':'none',border:'1px solid var(--border)'}} onClick={()=>setFMarksPending(f=>!f)}>
          ⚠️ Marks Pending
        </button>
        <button onClick={()=>clearFilters()} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>✕ Clear</button>
        {canEdit('nactet') && <button className="btn btn-primary btn-sm" onClick={()=>{setEditing(null);setForm(emptyProg);setShowModal(true);}}>+ Add Program</button>}
        {canEdit('nactet') && <div style={{display:'flex',alignItems:'center',gap:4,background:'var(--amber-light)',
          border:'1px solid var(--amber)',borderRadius:6,padding:'3px 8px'}}>
          <span style={{fontSize:11,color:'var(--amber)',fontWeight:600}}>⚡ New Year:</span>
          <select value={bulkYear} onChange={e=>setBulkYear(e.target.value)}
            style={{fontSize:11,padding:'2px 4px',border:'none',background:'transparent',color:'var(--text)',width:60}}>
            {[2027,2028,2029,2030].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-sm" onClick={bulkAddNew} disabled={bulkAdding}
            style={{background:'var(--amber)',color:'#fff',border:'none',padding:'3px 10px',fontSize:11}}>
            {bulkAdding?'Adding...':'Add All'}
          </button>
        </div>}
        {canEdit('nactet') && <button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkEdit(true)}>
          📋 Bulk Edit
        </button>}
        <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} programs</span>
      </div>

      <div className="table-wrap" style={{scrollbarWidth:'auto'}}>
        <style>{`div.table-wrap::-webkit-scrollbar{height:12px!important}div.table-wrap::-webkit-scrollbar-thumb{background:#00b09b!important;border-radius:6px!important}div.table-wrap::-webkit-scrollbar-track{background:#e0f5f1!important}`}</style>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:1100}}>
          <thead>
            <tr style={{background:'var(--accent-light)'}}>
              {['College','Year','Stream','Exam Date','Total','Attended','Status','Eval','Cert',''].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...filtered].sort((a,b)=>{
              const colA = colleges.find(c=>c.id===a.college_id);
              const colB = colleges.find(c=>c.id===b.college_id);
              // Colleges with data entered (not Pending) come first
              const hasDataA = a.status && a.status !== 'Pending' ? 0 : 1;
              const hasDataB = b.status && b.status !== 'Pending' ? 0 : 1;
              if (hasDataA !== hasDataB) return hasDataA - hasDataB;
              // Then alphabetical by college name
              return (colA?.college_name||'').localeCompare(colB?.college_name||'');
            }).map((p,i)=>{
              const col = colleges.find(c=>c.id===p.college_id);
              return (
                <tr key={p.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                  <td style={{padding:'8px 10px',fontWeight:500,maxWidth:180,wordBreak:'break-word'}}>{col?.college_name||'—'}</td>
                  <td style={{padding:'8px 10px'}}>{p.year}</td>
                  <td style={{padding:'8px 10px'}}><span className={`badge ${p.stream==='Mechanical'?'badge-blue':'badge-green'}`} style={{fontSize:9}}>{p.stream}{p.batch_label?' — Batch '+p.batch_label:''}</span></td>
                  <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{p.actual_exam_date?fmtDate(p.actual_exam_date):p.proposed_exam_date?'📅 '+fmtDate(p.proposed_exam_date):'—'}</td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>{p.total_students||p.registered_students||'—'}</td>
                  <td style={{padding:'8px 10px',textAlign:'center'}} title={entered[p.id]?.total?`${entered[p.id].total} students entered, ${entered[p.id].withMarks} with marks`:''}>{attendedOf(p)||'—'}</td>
                  <td style={{padding:'8px 10px'}}>
                    <span className={`badge ${STATUS_COLORS[p.status]||'badge-gray'}`} style={{fontSize:9}}>{p.status}</span>
                    {marksPending(p)&&<span title="Marks not entered" style={{marginLeft:4,fontSize:11}}>⚠️</span>}
                    {p.entered_by&&<span title={`Entered by: ${p.entered_by}`} style={{marginLeft:4,fontSize:10,color:'var(--text3)'}}>✏️{p.entered_by.split(' ')[0]}</span>}
                    {p.verified_by&&<span title={`Verified by: ${p.verified_by}`} style={{marginLeft:4,fontSize:10,color:'var(--green)'}}>✅{p.verified_by.split(' ')[0]}</span>}
                  </td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>{p.evaluation_done?'✅':'⏳'}</td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>{p.certificate_distributed?'✅':p.certificates_prepared?'📜':'⏳'}</td>
                  <td style={{padding:'8px 10px'}}>
                    {canEdit('nactet') && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(p.id);setForm({...p,year:p.year?.toString(),
          batch_students_a:p.batch_students_a||0,batch_students_b:p.batch_students_b||0,batch_students_c:p.batch_students_c||0,
          batch_attended_a:p.batch_attended_a||0,batch_attended_b:p.batch_attended_b||0,batch_attended_c:p.batch_attended_c||0,
        });setShowModal(true);}}>Edit</button>}
                    {canEdit('nactet') && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete this program? This will also delete all students and winners linked to it.'))return;
                      // Save exclusion so Add All won't re-add this program
                      await supabase.from('nactet_bulk_exclusions').upsert({college_id:p.college_id,year:p.year,stream:p.stream},{onConflict:'college_id,year,stream'});
                      const{error}=await supabase.from('nactet_programs').delete().eq('id',p.id);
                      if(error)toast.error(error.message);else{toast.success('Deleted');onRefresh();}}}>🗑</button>}
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}} title="Copy WhatsApp message"
                      onClick={()=>copyWhatsApp(p,col)}>📱</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length===0 && <div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No programs found</div>}
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowBulkEdit(false)}>
          <div className="modal" style={{maxWidth:'98vw',maxHeight:'95vh',overflowY:'auto',width:'98vw'}}>
            <div className="modal-header">
              <span className="modal-title">📋 Bulk Edit Programs</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkEdit(false)}>✕</button>
            </div>
            {/* Selector section */}
            <div style={{padding:'12px 20px',background:'var(--surface2)',borderBottom:'1px solid var(--border)',
              display:'flex',gap:12,alignItems:'flex-start',flexWrap:'wrap'}}>
              <div style={{flex:2,minWidth:250}}>
                <div style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
                  <label style={{fontSize:11,fontWeight:600}}>Select Colleges</label>
                  <span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>
                    ({bulkEditColleges.length} selected)
                  </span>
                  {bulkEditColleges.length>0 &&
                    <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'1px 6px'}}
                      onClick={()=>setBulkEditColleges([])}>Clear</button>}
                </div>
                <input value={bulkColSearch} onChange={e=>setBulkColSearch(e.target.value)}
                  placeholder="🔍 Search college..." style={{marginBottom:6,fontSize:12}}/>
                <div style={{maxHeight:180,overflowY:'auto',border:'1px solid var(--border)',
                  borderRadius:6,background:'#fff'}}>
                  {colleges
                    .filter(col=>!bulkColSearch||col.college_name.toLowerCase().includes(bulkColSearch.toLowerCase()))
                    .sort((a,b)=>a.college_name.localeCompare(b.college_name))
                    .map(col=>(
                      <label key={col.id} style={{
                        display:'grid',gridTemplateColumns:'20px 1fr auto',
                        alignItems:'center',gap:6,
                        padding:'5px 10px',cursor:'pointer',
                        borderBottom:'1px solid var(--border)',
                        background:bulkEditColleges.includes(col.id)?'var(--accent-light)':'#fff'}}>
                        <input type="checkbox"
                          checked={bulkEditColleges.includes(col.id)}
                          onChange={e=>setBulkEditColleges(prev=>
                            e.target.checked?[...prev,col.id]:prev.filter(id=>id!==col.id)
                          )}
                          style={{width:14,height:14,cursor:'pointer'}}/>
                        <span style={{fontSize:12,color:'var(--text)'}}>{col.college_name}</span>
                        <span style={{fontSize:10,color:'var(--text3)',whiteSpace:'nowrap'}}>{col.district}</span>
                      </label>
                    ))}
                </div>
                <div style={{marginTop:4,display:'flex',gap:6}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:10}}
                    onClick={()=>setBulkEditColleges(
                      colleges.filter(c=>!bulkColSearch||c.college_name.toLowerCase().includes(bulkColSearch.toLowerCase())).map(c=>c.id)
                    )}>Select All Visible</button>
                </div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8,minWidth:120}}>
                <div>
                  <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Year</label>
                  <select value={bulkEditYear} onChange={e=>setBulkEditYear(e.target.value)} style={{fontSize:12}}>
                    {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={openBulkEdit}
                  disabled={!bulkEditColleges.length}>
                  🔍 Load Programs
                </button>
                {bulkRows.length>0 &&
                  <span style={{fontSize:11,color:'var(--green)',fontWeight:600}}>
                    ✅ {bulkRows.length} programs loaded
                  </span>}
              </div>
            </div>
            <div style={{padding:12}}>
              {bulkRows.map(row=>{
                const col = colleges.find(c=>c.id===row.college_id);
                return (
                  <div key={row.id} className="card" style={{marginBottom:12,border:'1px solid var(--border)'}}>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'var(--accent)',
                      borderBottom:'1px solid var(--border)',paddingBottom:6}}>
                      🏫 {col?.college_name} — {row.stream}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8}}>
                      {/* Basic */}
                      <div className="form-group">
                        <label style={{fontSize:11}}>Status</label>
                        <select value={row.status||''} onChange={e=>updateBulkRow(row.id,'status',e.target.value)} style={{fontSize:11}}>
                          {STATUS_FLOW.map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Exam Mode</label>
                        <select value={row.exam_mode||''} onChange={e=>updateBulkRow(row.id,'exam_mode',e.target.value)} style={{fontSize:11}}>
                          {EXAM_MODES.map(m=><option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Total Students</label>
                        <input type="number" value={row.total_students||0} onChange={e=>updateBulkRow(row.id,'total_students',e.target.value)} style={{fontSize:11}} min={0}/>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Students Attended</label>
                        <input type="number" value={row.students_attended||0} onChange={e=>updateBulkRow(row.id,'students_attended',e.target.value)} style={{fontSize:11}} min={0}/>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Proposed Exam Date</label>
                        <DateInput value={row.proposed_exam_date||''} onChange={e=>updateBulkRow(row.id,'proposed_exam_date',e.target.value)} style={{fontSize:11}}/>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Actual Exam Date</label>
                        <DateInput value={row.actual_exam_date||''} onChange={e=>updateBulkRow(row.id,'actual_exam_date',e.target.value)} style={{fontSize:11}}/>
                      </div>
                      <div className="form-group">
                        <label style={{fontSize:11}}>Exam Visit By</label>
                        <select value={row.exam_visit_by||''} onChange={e=>updateBulkRow(row.id,'exam_visit_by',e.target.value)} style={{fontSize:11}}>
                          <option value="">— Select Team —</option>
                          {teams.map(t=><option key={t.id} value={t.team_name}>{t.team_name}{t.member_1&&' ('+t.member_1+(t.member_2?' & '+t.member_2:'')+')'}</option>)}
                        </select>
                      </div>
                      {/* Exam Photo */}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`ep-${row.id}`} checked={!!row.exam_photo_taken} onChange={e=>updateBulkRow(row.id,'exam_photo_taken',e.target.checked)}/>
                        <label htmlFor={`ep-${row.id}`} style={{fontSize:11,marginBottom:0}}>Exam Photo Taken</label>
                      </div>
                      {row.exam_photo_taken && (
                        <div className="form-group">
                          <label style={{fontSize:11}}>Exam Photo Date</label>
                          <DateInput value={row.exam_photo_date||''} onChange={e=>{updateBulkRow(row.id,'exam_photo_date',e.target.value);updateBulkRow(row.id,'actual_exam_date',e.target.value);}} style={{fontSize:11}}/>
                        </div>
                      )}
                      {/* QB */}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`qb-${row.id}`} checked={!!row.qb_required} onChange={e=>updateBulkRow(row.id,'qb_required',e.target.checked)}/>
                        <label htmlFor={`qb-${row.id}`} style={{fontSize:11,marginBottom:0}}>QB Required</label>
                      </div>
                      {row.qb_required && (<>
                        <div className="form-group">
                          <label style={{fontSize:11}}>QB Sent Date</label>
                          <DateInput value={row.qb_sent_date||''} onChange={e=>updateBulkRow(row.id,'qb_sent_date',e.target.value)} style={{fontSize:11}}/>
                        </div>
                        <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                          <input type="checkbox" id={`qbr-${row.id}`} checked={!!row.qb_received_confirmed} onChange={e=>updateBulkRow(row.id,'qb_received_confirmed',e.target.checked)}/>
                          <label htmlFor={`qbr-${row.id}`} style={{fontSize:11,marginBottom:0}}>QB Received</label>
                        </div>
                        <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                          <input type="checkbox" id={`qbret-${row.id}`} checked={!!row.qb_returned} onChange={e=>updateBulkRow(row.id,'qb_returned',e.target.checked)}/>
                          <label htmlFor={`qbret-${row.id}`} style={{fontSize:11,marginBottom:0}}>QB Returned</label>
                        </div>
                      </>)}
                      {/* Answer sheets */}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`ans-${row.id}`} checked={!!row.answer_sheets_received} onChange={e=>updateBulkRow(row.id,'answer_sheets_received',e.target.checked)}/>
                        <label htmlFor={`ans-${row.id}`} style={{fontSize:11,marginBottom:0}}>Answer Sheets Received</label>
                      </div>
                      {/* Evaluation */}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`ev-${row.id}`} checked={!!row.evaluation_done} onChange={e=>updateBulkRow(row.id,'evaluation_done',e.target.checked)}/>
                        <label htmlFor={`ev-${row.id}`} style={{fontSize:11,marginBottom:0}}>Evaluation Done</label>
                      </div>
                      {row.evaluation_done && (
                        <div className="form-group">
                          <label style={{fontSize:11}}>Evaluation Date</label>
                          <DateInput value={row.evaluation_date||''} onChange={e=>updateBulkRow(row.id,'evaluation_date',e.target.value)} style={{fontSize:11}}/>
                        </div>
                      )}
                      {/* Result */}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`rc-${row.id}`} checked={!!row.result_communicated} onChange={e=>updateBulkRow(row.id,'result_communicated',e.target.checked)}/>
                        <label htmlFor={`rc-${row.id}`} style={{fontSize:11,marginBottom:0}}>Result Communicated</label>
                      </div>
                      {row.result_communicated && (<>
                        <div className="form-group">
                          <label style={{fontSize:11}}>Result Date</label>
                          <DateInput value={row.result_communicated_date||''} onChange={e=>updateBulkRow(row.id,'result_communicated_date',e.target.value)} style={{fontSize:11}}/>
                        </div>
                        <div className="form-group">
                          <label style={{fontSize:11}}>Result Medium</label>
                          <select value={row.result_medium||''} onChange={e=>updateBulkRow(row.id,'result_medium',e.target.value)} style={{fontSize:11}}>
                            <option value="">—</option>{RESULT_MEDIUMS.map(m=><option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                      </>)}
                      {/* Certificates */}
                      <div className="form-group">
                        <label style={{fontSize:11}}>Certs to Prepare</label>
                        <input type="number" value={row.certificates_to_prepare||0} onChange={e=>updateBulkRow(row.id,'certificates_to_prepare',e.target.value)} style={{fontSize:11}} min={0}/>
                      </div>
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`cp-${row.id}`} checked={!!row.certificates_prepared} onChange={e=>updateBulkRow(row.id,'certificates_prepared',e.target.checked)}/>
                        <label htmlFor={`cp-${row.id}`} style={{fontSize:11,marginBottom:0}}>Certs Prepared</label>
                      </div>
                      {row.certificates_prepared && (
                        <div className="form-group">
                          <label style={{fontSize:11}}>Prepared Date</label>
                          <DateInput value={row.certificates_prepared_date||''} onChange={e=>updateBulkRow(row.id,'certificates_prepared_date',e.target.value)} style={{fontSize:11}}/>
                        </div>
                      )}
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="checkbox" id={`cd-${row.id}`} checked={!!row.certificate_distributed} onChange={e=>updateBulkRow(row.id,'certificate_distributed',e.target.checked)}/>
                        <label htmlFor={`cd-${row.id}`} style={{fontSize:11,marginBottom:0}}>Certs Distributed</label>
                      </div>
                      {row.certificate_distributed && (<>
                        <div className="form-group">
                          <label style={{fontSize:11}}>Distribution Date</label>
                          <DateInput value={row.certificate_distribution_date||''} onChange={e=>updateBulkRow(row.id,'certificate_distribution_date',e.target.value)} style={{fontSize:11}}/>
                        </div>
                        <div className="form-group" style={{display:'flex',alignItems:'center',gap:6}}>
                          <input type="checkbox" id={`cph-${row.id}`} checked={!!row.certificate_photo_taken} onChange={e=>updateBulkRow(row.id,'certificate_photo_taken',e.target.checked)}/>
                          <label htmlFor={`cph-${row.id}`} style={{fontSize:11,marginBottom:0}}>Dist Photo Taken</label>
                        </div>
                        <div className="form-group">
                          <label style={{fontSize:11}}>Distribution Visit By</label>
                          <select value={row.cert_visit_by||''} onChange={e=>updateBulkRow(row.id,'cert_visit_by',e.target.value)} style={{fontSize:11}}>
                            <option value="">— Select Team —</option>
                          {teams.map(t=><option key={t.id} value={t.team_name}>{t.team_name}{t.member_1&&' ('+t.member_1+(t.member_2?' & '+t.member_2:'')+')'}</option>)}
                          </select>
                        </div>
                      </>)}
                      {/* Notes */}
                      <div className="form-group" style={{gridColumn:'1/-1'}}>
                        <label style={{fontSize:11}}>Notes</label>
                        <textarea value={row.notes||''} onChange={e=>updateBulkRow(row.id,'notes',e.target.value)} rows={1} style={{fontSize:11}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{padding:'10px 16px',display:'flex',gap:8,justifyContent:'flex-end',borderTop:'1px solid var(--border)',position:'sticky',bottom:0,background:'var(--surface)'}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkEdit(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBulkEdit} disabled={bulkSaving}>
                {bulkSaving?'⏳ Saving...':'💾 Save All Programs'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Program Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:620,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Program</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>College *</label>
                  <SearchableSelect value={form.college_id} onChange={v=>sf('college_id',v||'')}
                    options={colleges.filter(c=>c.is_active&&c.college_status!=='Shutdown').map(c=>({value:c.id,label:c.college_name}))}
                    placeholder="Select college" searchPlaceholder="Search..."/>
                </div>
                <div className="form-group">
                  <label>Year *</label>
                  <select value={form.year} onChange={e=>sf('year',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stream *</label>
                  <select value={form.stream} onChange={e=>sf('stream',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e=>sf('status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {STATUS_FLOW.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Exam Mode</label>
                  <select value={form.exam_mode} onChange={e=>sf('exam_mode',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {EXAM_MODES.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                {/* Only show batch split UI for new programs */}
                {!form.batch_label&&(
                <div className="form-group">
                  <label>No of Batches</label>
                  <input type="number" value={form.num_batches||''} onChange={e=>sf('num_batches',e.target.value)} min={0} placeholder="e.g. 3"/>
                </div>
                )}
                {((!form.batch_label&&Number(form.num_batches)<=1)||form.batch_label)&&(
                  <div className="form-group">
                    <label>Total No of Students{form.batch_label?` (Batch ${form.batch_label})`:''}</label>
                    <input type="number" value={form.total_students??''} onChange={e=>sf('total_students',e.target.value)} min={0}/>
                  </div>
                )}
                {!form.batch_label&&Number(form.num_batches)>1&&(
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Students per Batch</label>
                    <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:4}}>
                      {['A','B','C','D','E'].slice(0,Number(form.num_batches)).map(b=>(
                        <div key={b} style={{display:'flex',alignItems:'center',gap:6}}>
                          <span style={{fontWeight:600,fontSize:13,minWidth:55}}>Batch {b}:</span>
                          <input type="number" value={form['batch_students_'+b.toLowerCase()]||''} onChange={e=>sf('batch_students_'+b.toLowerCase(),e.target.value)} style={{width:80}} min={0} placeholder="0"/>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Pre-exam */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>📅 Exam Scheduling</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Proposed Exam Date</label>
                  <DateInput value={form.proposed_exam_date||''} onChange={e=>sf('proposed_exam_date',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label>Actual Exam Date</label>
                  <DateInput value={form.actual_exam_date||''} onChange={e=>sf('actual_exam_date',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label>Registration Status</label>
                  <select value={form.reg_status||''} onChange={e=>sf('reg_status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    <option value="Completed">Completed</option>
                    <option value="Not Completed">Not Completed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Exam Visit By</label>
                  <select value={form.exam_visit_by||''} onChange={e=>sf('exam_visit_by',e.target.value)}>
                    <option value="">— Select Team —</option>
                          {teams.map(t=><option key={t.id} value={t.team_name}>{t.team_name}{t.member_1&&' ('+t.member_1+(t.member_2?' & '+t.member_2:'')+')'}</option>)}
                  </select>
                </div>
              </div>

              {/* QB Section */}
              {form.exam_mode==='Offline' && (
                <>
                  <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>📦 Question Booklet</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div className="form-group" style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1'}}>
                      <input type="checkbox" id="qbReq" checked={form.qb_required} onChange={e=>sf('qb_required',e.target.checked)}/>
                      <label htmlFor="qbReq" style={{marginBottom:0}}>QB Required</label>
                    </div>
                    {form.qb_required && (<>
                      <div className="form-group">
                        <label>QB Code</label>
                        <input value={form.qb_code||''} onChange={e=>sf('qb_code',e.target.value)} placeholder="e.g. A, B, C"/>
                      </div>
                      <div className="form-group">
                        <label>QB Sent Date</label>
                        <DateInput value={form.qb_sent_date||''} onChange={e=>sf('qb_sent_date',e.target.value)}/>
                      </div>
                      <div className="form-group">
                        <label>Delivery Method</label>
                        <select value={form.qb_delivery_method||''} onChange={e=>sf('qb_delivery_method',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                          <option value="">Select</option>
                          {DELIVERY_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Courier Tracking No</label>
                        <input value={form.qb_courier_tracking||''} onChange={e=>sf('qb_courier_tracking',e.target.value)}/>
                      </div>
                      {form.qb_delivery_method==='Direct Delivery'&&<>
                      <div className="form-group">
                        <label>Handed Over To</label>
                        <input value={form.qb_delivered_by||''} onChange={e=>sf('qb_delivered_by',e.target.value)} placeholder="Staff name"/>
                      </div>
                      <div className="form-group">
                        <label>Contact Number</label>
                        <input value={form.qb_delivered_contact||''} onChange={e=>sf('qb_delivered_contact',e.target.value)} placeholder="Phone"/>
                      </div>
                      <div className="form-group">
                        <label>Handed Over Date</label>
                        <DateInput value={form.qb_delivered_date||''} onChange={e=>sf('qb_delivered_date',e.target.value)}/>
                      </div>
                      </>}

                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                        <input type="checkbox" id="qbRec" checked={form.qb_received_confirmed} onChange={e=>sf('qb_received_confirmed',e.target.checked)}/>
                        <label htmlFor="qbRec" style={{marginBottom:0}}>Received by College</label>
                      </div>
                      <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                        <input type="checkbox" id="qbRet" checked={form.qb_returned} onChange={e=>sf('qb_returned',e.target.checked)}/>
                        <label htmlFor="qbRet" style={{marginBottom:0}}>QB Returned</label>
                      </div>
                    </>)}
                  </div>
                </>
              )}

              {/* Exam */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>📝 Exam</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {(Number(form.num_batches)<=1||form.batch_label)&&(
                  <div className="form-group">
                    <label>Students Attended{form.batch_label?` (Batch ${form.batch_label})`:''}</label>
                    <input type="number" value={form.students_attended??''} onChange={e=>sf('students_attended',e.target.value)} min={0}/>
                  </div>
                )}
                {!form.batch_label&&Number(form.num_batches)>1&&(
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Students Attended per Batch</label>
                    <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:4}}>
                      {['A','B','C','D','E'].slice(0,Number(form.num_batches)).map(b=>(
                        <div key={b} style={{display:'flex',alignItems:'center',gap:6}}>
                          <span style={{fontWeight:600,fontSize:13,minWidth:55}}>Batch {b}:</span>
                          <input type="number" value={form['batch_attended_'+b.toLowerCase()]||''} onChange={e=>sf('batch_attended_'+b.toLowerCase(),e.target.value)} style={{width:80}} min={0} placeholder="0"/>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="examPhoto" checked={form.exam_photo_taken} onChange={e=>sf('exam_photo_taken',e.target.checked)}/>
                  <label htmlFor="examPhoto" style={{marginBottom:0}}>Exam Photo Taken</label>
                </div>
                {form.exam_photo_taken && (
                  <div className="form-group">
                    <label>Exam Photo Date (also sets Actual Exam Date)</label>
                    <DateInput value={form.exam_photo_date||form.actual_exam_date||''}
                      onChange={e=>{sf('exam_photo_date',e.target.value);sf('actual_exam_date',e.target.value);}}/>
                  </div>
                )}
              </div>

              {/* Evaluation */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>📊 Evaluation</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Evaluation Location</label>
                  <select value={form.evaluation_location||''} onChange={e=>sf('evaluation_location',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {EVAL_LOCATIONS.map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="ansRec" checked={form.answer_sheets_received} onChange={e=>sf('answer_sheets_received',e.target.checked)}/>
                  <label htmlFor="ansRec" style={{marginBottom:0}}>Answer Sheets Received</label>
                </div>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="evalDone" checked={form.evaluation_done} onChange={e=>sf('evaluation_done',e.target.checked)}/>
                  <label htmlFor="evalDone" style={{marginBottom:0}}>Evaluation Done</label>
                </div>
                {form.evaluation_done && (
                  <div className="form-group">
                    <label>Evaluation Date</label>
                    <DateInput value={form.evaluation_date||''} onChange={e=>sf('evaluation_date',e.target.value)}/>
                  </div>
                )}
              </div>

              {/* Results */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>📢 Results</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="resCom" checked={form.result_communicated} onChange={e=>sf('result_communicated',e.target.checked)}/>
                  <label htmlFor="resCom" style={{marginBottom:0}}>Result Communicated</label>
                </div>
                {form.result_communicated && (<>
                  <div className="form-group">
                    <label>Result Date</label>
                    <DateInput value={form.result_communicated_date||''} onChange={e=>sf('result_communicated_date',e.target.value)}/>
                  </div>
                  <div className="form-group">
                    <label>Medium</label>
                    <select value={form.result_medium||''} onChange={e=>sf('result_medium',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                      <option value="">Select</option>
                      {RESULT_MEDIUMS.map(m=><option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </>)}
              </div>

              {/* Certificates */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>🎓 Certificates</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Certificates to Prepare</label>
                  <input type="number" value={form.certificates_to_prepare??''} onChange={e=>sf('certificates_to_prepare',e.target.value)} min={0}/>
                </div>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="brochure" checked={form.brochure_distributed||false} onChange={e=>sf('brochure_distributed',e.target.checked)}/>
                  <label htmlFor="brochure" style={{marginBottom:0}}>Brochure Distributed</label>
                </div>

                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="certPrep" checked={form.certificates_prepared} onChange={e=>sf('certificates_prepared',e.target.checked)}/>
                  <label htmlFor="certPrep" style={{marginBottom:0}}>Certificates Prepared</label>
                </div>
                {form.certificates_prepared && (
                  <div className="form-group">
                    <label>Prepared Date</label>
                    <DateInput value={form.certificates_prepared_date||''} onChange={e=>sf('certificates_prepared_date',e.target.value)}/>
                  </div>
                )}
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="checkbox" id="certDist" checked={form.certificate_distributed} onChange={e=>sf('certificate_distributed',e.target.checked)}/>
                  <label htmlFor="certDist" style={{marginBottom:0}}>Certificates Distributed</label>
                </div>
                {form.certificate_distributed && (<>
                  <div className="form-group">
                    <label>Distribution Date</label>
                    <DateInput value={form.certificate_distribution_date||''} onChange={e=>sf('certificate_distribution_date',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{display:'flex',alignItems:'center',gap:8}}>
                    <input type="checkbox" id="certPhoto" checked={form.certificate_photo_taken} onChange={e=>sf('certificate_photo_taken',e.target.checked)}/>
                    <label htmlFor="certPhoto" style={{marginBottom:0}}>Distribution Photo Taken</label>
                  </div>
                  <div className="form-group">
                    <label>Distribution Visit By</label>
                    <select value={form.cert_visit_by||''} onChange={e=>sf('cert_visit_by',e.target.value)}>
                    <option value="">— Select Team —</option>
                          {teams.map(t=><option key={t.id} value={t.team_name}>{t.team_name}{t.member_1&&' ('+t.member_1+(t.member_2?' & '+t.member_2:'')+')'}</option>)}
                  </select>
                  </div>
                </>)}
              </div>

              <div className="form-group" style={{marginTop:10}}>
              {/* Class Representatives */}
              <div style={{marginTop:10,marginBottom:6,fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>👥 Class Representatives</div>
              <div style={{marginBottom:8}}>
                <div style={{display:'flex',gap:8,marginBottom:6,alignItems:'center'}}>
                  <span style={{fontSize:12,color:'var(--text3)',flex:1}}>Add one rep per batch. Leave empty if not applicable.</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={()=>sf('class_reps',[...(form.class_reps||[]),{batch:'A',stream:'',name:'',phone:''}])}>+ Add Rep</button>
                </div>
                {(form.class_reps||[]).map((rep,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'80px 130px 1fr 1fr auto',gap:8,marginBottom:6,background:'var(--surface2)',padding:'8px',borderRadius:6}}>
                    <div><label style={{fontSize:11}}>Batch</label>
                      <select value={rep.batch||'A'} onChange={e=>{const r=[...(form.class_reps||[])];r[i]={...r[i],batch:e.target.value};sf('class_reps',r);}} style={{width:'100%',fontSize:12}}>
                        {['A','B','C','D','All'].map(b=><option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div><label style={{fontSize:11}}>Stream</label>
                      <select value={rep.stream||''} onChange={e=>{const r=[...(form.class_reps||[])];r[i]={...r[i],stream:e.target.value};sf('class_reps',r);}} style={{width:'100%',fontSize:12}}>
                        <option value="">Select</option>
                        {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><label style={{fontSize:11}}>Name</label>
                      <input value={rep.name||''} onChange={e=>{const r=[...(form.class_reps||[])];r[i]={...r[i],name:e.target.value};sf('class_reps',r);}} placeholder="Rep name" style={{width:'100%',fontSize:12}}/>
                    </div>
                    <div><label style={{fontSize:11}}>Phone</label>
                      <input value={rep.phone||''} onChange={e=>{const r=[...(form.class_reps||[])];r[i]={...r[i],phone:e.target.value};sf('class_reps',r);}} placeholder="Phone number" style={{width:'100%',fontSize:12}}/>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--red)',alignSelf:'flex-end'}} onClick={()=>{const r=[...(form.class_reps||[])];r.splice(i,1);sf('class_reps',r);}}>✕</button>
                  </div>
                ))}
              </div>
                <label>Notes</label>
                <textarea value={form.notes||''} onChange={e=>sf('notes',e.target.value)} rows={2}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!form.college_id||saving} onClick={saveProg}>
                {saving?'Saving...':'Save Program'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Export helper ──────────────────────────────────────────────
function exportCSV(rows, filename) {   // now writes a styled Excel file (name kept so existing calls work)
  if (!rows.length) return;
  const base = String(filename).replace(/\.csv$/i, '');
  saveStyledXlsx(base + '.xlsx', [{ name: base.slice(0, 31), title: base.replace(/_/g, ' '), rows }]);
}

// ════════════════════════════════════════════════════════════════
// STUDENTS TAB
// ════════════════════════════════════════════════════════════════
// ── Push NACTET students → Marketing CRM (year-aware) ───────────────────
// Data year in the CRM = the program's exam year. College+stream rows are created in
// CRM Admin Panel (for that year) if missing; students are assigned to that row's staff.
async function pAll(jobs, limit = 12) {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, jobs.length) }, async () => {
    while (i < jobs.length) { const k = i++; await jobs[k](); }
  });
  await Promise.all(workers);
}

async function pushProgramsToCRM(progs, nactetColleges, onProgress, opts = {}) {
  const normStream = v => { const t = String(v||'').toLowerCase(); return t.includes('mech') ? 'Mechanical' : t.includes('civil') ? 'Civil' : t.includes('auto') ? 'Automobile' : (v||''); };
  // 1) CRM college names = master college name when linked
  const nIds = [...new Set(progs.map(p=>p.college_id).filter(Boolean))];
  const masterIds = [...new Set(nIds.map(id=>nactetColleges.find(c=>c.id===id)?.college_id).filter(Boolean))];
  const masterName = {};
  for (let i=0;i<masterIds.length;i+=100) {
    const {data} = await supabase.from('colleges').select('id,college_name').in('id', masterIds.slice(i,i+100));
    (data||[]).forEach(c=>{ masterName[c.id]=c.college_name; });
  }
  const nameOf = p => { const c = nactetColleges.find(x=>x.id===p.college_id); return (c && masterName[c.college_id]) || c?.college_name || ''; };

  // 2) students of all selected programs (paged — more than 1000 safe)
  const progIds = progs.map(p=>p.id);
  const allStu = [];
  for (let i=0;i<progIds.length;i+=50) {
    const ids = progIds.slice(i,i+50);
    for (let from=0; ; from+=1000) {
      const {data,error} = await supabase.from('nactet_students').select('*').in('program_id', ids).order('id').range(from, from+999);
      if (error) throw error;
      allStu.push(...(data||[]));
      if (!data || data.length<1000) break;
    }
  }

  // 3) make sure the CRM college rows exist for each year
  const years = [...new Set(progs.map(p=>Number(p.year)).filter(Boolean))];
  const crmCol = {}; // "year||college||stream" -> assigned_to
  for (const y of years) {
    const {data} = await supabase.from('crm_colleges').select('college_name,stream,assigned_to').eq('academic_year', y);
    (data||[]).forEach(c=>{ crmCol[y+'||'+c.college_name+'||'+c.stream] = c.assigned_to||null; });
  }
  const newCols = {};
  progs.forEach(p=>{
    const y = Number(p.year), name = nameOf(p), st = normStream(p.stream);
    const k = y+'||'+name+'||'+st;
    if (name && st && !(k in crmCol) && !newCols[k]) {
      const nc = nactetColleges.find(x=>x.id===p.college_id);
      newCols[k] = { college_name:name, stream:st, district:nc?.district||null, academic_year:y, assigned_to:null };
    }
  });
  const colsToAdd = opts.updateOnly ? [] : Object.values(newCols);
  if (colsToAdd.length) {
    const {error} = await supabase.from('crm_colleges').insert(colsToAdd);
    if (error) throw new Error('Could not create CRM colleges: '+error.message);
    colsToAdd.forEach(c=>{ crmCol[c.academic_year+'||'+c.college_name+'||'+c.stream] = null; });
  }

  // 4) per program: skip duplicates (same phone + college + year), insert the rest
  const summary = [];
  let done = 0;
  for (const p of progs) {
    const y = Number(p.year), name = nameOf(p);
    const stus = allStu.filter(x=>x.program_id===p.id);
    const row = { program: p, college: name, stream: p.stream, year: y, total: stus.length, pushed: 0, updated: 0, skipped: 0, noContact: 0, error: '' };
    try {
      if (!name) throw new Error('College name not found');
      const cand = [];
      const seen = new Set();
      stus.forEach(x=>{
        const phone = String(x.phone||'').trim();
        const email = String(x.email||'').trim().toLowerCase();
        if (!phone && !email) { row.noContact++; return; }
        const key = phone || ('e:'+email);
        if (seen.has(key)) { row.skipped++; return; }
        seen.add(key);
        cand.push({x, phone, key});
      });
      const exist = new Map(); // phone (or "e:email") -> existing CRM row
      const sel = 'id,phone,email,remarks,is_winner,nactet_remarks';
      const fixMsg = ' (run in Supabase: alter table crm_students add column if not exists is_winner boolean not null default false; alter table crm_students add column if not exists nactet_remarks text;)';
      const phones = cand.map(c=>c.phone).filter(Boolean);
      const lookups = [];
      for (let i=0;i<phones.length;i+=100) {
        lookups.push(async ()=>{
          const {data,error} = await supabase.from('crm_students').select(sel).in('phone', phones.slice(i,i+100)).eq('college_name', name).eq('academic_year', y);
          if (error) throw new Error(error.message + fixMsg);
          (data||[]).forEach(r=>exist.set(r.phone, r));
        });
      }
      const emails = cand.filter(c=>!c.phone).map(c=>String(c.x.email).trim());
      for (let i=0;i<emails.length;i+=100) {
        lookups.push(async ()=>{
          const {data,error} = await supabase.from('crm_students').select(sel).in('email', emails.slice(i,i+100)).eq('college_name', name).eq('academic_year', y);
          if (error) throw new Error(error.message + fixMsg);
          (data||[]).filter(r=>!r.phone).forEach(r=>exist.set('e:'+String(r.email||'').trim().toLowerCase(), r));
        });
      }
      await pAll(lookups, 6);
      const isAutoText = t => !t || /^🏆 NACTET Winner/.test(t) || t === 'NACTET Scholarship student';
      const source = 'NACTET - '+name+' - '+p.stream+' - '+p.year;
      const skipInsert = opts.updateOnly || (opts.onlyIfPushed && exist.size === 0);
      const rows = [];
      const toUpdate = [];
      cand.forEach(({x,phone,key})=>{
        const nr = (x.remarks||'').trim();
        if (exist.has(key)) {
          const ex = exist.get(key);
          const upd = {};
          if (nr !== (ex.nactet_remarks||'')) upd.nactet_remarks = nr || null;
          // clean up older pushes that had copied NACTET text into the CRM's own remarks
          if (ex.remarks && (isAutoText(ex.remarks) || ex.remarks === nr)) upd.remarks = null;
          if (!!x.is_winner !== !!ex.is_winner) upd.is_winner = !!x.is_winner;
          if (Object.keys(upd).length) toUpdate.push({ id: ex.id, upd }); else row.skipped++;
          return;
        }
        if (skipInsert) return;
        const st = normStream(x.stream || p.stream);
        rows.push({
          full_name: x.student_name, phone: phone||null, whatsapp: phone||null, email: x.email||null,
          stream: st, college_name: name, academic_year: y,
          scholarship_marks: x.marks!=null && x.marks!=='' ? Number(x.marks) : null,
          source, response: 'Not Called',
          assigned_to: crmCol[y+'||'+name+'||'+st] || crmCol[y+'||'+name+'||'+normStream(p.stream)] || null,
          nactet_remarks: nr || null,
          is_winner: !!x.is_winner,
        });
      });
      // students needing the same change are updated in one request; the rest in parallel
      const groups = {};
      toUpdate.forEach(u=>{ const k = JSON.stringify(u.upd); (groups[k] = groups[k] || { upd: u.upd, ids: [] }).ids.push(u.id); });
      const updJobs = [];
      Object.values(groups).forEach(g=>{
        for (let i=0;i<g.ids.length;i+=100) {
          const ids = g.ids.slice(i,i+100);
          updJobs.push(async ()=>{
            const {error} = await supabase.from('crm_students').update(g.upd).in('id', ids);
            if (error) throw new Error(error.message);
            row.updated += ids.length;
          });
        }
      });
      await pAll(updJobs, 12);
      for (let i=0;i<rows.length;i+=100) {
        const {error} = await supabase.from('crm_students').insert(rows.slice(i,i+100));
        if (error) throw new Error(error.message);
        row.pushed += Math.min(100, rows.length-i);
      }
    } catch (e) { row.error = e.message || String(e); }
    summary.push(row);
    done++;
    if (onProgress) onProgress(done, progs.length);
  }
  return { summary, collegesCreated: colsToAdd.length };
}

function StudentsTab({
  colleges, programs, isEditor, userDefaultZone='', onRefresh}) {
  const { canEdit } = useAuth();
  const [selProgram, setSelProgram] = useState('');
  const [selBatch, setSelBatch] = useState('A');
  const [phoneFilter, setPhoneFilter] = useState(false);
  const [remarksFilter, setRemarksFilter] = useState(false);
  const [fZone, setFZone] = useState(userDefaultZone);
  const [fUniv, setFUniv] = useState('');
  const [students, setStudents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBulkEntry, setShowBulkEntry] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSavingState, setBulkSavingState] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showReexam, setShowReexam] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showNoWinner, setShowNoWinner] = useState(false);
  const [noWinnerReason, setNoWinnerReason] = useState('');
  const [selStudent, setSelStudent] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fYear, setFYear] = useState('2027');
  const [fCollege, setFCollege] = useState('');
  const [reexamForm, setReexamForm] = useState({reexam_date:'',reexam_marks:''});
  const [contactForm, setContactForm] = useState({
    contact_date: new Date().toISOString().split('T')[0],
    contact_status:'Contacted', reason:'', contacted_by:'',
    certificate_given:false, provisional_cert_given:false,
    main_cert_given:false, cert_date:'', notes:''
  });

  const [entryStaff, setEntryStaff] = useState([]);
  const [fEnteredBy, setFEnteredBy] = useState('');
  const [fVerifiedBy, setFVerifiedBy] = useState('');
  const [showStaffReport, setShowStaffReport] = useState(false);
  const [pushMany, setPushMany] = useState(null); // {counts, sel:Set, running, prog, result}
  React.useEffect(()=>{
    supabase.from('hr_staff').select('id,staff_name,designation')
      .in('designation',['Academic Counselor','Office Admin'])
      .eq('is_active',true).order('staff_name')
      .then(({data})=>setEntryStaff(data||[]));
  },[]);
  const emptyStd = { student_name:'', phone:'', email:'', stream:'', source:'Answer Sheet', marks:'', rank:'', is_winner:false, interest_status:'Pending', remarks:'', batch_label:'' };
  const [form, setForm] = useState(emptyStd);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const zoneColIds = fZone ? colleges.filter(c=>c.zone===fZone).map(c=>c.id) : null;
  const univColIds = fUniv ? colleges.filter(c=>c.university===fUniv).map(c=>c.id) : null;
  const filteredProgs = programs.filter(p =>
    (!fYear || p.year==fYear) &&
    (!fZone || (zoneColIds&&zoneColIds.includes(p.college_id))) &&
    (!fUniv || (univColIds&&univColIds.includes(p.college_id))) &&
    (!fCollege || p.college_id===fCollege) &&
    (!fEnteredBy || (fEnteredBy==='__none__' ? !p.entered_by : p.entered_by===fEnteredBy)) &&
    (!fVerifiedBy || (fVerifiedBy==='__none__' ? !p.verified_by : p.verified_by===fVerifiedBy))
  );

  async function loadStudents(progId) {
    setSelectedIds(new Set());
    setSelProgram(progId);
    setLoading(true);
    const { data } = await supabase.from('nactet_students').select('*').eq('program_id', progId).order('rank');
    setStudents(data||[]);
    // Auto-detect batch label from program name
    const prog = programs.find(p=>p.id===progId);
    if (prog?.batch_label) setSelBatch(prog.batch_label);
    else if ((data||[]).length>0 && data[0].batch_label) setSelBatch(data[0].batch_label);
    else setSelBatch('A');
    const { data: cdata } = await supabase.from('nactet_rank_contacts')
      .select('*').eq('program_id', progId).order('created_at', {ascending:false});
    setContacts(cdata||[]);
    setLoading(false);
  }

  // another staff member changed a student mark / rank / contact in the open program
  useLiveRefresh(['nactet_students', 'nactet_rank_contacts'], async () => {
    if (!selProgram) return;
    const { data } = await supabase.from('nactet_students').select('*').eq('program_id', selProgram).order('rank');
    setStudents(data||[]);
    const { data: cdata } = await supabase.from('nactet_rank_contacts').select('*').eq('program_id', selProgram).order('created_at', {ascending:false});
    setContacts(cdata||[]);
  }, !!selProgram, 1500);

  async function saveStudent() {
    setSaving(true);
    const prog = programs.find(p=>p.id===selProgram);
    const payload = { ...form,
      program_id: selProgram || form.program_id || null,
      college_id: prog?.college_id || form.college_id || null,
      marks: form.marks ? Number(form.marks) : null,
      rank: form.rank ? Number(form.rank) : null,
      remarks: !form.marks ? (form.remarks || 'Absent') : form.remarks,
    };
    // Convert empty strings to null for UUID fields
    if (!payload.college_id) payload.college_id = null;
    if (!payload.program_id) payload.program_id = null;
    let savedId = editing;
    if (editing) {
      const { error } = await supabase.from('nactet_students').update(payload).eq('id', editing);
      if (error) { toast.error(error.message); setSaving(false); return; }
    } else {
      const { data, error } = await supabase.from('nactet_students').insert(payload).select('id').single();
      if (error) { toast.error(error.message); setSaving(false); return; }
      savedId = data?.id;
    }
    // Auto-create nactet_winners record when marked as winner
    if (payload.is_winner && savedId) {
      const { data: existing } = await supabase.from('nactet_winners')
        .select('id').eq('student_id', savedId).limit(1);
      if (!existing?.length) {
        await supabase.from('nactet_winners').insert({
          student_id: savedId,
          college_id: prog?.college_id,
          program_id: selProgram,
          year: Number(prog?.year),
          stream: payload.stream || prog?.stream,
          batch_label: payload.batch_label || '',
          join_status: 'Pending'
        });
      }
    }
    toast.success('Saved');
    // keep Marketing CRM remarks/winner in sync for students already pushed
    if (prog) pushProgramsToCRM([prog], colleges, null, { onlyIfPushed: true }).catch(()=>{});
    setShowModal(false);
    setEditing(null);
    setForm(emptyStd);
    loadStudents(selProgram);
    setSaving(false);
  }

  function initBulkRows(n=5) {
    return Array.from({length:n}, ()=>({student_name:'',phone:'',email:'',stream:selProg?.stream||'',marks:'',batch_label:selProg?.batch_label||''}));
  }

  async function saveBulkStudents() {
    const validRows = bulkRows.filter(r=>r.student_name.trim());
    if(!validRows.length){toast.error('Enter at least one student name');return;}
    setBulkSavingState(true);
    const payload = validRows.map(r=>({
      program_id: selProgram,
      college_id: selProg?.college_id||null,
      student_name: r.student_name.trim(),
      phone: r.phone.trim(),
      email: r.email, remarks: !r.marks ? (r.remarks || 'Absent') : (r.remarks||null),
      stream: r.stream||selProg?.stream||'',
      marks: r.marks!==''?Number(r.marks):null,
      batch_label: r.batch_label||'',
      source: 'Answer Sheet',
      interest_status: 'Pending'
    }));
    const {error} = await supabase.from('nactet_students').insert(payload);
    if(error){toast.error(error.message);}
    else{toast.success(`${validRows.length} students added`);{const pg=programs.find(p=>p.id===selProgram); if(pg) pushProgramsToCRM([pg], colleges, null, { onlyIfPushed: true }).catch(()=>{});}setShowBulkEntry(false);loadStudents(selProgram);}
    setBulkSavingState(false);
  }

  async function deleteSelected() {
    if(!selectedIds.size) return;
    if(!window.confirm(`Delete ${selectedIds.size} selected student(s)?`)) return;
    const ids = [...selectedIds];
    // Clear winners first to avoid FK constraint
    await Promise.all(ids.map(id=>supabase.from('nactet_winners').delete().eq('student_id',id)));
    await Promise.all(ids.map(id=>supabase.from('nactet_students').delete().eq('id',id)));
    toast.success(`${ids.length} student(s) deleted`);
    setSelectedIds(new Set());
    loadStudents(selProgram);
  }

  async function autoRank() {
    if (!selProgram) return;
    setSaving(true);
    const prog = programs.find(p=>p.id===selProgram);
    const batchStudents = prog?.num_batches>1 ? students.filter(s=>s.batch_label===selBatch) : students;
    const withMarks = [...batchStudents].filter(s=>s.marks!=null);

    // Group by college_id
    const byCollege = {};
    withMarks.forEach(s => {
      const cid = s.college_id || 'unknown';
      if (!byCollege[cid]) byCollege[cid] = [];
      byCollege[cid].push(s);
    });

    // Build updates array for bulk processing
    const updates = [];
    const winners = [];
    const tiedIds = new Set();
    let anyTie = false;

    for (const [collegeId, colStudents] of Object.entries(byCollege)) {
      const sorted = [...colStudents].sort((a,b)=>Number(b.marks)-Number(a.marks));
      const topMark = Number(sorted[0]?.marks);
      const topCount = sorted.filter(s=>Number(s.marks)===topMark).length;
      const hasTie = topCount > 1;
      if (hasTie) { anyTie = true; sorted.filter(s=>Number(s.marks)===topMark).forEach(s=>tiedIds.add(s.id)); }
      let rank = 1;
      for (let i=0; i<sorted.length; i++) {
        const isTied = i>0 && Number(sorted[i].marks)===Number(sorted[i-1].marks);
        const isTopTied = hasTie && Number(sorted[i].marks)===topMark;
        const isWinner = i===0 && !hasTie;
        const assignedRank = isTied ? updates.find(u=>u.id===sorted[i-1].id)?.rank || rank : rank;
        updates.push({ id: sorted[i].id, rank: assignedRank, is_winner: isWinner, reexam_required: isTopTied });
        if (isWinner) winners.push(sorted[i]);
        if (!isTied) rank++;
      }
    }

    // Bulk update all students in batches of 20
    const BATCH = 20;
    for (let i=0; i<updates.length; i+=BATCH) {
      const batch = updates.slice(i, i+BATCH);
      await Promise.all(batch.map(u =>
        supabase.from('nactet_students').update({rank:u.rank,is_winner:u.is_winner,reexam_required:u.reexam_required}).eq('id',u.id)
      ));
    }

    // Clean up stale winner records for this program first
    await supabase.from('nactet_winners').delete().eq('program_id', selProgram);
    // Mark all students as not winner first
    await supabase.from('nactet_students').update({is_winner:false}).eq('program_id', selProgram);
    // Re-apply winner flags
    for (const u of updates) {
      await supabase.from('nactet_students').update({rank:u.rank,is_winner:u.is_winner,reexam_required:u.reexam_required}).eq('id',u.id);
    }
    // Create winner records
    for (const s of winners) {
      const { data: existing } = await supabase.from('nactet_winners').select('id').eq('student_id',s.id).limit(1);
      if (!existing?.length) {
        await supabase.from('nactet_winners').insert({
          student_id:s.id, college_id:s.college_id, program_id:selProgram,
          year:Number(prog?.year), stream:s.stream||prog?.stream, join_status:'Pending'
        });
      }
    }

    setSaving(false);
    toast.success('Rankings updated per college' + (anyTie?' — ⚠️ Some ties! Re-exam required.':''));
    loadStudents(selProgram);
  }

  async function saveReexam() {
    if (!selStudent) return;
    setSaving(true);
    await supabase.from('nactet_students').update({
      reexam_date: reexamForm.reexam_date||null,
      reexam_marks: reexamForm.reexam_marks ? Number(reexamForm.reexam_marks) : null,
      reexam_required: false
    }).eq('id', selStudent.id);
    const tiedIds = students.filter(s=>Number(s.marks)===Number(selStudent.marks)).map(s=>s.id);
    const {data: reexamData} = await supabase.from('nactet_students').select('*').in('id', tiedIds);
    const reexamSorted = (reexamData||[]).filter(s=>s.reexam_marks!=null).sort((a,b)=>Number(b.reexam_marks)-Number(a.reexam_marks));
    for (let i=0; i<reexamSorted.length; i++) {
      await supabase.from('nactet_students').update({
        final_rank: i+1, is_final_winner: i===0, is_winner: i===0
      }).eq('id', reexamSorted[i].id);
    }
    toast.success('Re-exam saved & winner updated');
    setShowReexam(false);
    loadStudents(selProgram);
    setSaving(false);
  }

  async function deleteContact(id) {
    if (!window.confirm('Delete this contact record?')) return;
    await supabase.from('nactet_rank_contacts').delete().eq('id', id);
    const { data } = await supabase.from('nactet_rank_contacts')
      .select('*').eq('student_id', selStudent.id).order('created_at',{ascending:false});
    setContacts(data||[]);
  }

  async function saveContact() {
    if (!selStudent) return;
    setSaving(true);
    const prog = programs.find(p=>p.id===selProgram);
    const contactPayload = {
      ...contactForm,
      program_id: selProgram || null,
      student_id: selStudent.id || null,
      college_id: prog?.college_id || null,
      rank: selStudent.final_rank || selStudent.rank || null,
      is_reexam_rank: !!selStudent.reexam_marks
    };
    if (!contactPayload.contact_date) contactPayload.contact_date = new Date().toISOString().split('T')[0];
    Object.keys(contactPayload).forEach(k => { if (contactPayload[k] === '') contactPayload[k] = null; });
    const { error } = await supabase.from('nactet_rank_contacts').insert(contactPayload);
    if (error) { toast.error(error.message); setSaving(false); return; }
    if (contactForm.contact_status === 'Backed Out' && selStudent.is_winner) {
      await supabase.from('nactet_students').update({is_winner:false}).eq('id', selStudent.id);
      const nextRank = students.find(s=>(s.final_rank||s.rank)===((selStudent.final_rank||selStudent.rank)+1));
      if (nextRank) {
        await supabase.from('nactet_students').update({is_winner:true}).eq('id', nextRank.id);
        toast.info(`🔄 ${nextRank.student_name} (Rank ${(selStudent.final_rank||selStudent.rank)+1}) is now active winner`);
      }
    }
    toast.success('Contact recorded');
    setContactForm({contact_date:new Date().toISOString().split('T')[0],contact_status:'Contacted',reason:'',contacted_by:'',certificate_given:false,provisional_cert_given:false,main_cert_given:false,notes:''});
    const { data: updatedContacts } = await supabase.from('nactet_rank_contacts')
      .select('*').eq('student_id', selStudent.id).order('created_at',{ascending:false});
    setContacts(updatedContacts||[]);
    setSaving(false);
  }

  async function deleteStudent(id) {
    if (!window.confirm('Delete this student?')) return;
    await supabase.from('nactet_winners').delete().eq('student_id', id);
    await supabase.from('nactet_rank_contacts').delete().eq('student_id', id);
    const {error} = await supabase.from('nactet_students').delete().eq('id', id);
    if(error) toast.error(error.message);
    else { toast.success('Student deleted'); loadStudents(selProgram); }
  }

  async function pushToCRM() {
    if (!selProgram || students.length===0) return;
    const prog = programs.find(p=>p.id===selProgram);
    if (!prog) return;
    if (!window.confirm('Push '+students.length+' students of this program to Marketing CRM (year '+prog.year+')?')) return;
    try {
      const {summary, collegesCreated} = await pushProgramsToCRM([prog], colleges);
      const r = summary[0];
      if (r.error) toast.error('Push failed: '+r.error);
      else toast.success(r.pushed+' pushed, '+r.updated+' updated, '+(r.skipped+r.noContact)+' unchanged/skipped'+(collegesCreated?' · college added to CRM Admin Panel (assign a staff)':''));
    } catch (e) { toast.error('Push failed: '+e.message); }
  }

  async function openPushMany() {
    const ids = filteredProgs.map(p=>p.id);
    setPushMany({ counts:null, sel:new Set(), running:false, prog:'', result:null });
    const counts = {};
    for (let i=0;i<ids.length;i+=50) {
      for (let from=0; ; from+=1000) {
        const {data} = await supabase.from('nactet_students').select('id,program_id').in('program_id', ids.slice(i,i+50)).order('id').range(from, from+999);
        (data||[]).forEach(r=>{ counts[r.program_id]=(counts[r.program_id]||0)+1; });
        if (!data || data.length<1000) break;
      }
    }
    setPushMany({ counts, sel:new Set(ids.filter(id=>counts[id]>0)), running:false, prog:'', result:null });
  }

  async function runPushMany() {
    const list = filteredProgs.filter(p=>pushMany.sel.has(p.id));
    if (!list.length) { toast.error('Select at least one program'); return; }
    if (!window.confirm('Push students of '+list.length+' programs to Marketing CRM?')) return;
    setPushMany(m=>({...m, running:true, prog:'0/'+list.length}));
    try {
      const result = await pushProgramsToCRM(list, colleges, (d,t)=>setPushMany(m=>m?({...m, prog:d+'/'+t}):m));
      setPushMany(m=>({...m, running:false, result}));
    } catch (e) { toast.error('Push failed: '+e.message); setPushMany(m=>({...m, running:false})); }
  }

  const selProg = programs.find(p=>p.id===selProgram);
  const selCol = selProg ? colleges.find(c=>c.id===selProg.college_id) : null;
  // Per-college tie detection
  const collegeTopMarks = {};
  const collegeIds = [...new Set(students.map(s=>s.college_id).filter(Boolean))];
  collegeIds.forEach(cid => {
    const cs = students.filter(s=>s.college_id===cid && s.marks!=null);
    if(cs.length) collegeTopMarks[cid] = Math.max(...cs.map(s=>Number(s.marks)));
  });
  // tiedStudents = students who share top mark with another student in the SAME college
  const tiedStudents = students.filter(s => {
    if(!s.college_id || s.marks==null) return false;
    const colTop = collegeTopMarks[s.college_id];
    if(Number(s.marks) !== colTop) return false;
    return students.filter(x=>x.college_id===s.college_id && Number(x.marks)===colTop).length > 1;
  });
  const hasTie = tiedStudents.length > 0;

  function clearFilters(){setFZone('');setFUniv('');setFCollege('');setSelProgram('');setStudents([]);setFEnteredBy('');setFVerifiedBy('');}

  // ── Export to Excel ──────────────────────────────────────
  function exportExcel() {
    if(!selProg||!students.length) return;
    const college = selCol?.college_name||'College';
    const stream = selProg.stream||'';
    const batch = selProg.batch_label?'Batch '+selProg.batch_label:'';
    const year = selProg.year||'';
    const title = `NACTET Scholarship Exam — ${college}`;
    const subtitle = `${stream}${batch?' '+batch:''} | Year: ${year} | Total: ${students.length} | Attended: ${students.filter(s=>s.marks!=null).length}`;

    const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const colW = [4,28,12,10,12,10,8,10,14,20];
    const cols = colW.map((w,i)=>`<col min='${i+1}' max='${i+1}' width='${w}' customWidth='1'/>`).join('');
    const hdrs = ['#','Student Name','Phone','Batch','Stream','Marks','Rank','Winner','Remarks','Interest Status'];

    let rows = '';
    // Header rows
    rows += `<row r='1' ht='28' customHeight='1'><c r='A1' s='1' t='inlineStr'><is><t>${esc(title)}</t></is></c></row>`;
    rows += `<row r='2' ht='18' customHeight='1'><c r='A2' s='2' t='inlineStr'><is><t>${esc(subtitle)}</t></is></c></row>`;
    rows += `<row r='3' ht='18' customHeight='1'><c r='A3' s='2' t='inlineStr'><is><t>Generated: ${new Date().toLocaleDateString('en-IN')}</t></is></c></row>`;
    rows += `<row r='4' ht='20' customHeight='1'>${hdrs.map((h,i)=>`<c r='${String.fromCharCode(65+i)}4' s='3' t='inlineStr'><is><t>${esc(h)}</t></is></c>`).join('')}</row>`;

    students.forEach((s,i) => {
      const rn = i+5;
      const cells = [
        `<c r='A${rn}' s='4'><v>${i+1}</v></c>`,
        `<c r='B${rn}' s='4' t='inlineStr'><is><t>${esc(s.student_name)}</t></is></c>`,
        `<c r='C${rn}' s='4' t='inlineStr'><is><t>${esc(s.phone)}</t></is></c>`,
        `<c r='D${rn}' s='4' t='inlineStr'><is><t>${esc(s.batch_label||batch)}</t></is></c>`,
        `<c r='E${rn}' s='4' t='inlineStr'><is><t>${esc(s.stream||stream)}</t></is></c>`,
        `<c r='F${rn}' s='4'>${s.marks!=null?`<v>${s.marks}</v>`:''}</c>`,
        `<c r='G${rn}' s='4'>${s.rank!=null?`<v>${s.rank}</v>`:''}</c>`,
        `<c r='H${rn}' s='4' t='inlineStr'><is><t>${s.is_winner?'✓ Winner':''}</t></is></c>`,
        `<c r='I${rn}' s='4' t='inlineStr'><is><t>${esc(s.remarks||'')}</t></is></c>`,
        `<c r='J${rn}' s='4' t='inlineStr'><is><t>${esc(s.interest_status||'')}</t></is></c>`,
      ].join('');
      rows += `<row r='${rn}'>${cells}</row>`;
    });

    const styles = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><styleSheet xmlns='http://schemas.openxmlformats.org/spreadsheetml/2006/main'><fonts count='4'><font><sz val='11'/><color theme='1'/><name val='Calibri'/></font><font><b/><sz val='14'/><name val='Calibri'/></font><font><b/><sz val='10'/><name val='Calibri'/></font><font><sz val='10'/><name val='Calibri'/></font></fonts><fills count='3'><fill><patternFill patternType='none'/></fill><fill><patternFill patternType='gray125'/></fill><fill><patternFill patternType='solid'><fgColor rgb='FF1F4E79'/></patternFill></fill></fills><borders count='2'><border><left/><right/><top/><bottom/><diagonal/></border><border><left style='thin'><color auto='1'/></left><right style='thin'><color auto='1'/></right><top style='thin'><color auto='1'/></top><bottom style='thin'><color auto='1'/></bottom><diagonal/></border></borders><cellStyleXfs count='1'><xf numFmtId='0' fontId='0' fillId='0' borderId='0'/></cellStyleXfs><cellXfs count='5'><xf numFmtId='0' fontId='0' fillId='0' borderId='0' xfId='0'/><xf numFmtId='0' fontId='1' fillId='0' borderId='0' xfId='0' applyFont='1'><alignment wrapText='1'/></xf><xf numFmtId='0' fontId='2' fillId='0' borderId='0' xfId='0' applyFont='1'><alignment wrapText='1'/></xf><xf numFmtId='0' fontId='2' fillId='2' borderId='1' xfId='0' applyFont='1' applyFill='1' applyBorder='1'><alignment horizontal='center'/></xf><xf numFmtId='0' fontId='3' fillId='0' borderId='1' xfId='0' applyFont='1' applyBorder='1'/></cellXfs></styleSheet>`;
    const merges = `<mergeCells count='3'><mergeCell ref='A1:J1'/><mergeCell ref='A2:J2'/><mergeCell ref='A3:J3'/></mergeCells>`;
    const wsXml = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><worksheet xmlns='http://schemas.openxmlformats.org/spreadsheetml/2006/main'><sheetViews><sheetView workbookViewId='0' showGridLines='1'><pane ySplit='4' topLeftCell='A5' activePane='bottomLeft' state='frozen'/></sheetView></sheetViews><sheetFormatPr defaultRowHeight='15'/><cols>${cols}</cols><sheetData>${rows}</sheetData>${merges}</worksheet>`;
    const wbXml = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><workbook xmlns='http://schemas.openxmlformats.org/spreadsheetml/2006/main' xmlns:r='http://schemas.openxmlformats.org/officeDocument/2006/relationships'><sheets><sheet name='Students' sheetId='1' r:id='rId1'/></sheets></workbook>`;
    const relsXml = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><Relationships xmlns='http://schemas.openxmlformats.org/package/2006/relationships'><Relationship Id='rId1' Type='http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet' Target='worksheets/sheet1.xml'/><Relationship Id='rId2' Type='http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles' Target='styles.xml'/></Relationships>`;
    const ctXml = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><Types xmlns='http://schemas.openxmlformats.org/package/2006/content-types'><Default Extension='rels' ContentType='application/vnd.openxmlformats-package.relationships+xml'/><Default Extension='xml' ContentType='application/xml'/><Override PartName='/xl/workbook.xml' ContentType='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml'/><Override PartName='/xl/worksheets/sheet1.xml' ContentType='application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml'/><Override PartName='/xl/styles.xml' ContentType='application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml'/></Types>`;
    const topRels = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?><Relationships xmlns='http://schemas.openxmlformats.org/package/2006/relationships'><Relationship Id='rId1' Type='http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument' Target='xl/workbook.xml'/></Relationships>`;

    import('jszip').then(({default:JSZip})=>{
      const z = new JSZip();
      z.file('[Content_Types].xml', ctXml);
      z.file('_rels/.rels', topRels);
      z.file('xl/workbook.xml', wbXml);
      z.file('xl/_rels/workbook.xml.rels', relsXml);
      z.file('xl/worksheets/sheet1.xml', wsXml);
      z.file('xl/styles.xml', styles);
      z.generateAsync({type:'blob',mimeType:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
        .then(blob=>{
          const url=URL.createObjectURL(blob);
          const a=document.createElement('a');
          a.href=url;
          a.download=`NACTET_${college.replace(/[^a-z0-9]/gi,'_')}_${stream}_${year}${batch?'_'+batch:''}.xlsx`;
          document.body.appendChild(a);a.click();document.body.removeChild(a);
        });
    });
  }

  // ── Export to PDF ──────────────────────────────────────
  function exportPDF() {
    if(!selProg||!students.length) return;
    const college = selCol?.college_name||'College';
    const stream = selProg.stream||'';
    const batch = selProg.batch_label?'Batch '+selProg.batch_label:'';
    const year = selProg.year||'';
    const attended = students.filter(s=>s.marks!=null).length;
    const winners = students.filter(s=>s.is_winner);

    const rows = students.map((s,i) => `
      <tr style='background:${s.is_winner?'#fffbeb':i%2===0?'#ffffff':'#f8fafc'}'>
        <td>${i+1}</td>
        <td style='text-align:left;font-weight:${s.is_winner?'700':'400'}'>${s.student_name||''}${s.is_winner?' 🥇':''}</td>
        <td>${s.phone||'—'}</td>
        <td>${s.batch_label||batch||'—'}</td>
        <td>${s.stream||stream||'—'}</td>
        <td style='font-weight:600;color:${s.marks!=null?'#1e40af':'#94a3b8'}'>${s.marks!=null?s.marks:'AB'}</td>
        <td style='font-weight:600'>${s.rank!=null?'#'+s.rank:'—'}</td>
        <td style='color:${s.remarks==='Absent'?'#ef4444':'#374151'}'>${s.remarks||'—'}</td>
        <td>${s.interest_status||'—'}</td>
      </tr>`).join('');

    const winnerRows = winners.length ? winners.map((s,i)=>`
      <tr><td>${i+1}</td><td>${s.student_name}</td><td>${s.phone||'—'}</td><td>${s.marks}</td><td>#${s.rank}</td></tr>`).join('') : 
      '<tr><td colspan=5 style=\'text-align:center;color:#94a3b8\'>No winners selected</td></tr>';

    const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'>
    <title>NACTET Report</title>
    <style>
      body{font-family:Arial,sans-serif;margin:20px;color:#1e293b;font-size:11px;}
      .header{background:linear-gradient(135deg,#1e40af,#3b82f6);color:white;padding:16px 20px;border-radius:8px;margin-bottom:16px;}
      .header h1{margin:0;font-size:16px;letter-spacing:0.5px;}
      .header h2{margin:4px 0 0;font-size:12px;opacity:0.9;font-weight:400;}
      .stats{display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap;}
      .stat{background:#f1f5f9;border-radius:6px;padding:8px 14px;text-align:center;min-width:80px;}
      .stat .val{font-size:20px;font-weight:700;color:#1e40af;}
      .stat .lbl{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1e40af;color:white;padding:7px 8px;text-align:center;font-size:10px;letter-spacing:0.3px;}
      td{padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:center;}
      .section-title{font-size:13px;font-weight:700;color:#1e40af;border-bottom:2px solid #1e40af;padding-bottom:4px;margin:16px 0 10px;}
      .footer{text-align:center;color:#94a3b8;font-size:9px;margin-top:20px;border-top:1px solid #e2e8f0;padding-top:10px;}
      @media print{
        @page{size:A4 portrait;margin:15mm 12mm 15mm 12mm;}
        body{margin:0;font-size:10px;}
        .no-print{display:none;}
        table{page-break-inside:auto;}
        tr{page-break-inside:avoid;page-break-after:auto;}
        thead{display:table-header-group;}
        .header{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        th{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        tr[style*='fffbeb']{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      }
    </style></head><body>
    <div class='header'>
      <h1>🎓 NACTET SCHOLARSHIP EXAM REPORT</h1>
      <h2>${college} &nbsp;|&nbsp; ${stream}${batch?' — '+batch:''} &nbsp;|&nbsp; Year: ${year}</h2>
    </div>
    <div class='stats'>
      <div class='stat'><div class='val'>${students.length}</div><div class='lbl'>Registered</div></div>
      <div class='stat'><div class='val'>${attended}</div><div class='lbl'>Attended</div></div>
      <div class='stat'><div class='val'>${students.length-attended}</div><div class='lbl'>Absent</div></div>
      <div class='stat'><div class='val'>${winners.length}</div><div class='lbl'>Winners</div></div>
      <div class='stat'><div class='val'>${students.filter(s=>s.marks!=null).length?Math.max(...students.filter(s=>s.marks!=null).map(s=>s.marks)):'—'}</div><div class='lbl'>Top Marks</div></div>
      <div class='stat'><div class='val'>${students.filter(s=>s.marks!=null).length?(students.filter(s=>s.marks!=null).reduce((a,s)=>a+s.marks,0)/students.filter(s=>s.marks!=null).length).toFixed(1):'—'}</div><div class='lbl'>Average</div></div>
    </div>
    <div class='section-title'>📋 Student Results</div>
    <table>
      <thead><tr><th>#</th><th>Student Name</th><th>Phone</th><th>Batch</th><th>Stream</th><th>Marks</th><th>Rank</th><th>Remarks</th><th>Interest</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${winners.length>0?`<div class='section-title'>🥇 Winners</div>
    <table><thead><tr><th>#</th><th>Name</th><th>Phone</th><th>Marks</th><th>Rank</th></tr></thead>
    <tbody>${winnerRows}</tbody></table>`:''}
    <div class='footer'>Generated on ${new Date().toLocaleString('en-IN')} &nbsp;|&nbsp; NACTET Scholarship Program &nbsp;|&nbsp; Armstech Engineers</div>
    <div class='no-print' style='text-align:center;margin-top:20px;'>
      <button onclick='window.print()' style='background:#1e40af;color:white;border:none;padding:10px 24px;border-radius:6px;font-size:13px;cursor:pointer;'>🖨️ Print / Save as PDF</button>
    </div>
    </body></html>`;

    const win = window.open('','_blank');
    win.document.write(html);
    win.document.close();
  }


  return (
    <div>
      <div className="card" style={{marginBottom:12,display:'flex',gap:8,width:'fit-content',flexWrap:'wrap',alignItems:'center'}}>
        <select value={fYear} onChange={e=>setFYear(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={fZone} onChange={e=>{setFZone(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Zones</option>
          {[...new Set(colleges.map(c=>c.zone).filter(Boolean))].sort().map(z=><option key={z} value={z}>{z}</option>)}
        </select>
        <select value={fUniv} onChange={e=>{setFUniv(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Universities</option>
          {[...new Set(colleges.map(c=>c.university).filter(Boolean))].sort().map(u=><option key={u} value={u}>{u}</option>)}
        </select>
        <SearchableSelect value={fCollege} onChange={v=>setFCollege(v||'')}
          options={collegeFilterOptions(colleges,programs,fYear,fZone)}
          placeholder="All Colleges" searchPlaceholder="Search..." style={{minWidth:200}}/>
        <SearchableSelect
          value={selProgram}
          onChange={v=>{setSelProgram(v||'');if(v)loadStudents(v);}}
          options={[{value:'',label:'— All Programs —'},...filteredProgs.map(p=>{
            const col=colleges.find(c=>c.id===p.college_id);
            return {value:p.id, label:`${col?.college_name||'?'} — ${p.stream}${p.batch_label?' Batch '+p.batch_label:''} ${p.year}`};
          })]}
          placeholder="— All Programs —"
          searchPlaceholder="Search program..."
          style={{minWidth:250}}
        />
        <select value={fEnteredBy} onChange={e=>setFEnteredBy(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:150}}>
          <option value=''>All Entered By</option>
          {entryStaff.map(s=><option key={s.id} value={s.staff_name}>{s.staff_name}</option>)}
          <option value='__none__'>— Not Set —</option>
        </select>
        <select value={fVerifiedBy} onChange={e=>setFVerifiedBy(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:150}}>
          <option value=''>All Verified By</option>
          {entryStaff.map(s=><option key={s.id} value={s.staff_name}>{s.staff_name}</option>)}
          <option value='__none__'>— Not Verified —</option>
        </select>
        <button onClick={()=>clearFilters()} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>✕ Clear</button>
        <button onClick={()=>setShowStaffReport(true)} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>📊 Staff Report</button>
        {canEdit('nactet')&&<button onClick={openPushMany} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--green)',color:'var(--green)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>📤 Push many to CRM</button>}
        <button className="btn btn-primary btn-sm" onClick={async ()=>{
          setLoading(true);
          if (selProgram) {
            // Load specific program
            const { data } = await supabase.from('nactet_students').select('*').eq('program_id', selProgram).order('rank');
            setStudents(data||[]);
    // Auto-populate class_reps from nactet_class_reps if not set
    const prog2 = programs.find(p=>p.id===progId);
    if(prog2?.college_id && !(prog2.class_reps||[]).length) {
      const {data:repsData} = await supabase.from('nactet_class_reps').select('*').eq('college_id',prog2.college_id).eq('stream',prog2.stream||'');
      if(repsData?.length>0) {
        const mapped = repsData.map(r=>({batch:'All',name:r.rep_name,phone:r.contact}));
        await supabase.from('nactet_programs').update({class_reps:mapped}).eq('id',progId);
      }
    }
            const { data: cdata } = await supabase.from('nactet_rank_contacts').select('*').eq('program_id', selProgram).order('created_at',{ascending:false});
            setContacts(cdata||[]);
          } else if (filteredProgs.length > 0) {
            // Load all students for filtered programs
            const ids = filteredProgs.map(p=>p.id);
            const { data } = await supabase.from('nactet_students').select('*').in('program_id', ids).order('rank');
            setStudents(data||[]);
            const { data: cdata } = await supabase.from('nactet_rank_contacts').select('*').in('program_id', ids).order('created_at',{ascending:false});
            setContacts(cdata||[]);
          } else {
            toast.info('No programs found for selected filters');
          }
          setLoading(false);
        }} style={{minWidth:80}}>🔍 Load</button>
        {selProgram && canEdit('nactet') && (<>
          <button className="btn btn-primary btn-sm" onClick={()=>{setEditing(null);setForm({...emptyStd,stream:selProg?.stream||'',batch_label:selProg?.batch_label||''});setShowModal(true);}}>+ Add Student</button>
          {selProgram&&canEdit('nactet')&&<button className="btn btn-ghost btn-sm" onClick={()=>{setBulkRows(initBulkRows(5));setShowBulkEntry(true);}}>📋 Bulk Entry</button>}
          {selectedIds.size>0&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={deleteSelected}>🗑 Delete Selected ({selectedIds.size})</button>}
          {selProg?.no_winner&&<span className="badge badge-red" style={{marginLeft:4}}>🚫 No Winner Selected</span>}
          {!selProg?.no_winner&&canEdit('nactet')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>{setNoWinnerReason('');setShowNoWinner(true);}}>🚫 Mark No Winner</button>}
          {selProg?.no_winner&&canEdit('nactet')&&<button className="btn btn-ghost btn-sm" style={{color:'var(--green)',fontSize:11}} onClick={async()=>{await supabase.from('nactet_programs').update({no_winner:false,no_winner_reason:''}).eq('id',selProgram);toast.success('Cleared');loadStudents(selProgram);}}>↩ Clear No Winner</button>}
          <button className="btn btn-ghost btn-sm" onClick={autoRank} disabled={students.filter(s=>s.marks!=null).length===0}>🏆 Auto Rank</button>
          <button className="btn btn-ghost btn-sm" style={{color:'var(--green)'}} onClick={pushToCRM} disabled={students.length===0}>📤 Push to CRM</button>
          <button className="btn btn-ghost btn-sm" style={{color:'var(--blue,#3b82f6)'}} onClick={exportExcel} disabled={!students.length}>📊 Excel</button>
          <button className="btn btn-ghost btn-sm" style={{color:'var(--red,#ef4444)'}} onClick={exportPDF} disabled={!students.length}>📄 PDF</button>
        </>)}
      </div>

      {/* Staff Report Modal */}
      {showStaffReport&&(
        <div className='modal-overlay' onClick={e=>e.target===e.currentTarget&&setShowStaffReport(false)} style={{zIndex:9999,position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div className='modal' style={{maxWidth:600}}>
            <div className='modal-header'>
              <span className='modal-title'>📊 Data Entry & Verification Report</span>
              <button className='btn btn-ghost btn-sm' onClick={()=>setShowStaffReport(false)}>✕</button>
            </div>
            <div className='modal-body' style={{padding:16}}>
              {(()=>{
                const yearProgs = programs.filter(p=>!fYear||p.year==fYear);
                const staffRows = entryStaff.map(s=>{
                  const entered = yearProgs.filter(p=>p.entered_by===s.staff_name);
                  const verified = yearProgs.filter(p=>p.verified_by===s.staff_name);
                  return {...s, entered, verified};
                }).filter(s=>s.entered.length>0||s.verified.length>0);
                const notEntered = yearProgs.filter(p=>!p.entered_by).length;
                const notVerified = yearProgs.filter(p=>!p.verified_by).length;
                return (
                  <div>
                    <div style={{display:'flex',gap:12,marginBottom:12,flexWrap:'wrap'}}>
                      <span style={{fontSize:12,padding:'4px 10px',borderRadius:6,background:'var(--accent-light)',color:'var(--accent)',fontWeight:600}}>Total Programs: {yearProgs.length}</span>
                      <span style={{fontSize:12,padding:'4px 10px',borderRadius:6,background:'rgba(239,68,68,0.1)',color:'var(--red)',fontWeight:600}}>⚠️ Not Entered: {notEntered}</span>
                      <span style={{fontSize:12,padding:'4px 10px',borderRadius:6,background:'rgba(245,158,11,0.1)',color:'var(--amber)',fontWeight:600}}>⚠️ Not Verified: {notVerified}</span>
                    </div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'var(--surface2)'}}>
                          <th style={{padding:'8px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Staff</th>
                          <th style={{padding:'8px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Designation</th>
                          <th style={{padding:'8px 10px',textAlign:'center',borderBottom:'1px solid var(--border)'}}>✏️ Entered</th>
                          <th style={{padding:'8px 10px',textAlign:'center',borderBottom:'1px solid var(--border)'}}>✅ Verified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffRows.length===0
                          ? <tr><td colSpan={4} style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No data entry assigned yet</td></tr>
                          : staffRows.map(s=>(
                            <tr key={s.id} style={{borderBottom:'1px solid var(--border)'}}>
                              <td style={{padding:'8px 10px',fontWeight:600}}>{s.staff_name}</td>
                              <td style={{padding:'8px 10px',color:'var(--text3)',fontSize:11}}>{s.designation}</td>
                              <td style={{padding:'8px 10px',textAlign:'center'}}>
                                {s.entered.length>0
                                  ? <span style={{background:'var(--accent-light)',color:'var(--accent)',padding:'2px 10px',borderRadius:4,fontWeight:700,cursor:'pointer'}} onClick={()=>{setFEnteredBy(s.staff_name);setShowStaffReport(false);}}>{s.entered.length}</span>
                                  : <span style={{color:'var(--text3)'}}>—</span>}
                              </td>
                              <td style={{padding:'8px 10px',textAlign:'center'}}>
                                {s.verified.length>0
                                  ? <span style={{background:'var(--green-light)',color:'var(--green)',padding:'2px 10px',borderRadius:4,fontWeight:700,cursor:'pointer'}} onClick={()=>{setFVerifiedBy(s.staff_name);setShowStaffReport(false);}}>{s.verified.length}</span>
                                  : <span style={{color:'var(--text3)'}}>—</span>}
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
            <div className='modal-footer'>
              <button className='btn btn-ghost' onClick={()=>setShowStaffReport(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {selProgram && (
        <div className="card" style={{marginBottom:12,display:'flex',gap:16,fontSize:12,flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontWeight:600}}>{selCol?.college_name} — {selProg?.stream}{selProg?.batch_label?' Batch '+selProg.batch_label:''} {selProg?.year}</span>
          <span>Total Students: <strong>{selProg?.total_students||selProg?.registered_students||0}</strong></span>
          <span>Attended: <strong>{selProg?.students_attended||0}</strong></span>
          <span>In DB: <strong>{students.length}</strong></span>
          <span style={{color:'var(--green)'}}>Winners: <strong>{students.filter(s=>s.is_winner).length}</strong></span>
          <span style={{marginLeft:'auto',display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{fontSize:11,color:'var(--text3)'}}>✏️ Entered by:</span>
              {canEdit('nactet')
                ? <select value={selProg?.entered_by||''} onChange={async e=>{
                    await supabase.from('nactet_programs').update({entered_by:e.target.value||null}).eq('id',selProgram);
                    onRefresh();
                  }} style={{fontSize:11,padding:'2px 4px',border:'1px solid var(--border)',borderRadius:4,background:'var(--bg)'}}>
                    <option value=''>— Not set —</option>
                    {entryStaff.map(s=><option key={s.id} value={s.staff_name}>{s.staff_name} ({s.designation})</option>)}
                  </select>
                : <strong style={{fontSize:12}}>{selProg?.entered_by||'—'}</strong>
              }
            </span>
            <span style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{fontSize:11,color:'var(--text3)'}}>✅ Verified by:</span>
              {canEdit('nactet')
                ? <select value={selProg?.verified_by||''} onChange={async e=>{
                    await supabase.from('nactet_programs').update({verified_by:e.target.value||null}).eq('id',selProgram);
                    onRefresh();
                  }} style={{fontSize:11,padding:'2px 4px',border:'1px solid var(--border)',borderRadius:4,background:'var(--bg)'}}>
                    <option value=''>— Not set —</option>
                    {entryStaff.map(s=><option key={s.id} value={s.staff_name}>{s.staff_name} ({s.designation})</option>)}
                  </select>
                : <strong style={{fontSize:12}}>{selProg?.verified_by||'—'}</strong>
              }
            </span>
          </span>
        </div>
      )}
      {selProgram && (
        <div className="card" style={{marginBottom:12,display:'flex',gap:16,fontSize:12,flexWrap:'wrap',alignItems:'center',display:'none'}}>
          {hasTie && <span style={{background:'var(--amber-light)',color:'var(--amber)',padding:'2px 10px',borderRadius:8,fontWeight:600}}>
            ⚠️ Tie — {tiedStudents.length} students tied at top marks — Re-exam required!
          </span>}
        </div>
      )}

      {students.length>0&&<div style={{display:'flex',gap:10,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:12,background:'var(--accent-light)',color:'var(--accent)',padding:'3px 10px',borderRadius:8,fontWeight:600}}>👥 Total: {students.length}</span>
        <span style={{fontSize:12,background:'var(--green-light)',color:'var(--green)',padding:'3px 10px',borderRadius:8,fontWeight:600}}>✅ Attended: {students.filter(s=>s.marks!=null).length}</span>
        <span style={{fontSize:12,background:'var(--blue-light)',color:'var(--blue)',padding:'3px 10px',borderRadius:8,fontWeight:600}}>📝 Marks Entered: {students.filter(s=>s.marks!=null).length}</span>
        <span style={{fontSize:12,background:'var(--surface2)',color:'var(--text2)',padding:'3px 10px',borderRadius:8,fontWeight:600}}>⏳ Pending: {students.filter(s=>s.marks==null).length}</span>
      </div>}
      {(()=>{
        const prog = programs.find(p=>p.id===selProgram);
        const progReps = prog?.class_reps||[];
        const colReps = colleges.find(c=>c.id===prog?.college_id);
        if(progReps.length===0) return null;
        return(
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8,padding:'6px 10px',background:'var(--surface2)',borderRadius:6,fontSize:12,alignItems:'center'}}>
            <span style={{fontWeight:600,color:'var(--text2)'}}>👥 Class Reps:</span>
            {progReps.map((r,i)=>(
              <span key={i} style={{background:'var(--accent-light)',color:'var(--accent)',padding:'2px 8px',borderRadius:6}}>
                {r.stream&&<span style={{fontSize:10,background:'var(--accent)',color:'#fff',padding:'1px 5px',borderRadius:3,marginRight:4}}>{r.stream}</span>}
                {prog?.num_batches>1?`Batch ${r.batch}: `:''}<strong>{r.name}</strong>{r.phone?` — ${r.phone}`:''}
              </span>
            ))}
          </div>
        );
      })()}
      <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
        <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:phoneFilter?'var(--red)':'var(--text2)',background:phoneFilter?'var(--red-light)':'none',border:'1px solid var(--border)'}} onClick={()=>setPhoneFilter(f=>!f)}>
          ⚠️ Phone Issues ({students.filter(s=>!s.phone||!/^\d{10}$/.test(s.phone?.trim())).length})
        </button>
        <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:remarksFilter?'var(--accent)':'var(--text2)',background:remarksFilter?'var(--accent-light)':'none',border:'1px solid var(--border)'}} onClick={()=>setRemarksFilter(f=>!f)}>
          📝 Has Remarks ({students.filter(s=>s.remarks?.trim()).length})
        </button>
      </div>
      <div className="card" style={{padding:0,overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:900}}>
          <thead>
            <tr style={{background:'var(--accent-light)'}}>
              <th style={{padding:'8px 10px',width:32}}>
                <input type="checkbox" onChange={e=>{if(e.target.checked)setSelectedIds(new Set(students.map(s=>s.id)));else setSelectedIds(new Set());}}/>
              </th>
              {['Rank','College','Batch','Name','Phone','Marks','Re-exam','Final Rank','Stream','Last Contact','Remarks',''].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(()=>{
              const _allPhones = students.map(s=>s.phone?.trim()).filter(Boolean);
              const _dupPhones = new Set(_allPhones.filter((p,i)=>_allPhones.indexOf(p)!==i));
              const dispStudents = (selProg?.num_batches>1 && students.some(s=>s.batch_label)) ? students.filter(s=>s.batch_label===selBatch) : students;
              // Group by college, sort each group by rank, then concat
              // Group by college_id + stream
              const collegeStreamOrder = [...new Set(dispStudents.map(s=>s.college_id+'||'+(s.stream||'')))];
              const grouped = collegeStreamOrder.flatMap(key => {
                const [cid, stream] = key.split('||');
                return [...dispStudents.filter(s=>s.college_id===cid&&(s.stream||'')===(stream||''))].sort((a,b)=>{
                  if(a.rank&&b.rank) return a.rank-b.rank;
                  if(a.rank) return -1;
                  if(b.rank) return 1;
                  return Number(b.marks||0)-Number(a.marks||0);
                });
              });
              // Phone issues filter
              const allPhones = grouped.map(s=>s.phone?.trim()).filter(Boolean);
              const dupPhones = new Set(allPhones.filter((p,i)=>allPhones.indexOf(p)!==i));
              const isPhoneIssue = (s) => !s.phone || !/^\d{10}$/.test(s.phone?.trim()) || _dupPhones.has(s.phone?.trim());
              const finalList = grouped
                .filter(s => phoneFilter ? isPhoneIssue(s) : true)
                .filter(s => remarksFilter ? (s.remarks && s.remarks.trim()) : true);
              return finalList.map((s,i)=>{
              const studentContacts=contacts.filter(c=>c.student_id===s.id);
              const lastContact=studentContacts[0];
              const isTied=tiedStudents.some(t=>t.id===s.id);
              return (
                <tr key={s.id} style={{borderBottom:'1px solid var(--border)',
                  background:s.is_winner?'rgba(0,176,155,0.08)':isTied?'rgba(245,158,11,0.08)':i%2===0?'':'var(--surface2)'}}>
                  <td style={{padding:'4px 8px',textAlign:'center'}}>
                        <input type="checkbox" checked={selectedIds.has(s.id)}
                          onChange={e=>{const n=new Set(selectedIds);e.target.checked?n.add(s.id):n.delete(s.id);setSelectedIds(n);}}/>
                      </td>
                      <td style={{padding:'8px 10px',fontWeight:700,color:s.rank===1?'var(--amber)':'var(--text)'}}>
                    {s.marks==null?<span style={{color:'var(--red)',fontWeight:600}}>AB</span>:s.rank||'—'}
                    {s.rank===1&&isTied&&!s.reexam_marks&&<span style={{fontSize:9,color:'var(--amber)',display:'block'}}>⚠️ Tied</span>}
                  </td>
                  <td style={{padding:'8px 10px'}}>
                    <div style={{fontSize:11,color:'var(--text2)'}}>{colleges.find(c=>c.id===s.college_id)?.college_name||'—'}</div>
                  </td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>
                    {s.batch_label?<span className="badge badge-blue" style={{fontSize:9}}>{s.batch_label}</span>:'—'}
                  </td>
                  <td style={{padding:'8px 10px',fontWeight:500}}>
                    {s.student_name} {s.is_winner&&'🏆'} {s.is_final_winner&&<span style={{fontSize:10,color:'var(--green)'}}>★Final</span>}
                    {s.interest_status&&s.interest_status!=='Pending'&&<span style={{fontSize:9,marginLeft:4,padding:'1px 5px',borderRadius:4,background:s.interest_status==='Interested'?'var(--green-light)':s.interest_status==='Not Interested'?'rgba(239,68,68,0.1)':'rgba(245,158,11,0.1)',color:s.interest_status==='Interested'?'var(--green)':s.interest_status==='Not Interested'?'var(--red)':'var(--amber)'}}>{s.interest_status}</span>}
                    {s.interest_status&&s.interest_status!=='Pending'&&<span style={{fontSize:9,marginLeft:4,padding:'1px 5px',borderRadius:4,background:s.interest_status==='Interested'?'var(--green-light)':s.interest_status==='Not Interested'?'rgba(239,68,68,0.1)':'rgba(245,158,11,0.1)',color:s.interest_status==='Interested'?'var(--green)':s.interest_status==='Not Interested'?'var(--red)':'var(--amber)'}}>{s.interest_status}</span>}
                    <div style={{display:'flex',gap:4,marginTop:2}}>
                      {s.main_cert_given&&<span style={{fontSize:9,background:'var(--green-light)',color:'var(--green)',padding:'1px 4px',borderRadius:4}}>📜 Main</span>}
                      {s.provisional_cert_given&&!s.main_cert_given&&<span style={{fontSize:9,background:'var(--amber-light)',color:'var(--amber)',padding:'1px 4px',borderRadius:4}}>📄 Prov</span>}
                    </div>
                  </td>
                  <td style={{padding:'8px 10px'}}>
                    {s.phone||'—'}
                    {(!s.phone||!/^\d{10}$/.test(s.phone?.trim())||_dupPhones.has(s.phone?.trim()))&&<span title={!s.phone?'No phone':_dupPhones.has(s.phone?.trim())?'Duplicate phone':'Invalid (not 10 digits)'} style={{marginLeft:4,color:'var(--red)',fontSize:11}}>⚠️</span>}
                  </td>
                  <td style={{padding:'8px 10px',fontWeight:600,color:'var(--accent)'}}>{s.marks!=null?s.marks:<span style={{color:'var(--red)',fontWeight:600}}>AB</span>}</td>
                  <td style={{padding:'8px 10px',color:'var(--purple)'}}>
                    {s.reexam_marks!=null?<><strong>{s.reexam_marks}</strong><span style={{fontSize:10,color:'var(--text3)',display:'block'}}>{fmtDMY(s.reexam_date)}</span></>
                      :(isTied?<span style={{color:'var(--amber)',fontSize:11}}>Pending</span>:'—')}
                  </td>
                  <td style={{padding:'8px 10px',fontWeight:600}}>{s.marks==null?<span style={{color:'var(--red)',fontWeight:600}}>AB</span>:s.final_rank||'—'}</td>
                  <td style={{padding:'8px 10px'}}>{s.stream||'—'}</td>
                  <td style={{padding:'8px 10px'}}>
                    {lastContact?<span style={{fontSize:11,padding:'2px 6px',borderRadius:8,
                      background:lastContact.contact_status==='Joined'?'var(--green-light)':lastContact.contact_status==='Backed Out'?'var(--red-light)':'var(--amber-light)',
                      color:lastContact.contact_status==='Joined'?'var(--green)':lastContact.contact_status==='Backed Out'?'var(--red)':'var(--amber)'
                    }}>{lastContact.contact_status} ({studentContacts.length})</span>:'—'}
                  </td>
                  <td style={{padding:'8px 10px',fontSize:11,color:'var(--text2)'}}>{s.remarks||'—'}</td>
                  <td style={{padding:'8px 6px'}}>
                    <div style={{display:'flex',gap:2,flexWrap:'wrap'}}>
                      {canEdit('nactet')&&<>
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 5px'}}
                          onClick={()=>{setEditing(s.id);setForm({...s});setShowModal(true);}}>✏️</button>
                        {isTied&&!s.reexam_marks&&<button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 5px',color:'var(--amber)'}}
                          onClick={()=>{setSelStudent(s);setReexamForm({reexam_date:s.reexam_date||'',reexam_marks:s.reexam_marks||''});setShowReexam(true);}}>📝 Re-exam</button>}
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 5px',color:'var(--accent)'}}
                          onClick={()=>{setSelStudent(s);setShowContact(true);}}>📞</button>
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 5px',color:'var(--red)'}}
                          onClick={()=>deleteStudent(s.id)}>✕</button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            });
            })()
}          </tbody>
        </table>
        {!selProgram&&<div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>Select a program to view students</div>}
        {selProgram&&students.length===0&&!loading&&<div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No students added yet</div>}
      </div>

      {/* Add/Edit Student Modal */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Student</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Student Name *</label>
                  <input value={form.student_name} onChange={e=>sf('student_name',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input value={form.phone||''} onChange={e=>sf('phone',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input value={form.email||''} onChange={e=>sf('email',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label>Stream</label>
                  <select value={form.stream||''} onChange={e=>sf('stream',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select value={form.source||''} onChange={e=>sf('source',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {['Answer Sheet','Pre-registration','College Sent','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Marks</label>
                  <input type="number" value={form.marks||''} onChange={e=>sf('marks',e.target.value)} min={0} max={100}/>
                </div>
                <div className="form-group">
                  <label>Rank</label>
                  <input type="number" value={form.rank||''} onChange={e=>sf('rank',e.target.value)} min={1}/>
                </div>
                <div className="form-group">
                  <label>Interest Status</label>
                  <select value={form.interest_status||'Pending'} onChange={e=>sf('interest_status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {['Pending','Interested','Not Interested','Not Reachable','Call Later'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Interest Status</label>
                  <select value={form.interest_status||'Pending'} onChange={e=>sf('interest_status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {['Pending','Interested','Not Interested','Not Reachable','Call Later'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <label style={{display:'flex',gap:8,alignItems:'center',fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={form.provisional_cert_given||false} onChange={e=>sf('provisional_cert_given',e.target.checked)}/>
                  📄 Provisional Cert Given
                </label>
                <label style={{display:'flex',gap:8,alignItems:'center',fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={form.main_cert_given||false} onChange={e=>sf('main_cert_given',e.target.checked)}/>
                  📜 Main Cert Given
                </label>
                {selProg?.num_batches > 1 && (
                  <div className="form-group">
                    <label>Batch</label>
                    <select value={form.batch_label||''} onChange={e=>sf('batch_label',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                      <option value="">Select</option>
                      {Array.from({length:selProg.num_batches},(_,i)=>String.fromCharCode(65+i)).map(b=>(
                        <option key={b} value={b}>Batch {b}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Remarks</label>
                  <textarea value={form.remarks||''} onChange={e=>sf('remarks',e.target.value)} rows={2} placeholder="Any additional details about this student..."/>
                </div>
                <label style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1',fontSize:12,cursor:'pointer'}}>
                  <input type="checkbox" checked={form.is_winner||false} onChange={e=>sf('is_winner',e.target.checked)}/>
                  🏆 Mark as Winner
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!form.student_name||saving} onClick={saveStudent}>
                {saving?'Saving...':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-exam Modal */}
      {showReexam&&selStudent&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowReexam(false)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <span className="modal-title">📝 Re-exam — {selStudent.student_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowReexam(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'var(--amber-light)',padding:10,borderRadius:6,marginBottom:12,fontSize:12,color:'var(--amber)'}}>
                ⚠️ Tied at {selStudent.marks} marks. Enter re-exam details.
              </div>
              <div style={{display:'grid',gap:10}}>
                <div className="form-group">
                  <label>Re-exam Date</label>
                  <DateInput value={reexamForm.reexam_date} onChange={e=>setReexamForm(f=>({...f,reexam_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Re-exam Marks</label>
                  <input type="number" value={reexamForm.reexam_marks} onChange={e=>setReexamForm(f=>({...f,reexam_marks:e.target.value}))} min={0} max={100}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowReexam(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveReexam} disabled={saving}>
                {saving?'Saving...':'Save Re-exam'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact History Modal */}
      {showContact&&selStudent&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowContact(false)}>
          <div className="modal" style={{maxWidth:520,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">📞 {selStudent.student_name} — Rank {selStudent.final_rank||selStudent.rank}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowContact(false)}>✕</button>
            </div>
            <div className="modal-body">
              {contacts.filter(c=>c.student_id===selStudent.id).length>0&&(
                <div style={{marginBottom:12}}>
                  <div style={{fontWeight:600,fontSize:12,marginBottom:6}}>Contact History:</div>
                  {contacts.filter(c=>c.student_id===selStudent.id).map(c=>(
                    <div key={c.id} style={{padding:'6px 10px',background:'var(--surface2)',borderRadius:6,marginBottom:4,fontSize:12}}>
                      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                        <span style={{fontWeight:600}}>{fmtDMY(c.contact_date)}</span>
                        <span style={{padding:'1px 6px',borderRadius:8,fontSize:11,
                          background:c.contact_status==='Joined'?'var(--green-light)':c.contact_status==='Backed Out'?'var(--red-light)':'var(--amber-light)',
                          color:c.contact_status==='Joined'?'var(--green)':c.contact_status==='Backed Out'?'var(--red)':'var(--amber)'}}>
                          {c.contact_status}
                        </span>
                        {c.contacted_by&&<span style={{color:'var(--text3)',fontSize:11}}>by {c.contacted_by}</span>}
                        {c.provisional_cert_given&&<span style={{color:'var(--amber)',fontSize:11}}>📄 Prov Cert</span>}
                        {c.main_cert_given&&<span style={{color:'var(--green)',fontSize:11}}>📜 Main Cert</span>}
                      </div>
                      {c.reason&&<div style={{color:'var(--text3)',fontSize:11,marginTop:2}}>Reason: {c.reason}</div>}
                      {c.notes&&<div style={{color:'var(--text3)',fontSize:11}}>Notes: {c.notes}</div>}
                      <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)',marginTop:4}}
                        onClick={()=>deleteContact(c.id)}>🗑 Delete</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontWeight:600,fontSize:12,marginBottom:8,borderTop:'1px solid var(--border)',paddingTop:10}}>Add New Contact:</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Contact Date</label>
                  <DateInput value={contactForm.contact_date} onChange={e=>setContactForm(f=>({...f,contact_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={contactForm.contact_status} onChange={e=>setContactForm(f=>({...f,contact_status:e.target.value}))}>
                    {['Pending','Contacted','Interested','Joined','Backed Out','Not Responding','Declined'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Contacted By</label>
                  <select value={contactForm.contacted_by} onChange={e=>setContactForm(f=>({...f,contacted_by:e.target.value}))}>
                    <option value="">Select</option>
                    {ARMSTECH_STAFF.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
  
                {['Backed Out','Declined','Not Responding'].includes(contactForm.contact_status)&&(
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Reason</label>
                    <input value={contactForm.reason} onChange={e=>setContactForm(f=>({...f,reason:e.target.value}))} placeholder="Enter reason..."/>
                  </div>
                )}
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <textarea rows={2} value={contactForm.notes} onChange={e=>setContactForm(f=>({...f,notes:e.target.value}))} placeholder="Additional notes..."/>
                </div>

                {contactForm.contact_status==='Backed Out'&&selStudent.is_winner&&(
                  <div style={{gridColumn:'1/-1',background:'var(--red-light)',padding:8,borderRadius:6,fontSize:12,color:'var(--red)'}}>
                    ⚠️ This is the current winner. Saving will pass opportunity to next rank.
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowContact(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveContact} disabled={saving}>
                {saving?'Saving...':'Save Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Entry Modal */}
      {pushMany&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&!pushMany.running&&setPushMany(null)}>
          <div className="modal modal-lg" style={{maxWidth:860,maxHeight:'92vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">📤 Push many programs to Marketing CRM</span>
              <button className="btn btn-ghost btn-sm" disabled={pushMany.running} onClick={()=>setPushMany(null)}>✕</button>
            </div>
            <div className="modal-body">
              {!pushMany.result ? (<>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
                  Programs shown = your current filters (Year / Zone / University / College). Students go into CRM for each program's <b>exam year</b>; the college appears in CRM Admin Panel (assign staff there). Students already in CRM (same phone + college + year) are skipped.
                </div>
                {!pushMany.counts ? <div style={{padding:30,textAlign:'center'}}>Counting students…</div> : (<>
                  <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setPushMany(m=>({...m,sel:new Set(filteredProgs.filter(p=>m.counts[p.id]>0).map(p=>p.id))}))}>Select all with students</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setPushMany(m=>({...m,sel:new Set()}))}>Clear</button>
                    <span style={{fontSize:12,color:'var(--text3)'}}>{pushMany.sel.size} programs · {filteredProgs.filter(p=>pushMany.sel.has(p.id)).reduce((a,p)=>a+(pushMany.counts[p.id]||0),0)} students</span>
                  </div>
                  <div className="table-wrap" style={{maxHeight:'52vh',overflowY:'auto'}}>
                    <table>
                      <thead><tr><th></th><th>College</th><th>Stream</th><th>Year</th><th>Students</th></tr></thead>
                      <tbody>
                        {filteredProgs.map(p=>(
                          <tr key={p.id} style={{opacity:pushMany.counts[p.id]?1:0.45}}>
                            <td><input type="checkbox" disabled={!pushMany.counts[p.id]} checked={pushMany.sel.has(p.id)} onChange={()=>setPushMany(m=>{const n=new Set(m.sel); n.has(p.id)?n.delete(p.id):n.add(p.id); return {...m,sel:n};})}/></td>
                            <td>{colleges.find(c=>c.id===p.college_id)?.college_name||'?'}</td>
                            <td>{p.stream}</td><td>{p.year}</td><td>{pushMany.counts[p.id]||0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
                    <button className="btn btn-ghost" disabled={pushMany.running} onClick={()=>setPushMany(null)}>Cancel</button>
                    <button className="btn btn-primary" disabled={pushMany.running||!pushMany.sel.size} onClick={runPushMany}>{pushMany.running?('Pushing… '+pushMany.prog):'📤 Push selected to CRM'}</button>
                  </div>
                </>)}
              </>) : (<>
                <div style={{fontSize:14,fontWeight:700,marginBottom:8}}>
                  ✅ {pushMany.result.summary.reduce((a,r)=>a+r.pushed,0)} students pushed · {pushMany.result.summary.reduce((a,r)=>a+r.updated,0)} updated · {pushMany.result.summary.reduce((a,r)=>a+r.skipped+r.noContact,0)} unchanged/skipped
                  {pushMany.result.collegesCreated>0&&<> · {pushMany.result.collegesCreated} new college entries added to CRM Admin Panel (please assign staff)</>}
                </div>
                <div className="table-wrap" style={{maxHeight:'55vh',overflowY:'auto'}}>
                  <table>
                    <thead><tr><th>College</th><th>Stream</th><th>Year</th><th>Pushed</th><th>Updated (remarks/winner)</th><th>Already in CRM, no change</th><th>No phone &amp; email</th><th>Error</th></tr></thead>
                    <tbody>
                      {pushMany.result.summary.map((r,i)=>(
                        <tr key={i}><td>{r.college}</td><td>{r.stream}</td><td>{r.year}</td><td>{r.pushed}</td><td>{r.updated}</td><td>{r.skipped}</td><td>{r.noContact}</td><td style={{color:'var(--red)'}}>{r.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}><button className="btn btn-primary" onClick={()=>setPushMany(null)}>Close</button></div>
              </>)}
            </div>
          </div>
        </div>
      )}
      {showBulkEntry&&(
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowBulkEntry(false);}}>
          <div className="modal" style={{width:'90vw',maxWidth:900,maxHeight:'85vh',overflow:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">📋 Bulk Student Entry — {selProg?.college_name||''}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkEntry(false)}>✕</button>
            </div>
            <div style={{padding:'12px 16px'}}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'var(--accent-light)'}}>
                    <th style={{padding:'6px 8px',width:30}}>#</th>
                    <th style={{padding:'6px 8px',minWidth:180}}>Student Name *</th>
                    <th style={{padding:'6px 8px',minWidth:120}}>Phone</th>
                    <th style={{padding:'6px 8px',minWidth:100}}>Marks</th>
                    <th style={{padding:'6px 8px',minWidth:100}}>Stream</th>
                    {selProg?.num_batches>1&&<th style={{padding:'6px 8px',minWidth:80}}>Batch</th>}
                    <th style={{padding:'6px 8px',minWidth:160}}>Email</th>
                    <th style={{padding:'6px 8px',minWidth:140}}>Remarks</th>
                    <th style={{padding:'6px 8px',width:40}}></th>
                  </tr></thead>
                  <tbody>
                  {bulkRows.map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={{padding:'4px 8px',color:'var(--text3)',textAlign:'center'}}>{i+1}</td>
                      <td style={{padding:'4px 6px'}}><input value={r.student_name} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],student_name:e.target.value};setBulkRows(nr);}} placeholder="Student name" style={{width:'100%',fontSize:12,padding:'4px 6px'}}/></td>
                      <td style={{padding:'4px 6px'}}><input value={r.phone} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],phone:e.target.value};setBulkRows(nr);}} placeholder="Phone" style={{width:'100%',fontSize:12,padding:'4px 6px'}}/></td>
                      <td style={{padding:'4px 6px'}}><input type="number" value={r.marks} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],marks:e.target.value};setBulkRows(nr);}} placeholder="Marks" style={{width:'100%',fontSize:12,padding:'4px 6px'}}/></td>
                      <td style={{padding:'4px 6px'}}><select value={r.stream} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],stream:e.target.value};setBulkRows(nr);}} style={{fontSize:12,padding:'4px 6px',width:'100%'}}><option value="">Select</option><option>Mechanical</option><option>Civil</option><option>Electrical</option></select></td>
                      {selProg?.num_batches>1&&<td style={{padding:'4px 6px'}}><select value={r.batch_label} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],batch_label:e.target.value};setBulkRows(nr);}} style={{fontSize:12,padding:'4px 6px',width:'100%'}}><option value="">—</option><option>A</option><option>B</option><option>C</option></select></td>}
                      <td style={{padding:'4px 6px'}}><input value={r.email} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],email:e.target.value};setBulkRows(nr);}} placeholder="Email" style={{width:'100%',fontSize:12,padding:'4px 6px'}}/></td>
                      <td style={{padding:'4px 6px'}}><input value={r.remarks||''} onChange={e=>{const nr=[...bulkRows];nr[i]={...nr[i],remarks:e.target.value};setBulkRows(nr);}} placeholder="Remarks" style={{width:'100%',fontSize:12,padding:'4px 6px'}}/></td>
                      <td style={{padding:'4px 6px',textAlign:'center'}}><button className="btn btn-ghost btn-sm" style={{color:'var(--red)',fontSize:10}} onClick={()=>{const nr=[...bulkRows];nr.splice(i,1);setBulkRows(nr);}}>✕</button></td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:'flex',gap:8,marginTop:12,alignItems:'center'}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setBulkRows(r=>[...r,{student_name:'',phone:'',email:'',stream:selProg?.stream||'',marks:'',batch_label:selProg?.num_batches>1?selBatch:'',remarks:''}])}>+ Add Row</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setBulkRows(r=>[...r,...Array.from({length:5},()=>({student_name:'',phone:'',email:'',stream:selProg?.stream||'',marks:'',batch_label:selProg?.num_batches>1?selBatch:'',remarks:'',remarks:''}))])}>+ 5 Rows</button>
                <span style={{fontSize:11,color:'var(--text3)'}}>{bulkRows.filter(r=>r.student_name.trim()).length} students ready to save</span>
                <button className="btn btn-primary" style={{marginLeft:'auto'}} disabled={bulkSavingState||!bulkRows.some(r=>r.student_name.trim())} onClick={saveBulkStudents}>{bulkSavingState?'Saving...':'💾 Save All'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Winner Modal */}
      {showNoWinner&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowNoWinner(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">🚫 Mark No Winner</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowNoWinner(false)}>✕</button></div>
            <div className="modal-body">
              <div style={{marginBottom:12,fontSize:13,color:'var(--text2)'}}>
                Mark <strong>{selProg?.stream}</strong> as having no winner. This means no student from this college/stream joined the program.
              </div>
              <div className="form-group">
                <label>Reason</label>
                <select value={noWinnerReason} onChange={e=>setNoWinnerReason(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                  <option value="">Select reason</option>
                  {['No students interested','All top rankers not interested','Students not reachable','College not cooperating','Other'].map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowNoWinner(false)}>Cancel</button>
              <button className="btn btn-ghost btn-sm" style={{color:'var(--red)',border:'1px solid var(--red)'}} onClick={async()=>{
                await supabase.from('nactet_programs').update({no_winner:true,no_winner_reason:noWinnerReason}).eq('id',selProgram);
                toast.success('Marked as No Winner');
                setShowNoWinner(false);
                loadStudents(selProgram);
              }}>Confirm No Winner</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// WINNERS TAB
// ════════════════════════════════════════════════════════════════
function WinnersTab({
  colleges, programs, isEditor , userDefaultZone=''}) {
  const { canEdit } = useAuth();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fYear, setFYear] = useState('2027');
  const [fCollege, setFCollege] = useState('');
  const [fZone, setFZone] = useState(userDefaultZone);
  const [fUniv, setFUniv] = useState('');
  const [fJoinStatus, setFJoinStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nactetStudents, setNactetStudents] = useState([]);
  const [showWinContact, setShowWinContact] = useState(false);
  const [selWinner, setSelWinner] = useState(null);
  const [winContacts, setWinContacts] = useState([]);
  const [winContactForm, setWinContactForm] = useState({contact_date:new Date().toISOString().split('T')[0],contact_status:'Contacted',reason:'',contacted_by:'',notes:'',provisional_cert_given:false,main_cert_given:false});
  const [erpStudents, setErpStudents] = useState([]);

  const emptyWin = {
    student_id:'', college_id:'', program_id:'', year: '2027', stream:'',
    join_status:'', not_joined_reason:'', followup_date:'', followup_notes:'',
    joined_batch:'', joined_package:'', student_profile_id:'', training_status:'',
    college_feedback_given:false, college_feedback_notes:'', remarks:'',
    is_actual_winner:false
  };
  const [form, setForm] = useState(emptyWin);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => { loadWinners('2027','',''); }, []);

  useLiveRefresh(['nactet_winners', 'nactet_students'], () => loadWinnersFiltered(true), true, 1500);

  function loadWinnersFiltered(silent) {
    const zoneIds = (fZone||fUniv) && !fCollege
      ? colleges.filter(c=>(!fZone||c.zone===fZone)&&(!fUniv||c.university===fUniv)).map(c=>c.id)
      : null;
    loadWinners(fYear, fCollege, fJoinStatus, zoneIds, silent === true);
  }

  async function loadWinners(year, college, joinStatus, zoneCollegeIds, silent) {
    const y = year !== undefined ? year : fYear;
    const col = college !== undefined ? college : fCollege;
    const js = joinStatus !== undefined ? joinStatus : fJoinStatus;
    if (silent !== true) setLoading(true);

    // Query nactet_winners with joins
    let q = supabase.from('nactet_winners')
      .select(`*,
        nactet_students(student_name, marks, rank, reexam_marks, final_rank,
          is_final_winner, main_cert_given, provisional_cert_given, phone),
        nactet_colleges(college_name, district, zone),
        nactet_programs(year, stream, exam_mode)
      `)
      .order('created_at', { ascending: false });

    if (y) q = q.eq('year', Number(y));
    if (col && col !== '__zone__') q = q.eq('college_id', col);
    else if (zoneCollegeIds && zoneCollegeIds.length) q = q.in('college_id', zoneCollegeIds);
    if (js) q = q.eq('join_status', js);

    const { data, error } = await q;
    if (error) { toast.error(error.message); setLoading(false); return; }

    // Get contact history
    const studentIds = (data||[]).map(w=>w.student_id).filter(Boolean);
    let contacts = [];
    if (studentIds.length > 0) {
      const { data: cdata } = await supabase.from('nactet_rank_contacts')
        .select('*').in('student_id', studentIds).order('created_at',{ascending:false});
      contacts = cdata||[];
    }

    // Enrich with joined data
    const enriched = (data||[]).map(w=>({
      ...w,
      student_name: w.nactet_students?.student_name,
      marks: w.nactet_students?.marks,
      rank: w.nactet_students?.rank,
      reexam_marks: w.nactet_students?.reexam_marks,
      final_rank: w.nactet_students?.final_rank,
      is_final_winner: w.nactet_students?.is_final_winner,
      main_cert_given: w.nactet_students?.main_cert_given,
      provisional_cert_given: w.nactet_students?.provisional_cert_given,
      phone: w.nactet_students?.phone,
      college_name: w.nactet_colleges?.college_name,
      district: w.nactet_colleges?.district,
      stream: w.stream || w.nactet_programs?.stream,
      contacts: contacts.filter(c=>c.student_id===w.student_id),
    }));

    setWinners(enriched);

    // Load nactet students for modal
    const { data: stds } = await supabase.from('nactet_students').select('id,student_name,program_id,stream,is_winner').order('rank');
    setNactetStudents(stds||[]);

    // Load ERP students
    const { data: erp } = await supabase.from('students').select('id,full_name,register_number,batch_name').eq('is_active', true).order('full_name');
    setErpStudents(erp||[]);

    setLoading(false);
  }

  async function deleteWinContact(id, studentId) {
    if (!window.confirm('Delete this contact record?')) return;
    const { error } = await supabase.from('nactet_rank_contacts').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Contact deleted');
    const sid = studentId || selWinner?.student_id;
    if (sid) {
      const { data } = await supabase.from('nactet_rank_contacts')
        .select('*').eq('student_id', sid).order('created_at',{ascending:false});
      setWinContacts(data||[]);
    }
  }

  async function loadWinnerContacts(winner) {
    setSelWinner(winner);
    const { data } = await supabase.from('nactet_rank_contacts')
      .select('*').eq('student_id', winner.student_id)
      .order('contact_date', {ascending:false});
    setWinContacts(data||[]);
    setShowWinContact(true);
  }

  async function saveWinContact() {
    if (!selWinner) return;
    setSaving(true);
    const winPayload = {
      ...winContactForm,
      student_id: selWinner.student_id || null,
      college_id: selWinner.college_id || null,
      program_id: selWinner.program_id || null,
    };
    if (!winPayload.contact_date) winPayload.contact_date = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('nactet_rank_contacts').insert(winPayload);
    if (error) { toast.error(error.message); setSaving(false); return; }
    toast.success('Contact logged');
    setWinContactForm({contact_date:new Date().toISOString().split('T')[0],contact_status:'Contacted',reason:'',contacted_by:'',notes:'',provisional_cert_given:false,main_cert_given:false});
    loadWinnerContacts(selWinner);
    setSaving(false);
  }

  async function saveWinner() {
    setSaving(true);
    const payload = { ...form, year: Number(form.year) };
    // Remove joined data fields that don't belong in nactet_winners
    delete payload.nactet_students;
    delete payload.nactet_colleges;
    delete payload.nactet_programs;
    delete payload.contacts;
    delete payload.student_name;
    delete payload.marks;
    delete payload.phone;
    delete payload.college_name;
    delete payload.district;
    // Convert empty strings to null for UUID fields
    if (!payload.student_id) payload.student_id = null;
    if (!payload.college_id) payload.college_id = null;
    if (!payload.program_id) payload.program_id = null;
    if (!payload.student_profile_id) payload.student_profile_id = null;
    const { error } = editing
      ? await supabase.from('nactet_winners').update(payload).eq('id', editing)
      : await supabase.from('nactet_winners').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success('Saved'); setShowModal(false); setEditing(null); setForm(emptyWin); loadWinners(fYear, fCollege, fJoinStatus); }
    setSaving(false);
  }

  function clearFilters(){setFCollege('');setFZone('');setFUniv('');setFJoinStatus('');}

  return (
    <div>
      <div className="card" style={{marginBottom:12,display:'flex',gap:8,width:'fit-content',flexWrap:'wrap',alignItems:'center'}}>
        <select value={fYear} onChange={e=>setFYear(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Years</option>
          {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select value={fZone} onChange={e=>{setFZone(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Zones</option>
          {[...new Set(colleges.map(c=>c.zone).filter(Boolean))].sort().map(z=><option key={z} value={z}>{z}</option>)}
        </select>
        <select value={fUniv} onChange={e=>{setFUniv(e.target.value);setFCollege('');}} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Universities</option>
          {[...new Set(colleges.map(c=>c.university).filter(Boolean))].sort().map(u=><option key={u} value={u}>{u}</option>)}
        </select>
        <SearchableSelect value={fCollege} onChange={v=>setFCollege(v||'')}
          options={collegeFilterOptions(colleges,programs,fYear,fZone)}
          placeholder="All Colleges" searchPlaceholder="Search..." style={{minWidth:200}}/>
        <select value={fJoinStatus} onChange={e=>setFJoinStatus(e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
          <option value="">All Status</option>
          {JOIN_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={()=>clearFilters()} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',whiteSpace:'nowrap'}}>✕ Clear</button>
        <button className="btn btn-primary btn-sm" onClick={()=>loadWinnersFiltered()} style={{minWidth:80}}>
          🔍 Search
        </button>
        {canEdit('nactet') && <button className="btn btn-primary btn-sm" onClick={()=>{
          setEditing(null);
          const baseForm = {...emptyWin, year: fYear||'2027', college_id: fCollege||''};
          // If college and year both selected, try to auto-fill program and student
          if (fCollege && fYear) {
            const matchingProgs = programs.filter(p=>p.college_id===fCollege && String(p.year)===String(fYear));
            if (matchingProgs.length===1) {
              const prog = matchingProgs[0];
              baseForm.program_id = prog.id;
              baseForm.stream = prog.stream;
              // Auto-select winner student from this program
              const progStudents = nactetStudents.filter(s=>s.program_id===prog.id && s.is_winner);
              if (progStudents.length===1) baseForm.student_id = progStudents[0].id;
            }
          }
          setForm(baseForm);
          setShowModal(true);
        }}>+ Add Winner</button>}
        <span style={{fontSize:12,color:'var(--text3)'}}>{winners.length} winners</span>
      </div>

      {/* Summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:12}}>
        {JOIN_STATUS.map(s=>{
          const count = winners.filter(w=>w.join_status===s).length;
          return (
            <div key={s} className="card" style={{textAlign:'center',padding:10,cursor:count>0?'pointer':'default',
              border:fJoinStatus===s?'2px solid var(--accent)':'2px solid transparent'}}
              onClick={()=>{if(count>0){setFJoinStatus(fJoinStatus===s?'':s);}}}>
              <div style={{fontSize:18,fontWeight:700,color:s==='Joined'?'var(--green)':s==='Not Joined'?'var(--red)':s==='Interested'?'var(--amber)':'var(--blue)'}}>{count}</div>
              <div style={{fontSize:11,color:'var(--text3)'}}>{s}</div>
              {count>0&&<div style={{fontSize:9,color:'var(--accent)',marginTop:2}}>{fJoinStatus===s?'✕ Clear':'Click to filter'}</div>}
            </div>
          );
        })}
      </div>

      <div className="card" style={{padding:0}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--accent-light)'}}>
              {['College','Year','Stream','Batch','Winner','Last Contact','Rank',''].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(fJoinStatus ? winners.filter(w=>w.join_status===fJoinStatus) : winners).map((w,i)=>{
              const lastContact = (w.contacts||[])[0];
              return (
                <tr key={w.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                  <td style={{padding:'8px 10px',fontWeight:500,whiteSpace:'nowrap'}}>
                    {w.college_name || colleges.find(c=>c.id===w.nactet_programs?.college_id)?.college_name || '—'}
                    <div style={{fontSize:10,color:'var(--text3)'}}>{w.district}</div>
                  </td>
                  <td style={{padding:'8px 10px'}}>{w.year || w.nactet_programs?.year}</td>
                  <td style={{padding:'8px 10px'}}><span className={`badge ${(w.stream||w.nactet_programs?.stream)==='Mechanical'?'badge-blue':'badge-green'}`} style={{fontSize:9}}>{w.stream||w.nactet_programs?.stream}</span></td>
                  <td style={{padding:'8px 10px',textAlign:'center'}}>
                    {w.batch_label?<span className="badge badge-blue">{w.batch_label}</span>:'—'}
                  </td>
                  <td style={{padding:'8px 10px',fontWeight:500}}>
                    {w.student_name} 🏆
                    {w.is_final_winner && <span style={{fontSize:10,color:'var(--green)',display:'block'}}>★ Final Winner</span>}
                    {w.is_actual_winner
                      ? <span style={{fontSize:10,color:'var(--accent)',display:'block',fontWeight:700}}>✅ Actual Winner</span>
                      : <span style={{fontSize:9,color:'var(--red)',display:'block'}}>⚠️ Not confirmed</span>}

                    <div style={{fontSize:10,color:'var(--accent)',fontWeight:600}}>Marks: {w.marks||'—'} {w.reexam_marks&&`| Re: ${w.reexam_marks}`}</div>
                    <div style={{display:'flex',gap:4,marginTop:2}}>
                      {w.main_cert_given&&<span style={{fontSize:9,background:'var(--green-light)',color:'var(--green)',padding:'1px 4px',borderRadius:4}}>📜 Main</span>}
                      {w.provisional_cert_given&&!w.main_cert_given&&<span style={{fontSize:9,background:'var(--amber-light)',color:'var(--amber)',padding:'1px 4px',borderRadius:4}}>📄 Prov</span>}
                    </div>
                  </td>
                  <td style={{padding:'8px 10px'}}>
                    {lastContact ? (
                      <span style={{padding:'2px 6px',borderRadius:8,fontSize:11,
                        background:lastContact.contact_status==='Joined'?'var(--green-light)':lastContact.contact_status==='Backed Out'?'var(--red-light)':'var(--amber-light)',
                        color:lastContact.contact_status==='Joined'?'var(--green)':lastContact.contact_status==='Backed Out'?'var(--red)':'var(--amber)'}}>
                        {lastContact.contact_status}
                      </span>
                    ) : <span style={{fontSize:11,color:'var(--text3)'}}>Not contacted</span>}
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>{(w.contacts||[]).length} contact(s)</div>
                  </td>
                  <td style={{padding:'8px 10px',fontSize:11}}>{w.rank||'—'} {w.final_rank&&`→ ${w.final_rank}`}</td>
                  <td style={{padding:'8px 10px'}}>
                    {canEdit('nactet') && <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(w.id);setForm({...w,year:w.year?.toString()});setShowModal(true);}}>Edit</button>}
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--accent)'}} onClick={()=>loadWinnerContacts(w)}>📞</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {winners.length===0 && !loading && <div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No winners found</div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal" style={{maxWidth:500,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Winner Tracking</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>College</label>
                  <SearchableSelect value={form.college_id||''} onChange={v=>sf('college_id',v||'')}
                    options={colleges.filter(c=>c.is_active&&c.college_status!=='Shutdown').map(c=>({value:c.id,label:c.college_name}))}
                    placeholder="Select college" searchPlaceholder="Search..."/>
                </div>
                <div className="form-group">
                  <label>Program</label>
                  <select value={form.program_id||''} onChange={e=>{
                    const pid = e.target.value;
                    const prog = programs.find(p=>p.id===pid);
                    sf('program_id', pid);
                    if (prog) {
                      sf('stream', prog.stream);
                      sf('year', String(prog.year));
                      // Auto-select winner if only one
                      const progStudents = nactetStudents.filter(s=>s.program_id===pid && s.is_winner);
                      if (progStudents.length===1) sf('student_id', progStudents[0].id);
                      else sf('student_id', '');
                    }
                  }}>
                    <option value="">Select Program</option>
                    {programs
                      .filter(p=>(!form.college_id||p.college_id===form.college_id) &&
                                 (!form.year||String(p.year)===String(form.year)))
                      .map(p=>{
                        const col = colleges.find(c=>c.id===p.college_id);
                        return <option key={p.id} value={p.id}>{col?.college_name} — {p.stream} {p.year}</option>;
                      })}
                  </select>
                </div>
                <div className="form-group">
                  <label>Student (Winner) — from Students tab</label>
                  <select value={form.student_id||''} onChange={e=>sf('student_id',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select Student</option>
                    {nactetStudents
                      .filter(s=>!form.program_id||s.program_id===form.program_id)
                      .map(s=><option key={s.id} value={s.id}>{s.is_winner?'🏆 ':''}{s.student_name} ({s.stream})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Year</label>
                  <select value={form.year} onChange={e=>sf('year',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stream</label>
                  <select value={form.stream||''} onChange={e=>sf('stream',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div style={{gridColumn:'1/-1',fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4,marginTop:4}}>🎯 Joining Status</div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Join Status</label>
                  <select value={form.join_status||''} onChange={e=>sf('join_status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                    <option value="">Select</option>
                    {JOIN_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {form.join_status==='Not Joined' && (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Reason Not Joined</label>
                    <textarea value={form.not_joined_reason||''} onChange={e=>sf('not_joined_reason',e.target.value)} rows={2}/>
                  </div>
                )}
                {(form.join_status==='Interested'||form.join_status==='Follow Up') && (<>
                  <div className="form-group">
                    <label>Follow-up Date</label>
                    <DateInput value={form.followup_date||''} onChange={e=>sf('followup_date',e.target.value)}/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Follow-up Notes</label>
                    <textarea value={form.followup_notes||''} onChange={e=>sf('followup_notes',e.target.value)} rows={2}/>
                  </div>
                </>)}
                {form.join_status==='Joined' && (<>
                  <div style={{gridColumn:'1/-1',fontWeight:600,fontSize:12,color:'var(--green)',borderBottom:'1px solid var(--border)',paddingBottom:4,marginTop:4}}>✅ Training Details</div>
                  <div className="form-group">
                    <label>Joined Batch</label>
                    <input value={form.joined_batch||''} onChange={e=>sf('joined_batch',e.target.value)} placeholder="e.g. EM48"/>
                  </div>
                  <div className="form-group">
                    <label>Package</label>
                    <select value={form.joined_package||''} onChange={e=>sf('joined_package',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                      <option value="">Select</option>
                      {PACKAGES.map(p=><option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Training Status</label>
                    <select value={form.training_status||''} onChange={e=>sf('training_status',e.target.value)} style={{fontSize:12,padding:'3px 6px',maxWidth:130}}>
                      <option value="">Select</option>
                      {TRAIN_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Link to ERP Student Profile</label>
                    <SearchableSelect value={form.student_profile_id||''} onChange={v=>sf('student_profile_id',v||'')}
                      options={erpStudents.map(s=>({value:s.id,label:s.register_number+' — '+s.full_name+' ('+s.batch_name+')'}))}
                      placeholder="Search ERP student..." searchPlaceholder="Search by name or reg no..."/>
                  </div>
                </>)}

                <div style={{gridColumn:'1/-1',fontWeight:600,fontSize:12,color:'var(--accent)',borderBottom:'1px solid var(--border)',paddingBottom:4,marginTop:4}}>🏫 College Feedback</div>
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8,gridColumn:'1/-1'}}>
                  <input type="checkbox" id="fbGiven" checked={form.college_feedback_given||false} onChange={e=>sf('college_feedback_given',e.target.checked)}/>
                  <label htmlFor="fbGiven" style={{marginBottom:0}}>Feedback given to college coordinator</label>
                </div>
                {form.college_feedback_given && (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Feedback Notes</label>
                    <textarea value={form.college_feedback_notes||''} onChange={e=>sf('college_feedback_notes',e.target.value)} rows={2}/>
                  </div>
                )}
              </div>
              <div style={{marginTop:10,borderTop:'1px solid var(--border)',paddingTop:10}}>
                <div className="form-group" style={{gridColumn:'1/-1',background:'rgba(34,197,94,0.08)',padding:10,borderRadius:8,border:'2px solid rgba(34,197,94,0.3)'}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontWeight:700,fontSize:13}}>
                    <input type="checkbox" checked={form.is_actual_winner||false} onChange={e=>sf('is_actual_winner',e.target.checked)}/>
                    ★ Confirmed Actual Winner
                  </label>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Tick to confirm this is the real final winner for this college/stream</div>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea value={form.remarks||''} onChange={e=>sf('remarks',e.target.value)} rows={2} placeholder="Any additional details about this winner..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={saving} onClick={saveWinner}>
                {saving?'Saving...':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Winner Contact Modal */}
      {showWinContact && selWinner && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowWinContact(false)}>
          <div className="modal" style={{maxWidth:520,maxHeight:'90vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">📞 {selWinner.student_name} — Contact History</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowWinContact(false)}>✕</button>
            </div>
            <div className="modal-body">
              {winContacts.length>0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontWeight:600,fontSize:12,marginBottom:6}}>Contact History ({winContacts.length}):</div>
                  {winContacts.map(c=>(
                    <div key={c.id} style={{padding:'6px 10px',background:'var(--surface2)',borderRadius:6,marginBottom:4,fontSize:12}}>
                      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                        <span style={{fontWeight:600}}>{fmtDMY(c.contact_date)}</span>
                        <span style={{padding:'1px 6px',borderRadius:8,fontSize:11,
                          background:c.contact_status==='Joined'?'var(--green-light)':c.contact_status==='Backed Out'?'var(--red-light)':'var(--amber-light)',
                          color:c.contact_status==='Joined'?'var(--green)':c.contact_status==='Backed Out'?'var(--red)':'var(--amber)'}}>
                          {c.contact_status}
                        </span>
                        {c.contacted_by&&<span style={{color:'var(--text3)',fontSize:11}}>by {c.contacted_by}</span>}
                        <button className="btn btn-ghost btn-sm" style={{fontSize:10,color:'var(--red)',padding:'1px 6px'}}
                          onClick={()=>deleteWinContact(c.id, c.student_id)}>🗑</button>
                      </div>
                      {c.reason&&<div style={{color:'var(--text3)',fontSize:11,marginTop:2}}>Reason: {c.reason}</div>}
                      {c.notes&&<div style={{color:'var(--text3)',fontSize:11}}>Notes: {c.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontWeight:600,fontSize:12,marginBottom:8,borderTop:'1px solid var(--border)',paddingTop:10}}>Add New Contact:</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group">
                  <label>Date</label>
                  <DateInput value={winContactForm.contact_date} onChange={e=>setWinContactForm(f=>({...f,contact_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={winContactForm.contact_status} onChange={e=>setWinContactForm(f=>({...f,contact_status:e.target.value}))}>
                    {['Pending','Contacted','Interested','Joined','Backed Out','Not Responding','Declined'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Contacted By</label>
                  <select value={winContactForm.contacted_by} onChange={e=>setWinContactForm(f=>({...f,contacted_by:e.target.value}))}>
                    <option value="">Select</option>
                    {ARMSTECH_STAFF.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {['Backed Out','Declined','Not Responding'].includes(winContactForm.contact_status)&&(
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Reason</label>
                    <input value={winContactForm.reason} onChange={e=>setWinContactForm(f=>({...f,reason:e.target.value}))} placeholder="Enter reason..."/>
                  </div>
                )}
                {winContactForm.contact_status==='Joined'&&(<>
                    <div style={{fontWeight:600,fontSize:12,color:'var(--green)',marginBottom:8}}>✅ Joined — Update Winner Record</div>
                    <div style={{fontSize:12,color:'var(--text3)'}}>Go to Edit Winner to update batch, package and training details.</div>
                </>)}
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <textarea rows={2} value={winContactForm.notes} onChange={e=>setWinContactForm(f=>({...f,notes:e.target.value}))} placeholder="Additional notes..."/>
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowWinContact(false)}>Close</button>
              <button className="btn btn-primary" onClick={saveWinContact} disabled={saving}>
                {saving?'Saving...':'Save Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// FOLLOW-UP TAB
// ════════════════════════════════════════════════════════════════
function FollowUpTab({ colleges, programs }) {
  const [winners, setWinners] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [daysAhead, setDaysAhead] = React.useState(7);

  React.useEffect(() => { loadFollowUps(); }, [daysAhead]);
  useLiveRefresh(['nactet_winners'], () => loadFollowUps(true));

  async function loadFollowUps(silent) {
    if (silent !== true) setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const future = new Date(Date.now() + daysAhead*86400000).toISOString().split('T')[0];

    // Winner follow-ups
    const { data: wData } = await supabase.from('nactet_winners')
      .select('*,nactet_colleges(college_name),nactet_students(student_name,phone)')
      .not('followup_date','is',null)
      .gte('followup_date', today)
      .lte('followup_date', future)
      .order('followup_date');
    setWinners(wData||[]);
    setLoading(false);
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div>
      <NoteReminders colleges={colleges}/>
      <div className="card" style={{marginBottom:12,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:14}}>🔔 Follow-up Reminders</span>
        <select value={daysAhead} onChange={e=>setDaysAhead(Number(e.target.value))}
          style={{fontSize:12,fontWeight:600,color:'var(--accent)'}}>
          <option value={3}>Next 3 days</option>
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
          <option value={30}>Next 30 days</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={loadFollowUps}>🔄 Refresh</button>
        <span style={{fontSize:12,color:'var(--text3)'}}>{winners.length} follow-ups pending</span>
      </div>

      {winners.length===0 && !loading && (
        <div className="card" style={{textAlign:'center',padding:32,color:'var(--text3)'}}>
          ✅ No follow-ups due in the next {daysAhead} days
        </div>
      )}

      {winners.length>0 && (
        <div className="card" style={{padding:0}}>
          <div style={{padding:'8px 12px',fontWeight:600,fontSize:13,borderBottom:'1px solid var(--border)',background:'var(--accent-light)'}}>
            🏆 Winner Follow-ups
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--surface2)'}}>
                {['Follow-up Date','College','Winner','Phone','Join Status','Notes'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {winners.map((w,i)=>(
                <tr key={w.id} style={{borderBottom:'1px solid var(--border)',
                  background:w.followup_date===today?'rgba(245,158,11,0.08)':i%2===0?'':'var(--surface2)'}}>
                  <td style={{padding:'8px 10px',fontWeight:600,
                    color:w.followup_date===today?'var(--amber)':w.followup_date<today?'var(--red)':'var(--text)'}}>
                    {w.followup_date===today?'⚠️ Today':w.followup_date}
                  </td>
                  <td style={{padding:'8px 10px'}}>{w.nactet_colleges?.college_name||'—'}</td>
                  <td style={{padding:'8px 10px',fontWeight:500}}>{w.nactet_students?.student_name||'—'}</td>
                  <td style={{padding:'8px 10px'}}>{w.nactet_students?.phone||'—'}</td>
                  <td style={{padding:'8px 10px'}}>
                    <span style={{padding:'2px 8px',borderRadius:8,fontSize:11,
                      background:w.join_status==='Joined'?'var(--green-light)':w.join_status==='Not Joined'?'var(--red-light)':'var(--amber-light)',
                      color:w.join_status==='Joined'?'var(--green)':w.join_status==='Not Joined'?'var(--red)':'var(--amber)'}}>
                      {w.join_status||'Pending'}
                    </span>
                  </td>
                  <td style={{padding:'8px 10px',color:'var(--text3)'}}>{w.followup_notes||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// REPORTS TAB
// ════════════════════════════════════════════════════════════════
// College Notes report (Reports tab): upcoming batch availability, class strengths, open reminders
function NotesReport({ colleges }) {
  const [rows, setRows] = useState([]);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fType, setFType] = useState('');
  const [fStream, setFStream] = useState('');
  const [fYear, setFYear] = useState('');
  const [fZone, setFZone] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(true);

  useEffect(() => {
    supabase.from('nactet_college_notes').select('*').order('created_at', { ascending:false }).limit(5000)
      .then(({ data, error }) => { if (error) setMissing(true); else setRows(data||[]); setLoading(false); });
  }, []);

  const today = todayISO();
  const colOf = id => colleges.find(c=>String(c.id)===String(id));
  const statusOf = n => !n.remind_on ? 'No reminder' : n.reminder_status==='Done' ? 'Done' : n.remind_on<=today ? 'Due' : 'Upcoming';
  const filtered = rows.filter(n => {
    const c = colOf(n.college_id);
    const q = search.toLowerCase();
    return (!fType || n.note_type===fType) && (!fStream || n.stream===fStream)
      && (!fYear || String(n.batch_year)===fYear) && (!fZone || c?.zone===fZone)
      && (!fStatus || statusOf(n)===fStatus)
      && (!q || (c?.college_name||'').toLowerCase().includes(q) || (n.note||'').toLowerCase().includes(q));
  });
  const years = [...new Set(rows.map(n=>n.batch_year).filter(Boolean))].sort();
  const zones = [...new Set(colleges.map(c=>c.zone).filter(Boolean))].sort();
  const counts = { Due: rows.filter(n=>statusOf(n)==='Due').length, Upcoming: rows.filter(n=>statusOf(n)==='Upcoming').length, Done: rows.filter(n=>statusOf(n)==='Done').length };
  // batch availability pivot: year -> stream -> number of colleges
  const avail = rows.filter(n=>n.note_type==='Batch Availability' && n.batch_year);
  const availYears = [...new Set(avail.map(n=>n.batch_year))].sort();
  const availStreams = [...new Set(avail.map(n=>n.stream||'All streams'))].sort();

  function exportNotes() {
    exportCSV(filtered.map(n => { const c = colOf(n.college_id); return {
      College: c?.college_name||'', Zone: c?.zone||'', University: c?.university||'', Type: n.note_type||'', Stream: n.stream||'',
      Year: n.batch_year||'', 'Class Strength': n.class_strength??'', Note: n.note||'', 'Remind On': n.remind_on||'',
      Status: statusOf(n), 'Added By': n.created_by||'', 'Added On': (n.created_at||'').slice(0,10),
    }; }), 'NACTET_College_Notes.csv');
  }

  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',cursor:'pointer'}} onClick={()=>setOpen(o=>!o)}>
        <span style={{fontWeight:700,fontSize:14}}>🧠 College Notes & Reminders {open?'▾':'▸'}</span>
        {!missing && <span style={{fontSize:11,color:'var(--text3)'}}>{rows.length} notes · <b style={{color:'var(--red)'}}>{counts.Due} due</b> · {counts.Upcoming} upcoming · {counts.Done} done</span>}
      </div>
      {open && missing && <div style={{fontSize:12,color:'var(--amber)',marginTop:8}}>Notes table not set up yet — open any college in the Colleges tab to see the one-time setup SQL.</div>}
      {open && !missing && (loading ? <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>Loading…</div> : (
        <div style={{marginTop:10}}>
          {availYears.length>0 && (
            <div style={{overflowX:'auto',marginBottom:12}}>
              <div style={{fontWeight:600,fontSize:12,marginBottom:4}}>Final-year batch availability — colleges per year</div>
              <table style={{fontSize:11,borderCollapse:'collapse'}}>
                <thead><tr><th style={{textAlign:'left',padding:'4px 10px'}}>Stream</th>{availYears.map(y=><th key={y} style={{padding:'4px 10px'}}>{y}</th>)}</tr></thead>
                <tbody>{availStreams.map(s=>(
                  <tr key={s} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'4px 10px',fontWeight:600}}>{s}</td>
                    {availYears.map(y=>{ const k = new Set(avail.filter(n=>n.batch_year===y&&(n.stream||'All streams')===s).map(n=>n.college_id)).size;
                      return <td key={y} style={{padding:'4px 10px',textAlign:'center'}}>{k||'—'}</td>; })}
                  </tr>))}</tbody>
              </table>
            </div>
          )}
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8,alignItems:'center'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search college / note…" style={{minWidth:160}}/>
            <select value={fType} onChange={e=>setFType(e.target.value)} style={{fontSize:11}}><option value="">All Types</option>{NOTE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select>
            <select value={fStream} onChange={e=>setFStream(e.target.value)} style={{fontSize:11}}><option value="">All Streams</option>{STREAMS.map(s=><option key={s} value={s}>{s}</option>)}</select>
            <select value={fYear} onChange={e=>setFYear(e.target.value)} style={{fontSize:11}}><option value="">All Years</option>{years.map(y=><option key={y} value={y}>{y}</option>)}</select>
            <select value={fZone} onChange={e=>setFZone(e.target.value)} style={{fontSize:11}}><option value="">All Zones</option>{zones.map(z=><option key={z} value={z}>{z}</option>)}</select>
            <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:11}}><option value="">All Reminder Status</option>{['Due','Upcoming','Done','No reminder'].map(s=><option key={s} value={s}>{s}</option>)}</select>
            <button className="btn btn-primary btn-sm" onClick={exportNotes} disabled={!filtered.length}>📥 Export CSV</button>
            <span style={{fontSize:11,color:'var(--text3)'}}>{filtered.length} shown</span>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
              <thead><tr style={{textAlign:'left',color:'var(--text3)',fontSize:11}}>
                {['College','Zone','Type','Stream','Year','Strength','Note','Remind on','Status','By'].map(h=><th key={h} style={{padding:'6px 8px'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtered.length===0 && <tr><td colSpan={10} style={{padding:16,textAlign:'center',color:'var(--text3)'}}>No notes match.</td></tr>}
                {filtered.map(n=>{ const c = colOf(n.college_id); const st = statusOf(n); return (
                  <tr key={n.id} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'6px 8px',fontWeight:600}}>{c?.college_name||'—'}</td>
                    <td style={{padding:'6px 8px'}}>{c?.zone||'—'}</td>
                    <td style={{padding:'6px 8px'}}><span className={`badge ${NOTE_TYPE_BADGE[n.note_type]||'badge-gray'}`} style={{fontSize:9}}>{n.note_type}</span></td>
                    <td style={{padding:'6px 8px'}}>{n.stream||'—'}</td>
                    <td style={{padding:'6px 8px'}}>{n.batch_year||'—'}</td>
                    <td style={{padding:'6px 8px'}}>{n.class_strength??'—'}</td>
                    <td style={{padding:'6px 8px',maxWidth:260}}>{n.note||'—'}</td>
                    <td style={{padding:'6px 8px',whiteSpace:'nowrap'}}>{n.remind_on?fmtDate(n.remind_on):'—'}</td>
                    <td style={{padding:'6px 8px'}}><span className={`badge ${st==='Due'?'badge-red':st==='Upcoming'?'badge-amber':st==='Done'?'badge-green':'badge-gray'}`} style={{fontSize:9}}>{st}</span></td>
                    <td style={{padding:'6px 8px',color:'var(--text3)'}}>{n.created_by||'—'}</td>
                  </tr>); })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsTab({ colleges, programs, onRefresh }) {
  const [selYear, setSelYear] = React.useState('2027');
  const [expandedCollege, setExpandedCollege] = React.useState(null);
  const [showAllNew, setShowAllNew] = React.useState(false);
  const [showAllDropped, setShowAllDropped] = React.useState(false);
  const [showAllContinuing, setShowAllContinuing] = React.useState(false);
  const [winners, setWinners] = React.useState([]);
  const [allWinners, setAllWinners] = React.useState([]);
  const availYears = [...new Set(programs.map(p=>p.year?.toString()))].sort((a,b)=>b-a);

  React.useEffect(() => { loadWinnersAll(); }, []);
  React.useEffect(() => { loadWinnersYear(selYear); }, [selYear]);
  useLiveRefresh(['nactet_winners'], () => { loadWinnersAll(); loadWinnersYear(selYear); }, true, 2000);

  async function loadWinnersYear(year) {
    const { data } = await supabase.from('nactet_winners')
      .select('*,nactet_students(student_name,marks,rank,phone),nactet_colleges(college_name,district,zone)')
      .eq('year', Number(year));
    setWinners(data||[]);
  }

  async function loadWinnersAll() {
    const { data } = await supabase.from('nactet_winners')
      .select('year,join_status,college_id')
      .order('year');
    setAllWinners(data||[]);
  }

  async function autoRateColleges() {
    if (!window.confirm('Auto-calculate ratings for all colleges based on participation, attendance and winner joining rate?')) return;
    // Load all programs with attendance data
    const { data: allProgs } = await supabase.from('nactet_programs')
      .select('college_id,year,registered_students,students_attended');
    const { data: allWins } = await supabase.from('nactet_winners')
      .select('college_id,join_status');
    if (!allProgs) return;

    // Group by college
    const colMap = {};
    allProgs.forEach(p => {
      if (!colMap[p.college_id]) colMap[p.college_id] = {years:new Set(),attended:0,registered:0};
      colMap[p.college_id].years.add(p.year);
      const reg = p.registered_students||0;
      const att = Math.min(p.students_attended||0, reg);
      colMap[p.college_id].attended += att;
      colMap[p.college_id].registered += reg;
    });
    (allWins||[]).forEach(w => {
      if (!colMap[w.college_id]) return;
      if (!colMap[w.college_id].winners) colMap[w.college_id].winners = 0;
      if (!colMap[w.college_id].joined) colMap[w.college_id].joined = 0;
      colMap[w.college_id].winners++;
      if (w.join_status==='Joined') colMap[w.college_id].joined++;
    });

    // Calculate rating 1-5
    let updated = 0;
    for (const [colId, data] of Object.entries(colMap)) {
      const yearsCount = data.years.size;
      const attendRate = data.registered>0 ? data.attended/data.registered : 0;
      const joinRate = data.winners>0 ? (data.joined||0)/data.winners : 0;
      // Score: years(0-2) + attendance(0-2) + joining(0-1) = 0-5
      let score = 0;
      if (yearsCount >= 5) score += 2;
      else if (yearsCount >= 2) score += 1;
      if (attendRate >= 0.8) score += 2;
      else if (attendRate >= 0.5) score += 1;
      if (joinRate >= 0.5) score += 1;
      const rating = Math.max(1, Math.min(5, score+1));
      const { error } = await supabase.from('nactet_colleges')
        .update({college_rating: rating}).eq('id', colId);
      if (!error) updated++;
    }
    toast.success(`✅ Rated ${updated} colleges`);
    if (onRefresh) onRefresh();
  }

  // Year over year comparison
  const years = [...new Set(programs.map(p=>String(p.year)))].sort((a,b)=>b-a).slice(0,5);

  // Winner funnel data
  const funnelData = years.map(y=>{
    const yWinners = allWinners.filter(w=>String(w.year)===y);
    const uniqueCols = new Set(programs.filter(p=>String(p.year)===y).map(p=>p.college_id)).size;
    return {
      year: y,
      colleges: uniqueCols,
      programs: programs.filter(p=>String(p.year)===y).length,
      winners: yWinners.length,
      joined: yWinners.filter(w=>w.join_status==='Joined').length,
      interested: yWinners.filter(w=>w.join_status==='Interested').length,
      notJoined: yWinners.filter(w=>w.join_status==='Not Joined').length,
    };
  });

  // Year comparison: colleges in selYear vs previous year
  const prevYear = String(Number(selYear)-1);
  const thisYearColIds = new Set(programs.filter(p=>String(p.year)===selYear).map(p=>p.college_id));
  const prevYearColIds = new Set(programs.filter(p=>String(p.year)===prevYear).map(p=>p.college_id));
  const newColleges = [...thisYearColIds].filter(id=>!prevYearColIds.has(id));
  const droppedColleges = [...prevYearColIds].filter(id=>!thisYearColIds.has(id));
  const continuingColleges = [...thisYearColIds].filter(id=>prevYearColIds.has(id));

  // Export programs report
  function exportPrograms() {
    const rows = programs.filter(p=>String(p.year)===selYear).map(p=>{
      const col = colleges.find(c=>c.id===p.college_id);
      const winner = winners.find(w=>w.program_id===p.id);
      return {
        College: col?.college_name||'',
        District: col?.district||'',
        Zone: col?.zone||'',
        Year: p.year,
        Stream: p.stream,
        Batches: p.num_batches||'',
        Registered: p.registered_students||0,
        Attended: p.students_attended||0,
        Status: p.status||'',
        'Exam Mode': p.exam_mode||'',
        'Exam Date': p.actual_exam_date||p.proposed_exam_date||'',
        'Winner Name': winner?.nactet_students?.student_name||'',
        'Winner Marks': winner?.nactet_students?.marks||'',
        'Winner Phone': winner?.nactet_students?.phone||'',
        'Join Status': winner?.join_status||'',
        'Joined Batch': winner?.joined_batch||'',
      };
    });
    exportCSV(rows, `NACTET_Programs_${selYear}.csv`);
  }

  // Export winners report
  function exportWinners() {
    const rows = winners.map(w=>({
      College: w.nactet_colleges?.college_name||'',
      District: w.nactet_colleges?.district||'',
      Zone: w.nactet_colleges?.zone||'',
      Year: w.year,
      Stream: w.stream||'',
      'Winner Name': w.nactet_students?.student_name||'',
      'Marks': w.nactet_students?.marks||'',
      'Phone': w.nactet_students?.phone||'',
      'Join Status': w.join_status||'',
      'Joined Batch': w.joined_batch||'',
      'Package': w.joined_package||'',
      'Training Status': w.training_status||'',
      'Follow-up Date': w.followup_date||'',
      'Remarks': w.remarks||'',
    }));
    exportCSV(rows, `NACTET_Winners_${selYear}.csv`);
  }

  return (
    <div>
      <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
        <span style={{fontWeight:700,fontSize:14}}>📈 Reports & Analytics</span>
        <select value={selYear} onChange={e=>setSelYear(e.target.value)}
          style={{fontWeight:600,color:'var(--accent)'}}>
          {availYears.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={exportPrograms}>
          📥 Export Programs {selYear}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={exportWinners}>
          📥 Export Winners {selYear}
        </button>
        <button className="btn btn-ghost btn-sm" style={{color:'var(--amber)'}} onClick={autoRateColleges}>
          ⭐ Auto-Rate Colleges
        </button>
      </div>

      <NotesReport colleges={colleges}/>

      {/* Year-over-Year Table */}
      <div className="card" style={{marginBottom:12}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>📊 Year-over-Year Summary</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--accent-light)'}}>
                {['Year','Colleges','Programs','Winners','Joined','Interested','Not Joined','Join Rate'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:h==='Year'?'left':'center'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {funnelData.map((row,i)=>{
                const joinRate = row.winners>0?((row.joined/row.winners)*100).toFixed(0):0;
                return (
                  <tr key={row.year} style={{borderBottom:'1px solid var(--border)',
                    background:row.year===selYear?'var(--accent-light)':'',cursor:'pointer'}}
                    onClick={()=>setSelYear(row.year)}>
                    <td style={{padding:'8px 10px',fontWeight:700,color:'var(--accent)'}}>{row.year}</td>
                    <td style={{padding:'8px 10px',textAlign:'center'}}>{row.colleges}</td>
                    <td style={{padding:'8px 10px',textAlign:'center'}}>{row.programs}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',fontWeight:600}}>{row.winners}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{row.joined}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',color:'var(--amber)'}}>{row.interested}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',color:'var(--red)'}}>{row.notJoined}</td>
                    <td style={{padding:'8px 10px',textAlign:'center'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                        <div style={{width:50,height:6,background:'#e5e7eb',borderRadius:3,overflow:'hidden'}}>
                          <div style={{width:joinRate+'%',height:'100%',background:'var(--green)',borderRadius:3}}/>
                        </div>
                        <span style={{fontSize:11,fontWeight:600}}>{joinRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* College Comparison */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12}}>
        <div className="card">
          <div style={{fontWeight:600,fontSize:12,color:'var(--green)',marginBottom:8}}>
            ✅ New in {selYear} ({newColleges.length})
          </div>
          {(showAllNew ? newColleges : newColleges.slice(0,8)).map(id=>{
            const col = colleges.find(c=>c.id===id);
            return <div key={id} style={{fontSize:11,padding:'3px 0',borderBottom:'1px solid var(--border)'}}>{col?.college_name||id}</div>;
          })}
          {newColleges.length>8&&<div onClick={()=>setShowAllNew(v=>!v)} style={{fontSize:11,color:'var(--accent)',marginTop:4,cursor:'pointer'}}>{showAllNew?'▲ Show less':`+${newColleges.length-8} more`}</div>}
          {newColleges.length===0&&<div style={{fontSize:11,color:'var(--text3)'}}>None</div>}
        </div>
        <div className="card">
          <div style={{fontWeight:600,fontSize:12,color:'var(--blue)',marginBottom:8}}>
            🔄 Continuing ({continuingColleges.length})
          </div>
          <div style={{fontSize:22,fontWeight:800,color:'var(--blue)',textAlign:'center',padding:'8px 0'}}>
            {continuingColleges.length}
          </div>
          <div style={{fontSize:11,color:'var(--text3)',textAlign:'center',marginBottom:8}}>from {prevYear}</div>
          {(showAllContinuing ? continuingColleges : continuingColleges.slice(0,8)).map(id=>{
            const col = colleges.find(c=>c.id===id);
            return <div key={id} style={{fontSize:11,padding:'3px 0',borderBottom:'1px solid var(--border)',color:'var(--blue)'}}>{col?.college_name||id}</div>;
          })}
          {continuingColleges.length>8&&<div onClick={()=>setShowAllContinuing(v=>!v)} style={{fontSize:11,color:'var(--accent)',marginTop:4,cursor:'pointer'}}>{showAllContinuing?'▲ Show less':`+${continuingColleges.length-8} more`}</div>}
        </div>
        <div className="card">
          <div style={{fontWeight:600,fontSize:12,color:'var(--red)',marginBottom:8}}>
            ❌ Dropped from {prevYear} ({droppedColleges.length})
          </div>
          {(showAllDropped ? droppedColleges : droppedColleges.slice(0,8)).map(id=>{
            const col = colleges.find(c=>c.id===id);
            return <div key={id} style={{fontSize:11,padding:'3px 0',borderBottom:'1px solid var(--border)',color:'var(--red)'}}>{col?.college_name||id}</div>;
          })}
          {droppedColleges.length>8&&<div onClick={()=>setShowAllDropped(v=>!v)} style={{fontSize:11,color:'var(--accent)',marginTop:4,cursor:'pointer'}}>{showAllDropped?'▲ Show less':`+${droppedColleges.length-8} more`}</div>}
          {droppedColleges.length===0&&<div style={{fontSize:11,color:'var(--text3)'}}>None</div>}
        </div>
      </div>

      {/* Winner Funnel Visual */}
      <div className="card">
        <div style={{fontWeight:700,fontSize:13,marginBottom:12}}>🏆 Winner Joining Funnel — {selYear}</div>
        {(() => {
          const yearW = allWinners.filter(w=>String(w.year)===selYear);
          const total = yearW.length;
          if (total===0) return <div style={{color:'var(--text3)',fontSize:12}}>No winners data for {selYear}</div>;
          const joined = yearW.filter(w=>w.join_status==='Joined').length;
          const interested = yearW.filter(w=>w.join_status==='Interested').length;
          const notJoined = yearW.filter(w=>w.join_status==='Not Joined').length;
          const followUp = yearW.filter(w=>w.join_status==='Follow Up').length;
          const pending = yearW.filter(w=>!w.join_status||w.join_status==='Pending').length;
          const bars = [
            {label:'Total Winners', count:total, color:'var(--accent)', pct:100},
            {label:'Contacted', count:total-pending, color:'var(--blue)', pct:((total-pending)/total*100).toFixed(0)},
            {label:'Interested', count:interested+joined, color:'var(--amber)', pct:((interested+joined)/total*100).toFixed(0)},
            {label:'Joined', count:joined, color:'var(--green)', pct:(joined/total*100).toFixed(0)},
          ];
          return (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {bars.map(b=>(
                <div key={b.label} style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:140,fontSize:12,color:'var(--text2)',flexShrink:0}}>{b.label}</div>
                  <div style={{flex:1,height:24,background:'#e5e7eb',borderRadius:4,overflow:'hidden'}}>
                    <div style={{width:b.pct+'%',height:'100%',background:b.color,borderRadius:4,
                      display:'flex',alignItems:'center',paddingLeft:8,transition:'width 0.5s'}}>
                      {b.pct>15&&<span style={{color:'#fff',fontSize:11,fontWeight:600}}>{b.count}</span>}
                    </div>
                  </div>
                  <div style={{width:50,fontSize:12,fontWeight:600,color:b.color,flexShrink:0}}>{b.pct}%</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* College Ratings */}
      <div className="card" style={{marginTop:12}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>⭐ College Ratings (based on participation)</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'var(--accent-light)'}}>
                {['College','Zone','District','Rating','Years','Attendance Rate'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:'left'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {colleges
                .filter(c=>c.college_rating)
                .map(c=>{
                  const colProgs = programs.filter(p=>p.college_id===c.id);
                  const years = new Set(colProgs.map(p=>p.year)).size;
                  const attended = colProgs.reduce((s,p)=>s+(p.students_attended||0),0);
                  const registered = colProgs.reduce((s,p)=>s+(p.registered_students||0),0);
                  const rate = registered>0?Math.min(100,((attended/registered)*100)).toFixed(0):0;
                  return {...c, _rate:Number(rate), _years:years};
                })
                .sort((a,b)=>b._rate-a._rate)
                .map((c,i)=>{
                  const rate = c._rate;
                  const years = c._years;
                  const isExpanded = expandedCollege===c.id;
                  const colProgs2 = programs.filter(p=>p.college_id===c.id);
                  // Score breakdown
                  const scoreYears = years>=5?2:years>=2?1:0;
                  const scoreAtt = rate>=80?2:rate>=50?1:0;
                  const colWins = allWinners.filter(w=>w.college_id===c.id);
                  const joinRate = colWins.length>0?(colWins.filter(w=>w.join_status==='Joined').length/colWins.length*100).toFixed(0):0;
                  const scoreJoin = joinRate>=50?1:0;
                  return (
                    <React.Fragment key={c.id}>
                    <tr style={{borderBottom:'1px solid var(--border)',background:isExpanded?'var(--accent-light)':i%2===0?'':'var(--surface2)',cursor:'pointer'}}
                      onClick={()=>setExpandedCollege(isExpanded?null:c.id)}>
                      <td style={{padding:'6px 10px',fontWeight:500}}>{c.college_name}</td>
                      <td style={{padding:'6px 10px',fontSize:11}}>{c.zone||'—'}</td>
                      <td style={{padding:'6px 10px',fontSize:11}}>{c.district||'—'}</td>
                      <td style={{padding:'6px 10px'}}>
                        <span style={{color:'var(--amber)',fontSize:14}}>{'★'.repeat(c.college_rating||0)}{'☆'.repeat(5-(c.college_rating||0))}</span>
                      </td>
                      <td style={{padding:'6px 10px',textAlign:'center'}}>{years}</td>
                      <td style={{padding:'6px 10px',textAlign:'center'}}>{rate}%</td>
                    </tr>
                    {isExpanded && (
                      <tr style={{background:'var(--accent-light)'}}>
                        <td colSpan={6} style={{padding:'12px 16px'}}>
                          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,fontSize:12}}>
                            {/* Score Breakdown */}
                            <div style={{background:'#fff',padding:10,borderRadius:6,border:'1px solid var(--border)'}}>
                              <div style={{fontWeight:700,marginBottom:6,color:'var(--accent)'}}>⭐ Rating Breakdown</div>
                              <div style={{display:'flex',justifyContent:'space-between'}}><span>Years ({years}y):</span><span style={{fontWeight:600,color:scoreYears>0?'var(--green)':'var(--text3)'}}>+{scoreYears} pts</span></div>
                              <div style={{display:'flex',justifyContent:'space-between'}}><span>Attendance ({rate}%):</span><span style={{fontWeight:600,color:scoreAtt>0?'var(--green)':'var(--text3)'}}>+{scoreAtt} pts</span></div>
                              <div style={{display:'flex',justifyContent:'space-between'}}><span>Winners joined ({joinRate}%):</span><span style={{fontWeight:600,color:scoreJoin>0?'var(--green)':'var(--text3)'}}>+{scoreJoin} pts</span></div>
                              <div style={{borderTop:'1px solid var(--border)',marginTop:6,paddingTop:6,display:'flex',justifyContent:'space-between',fontWeight:700}}>
                                <span>Calculated Rating:</span>
                                <span style={{color:'var(--amber)'}}>{'★'.repeat(Math.min(5,scoreYears+scoreAtt+scoreJoin+1))}{'☆'.repeat(Math.max(0,5-Math.min(5,scoreYears+scoreAtt+scoreJoin+1)))}</span>
                              </div>
                              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                                Stored rating: {'★'.repeat(c.college_rating||0)} — click ⭐ Auto-Rate to update
                              </div>
                            </div>
                            {/* Year-wise breakdown */}
                            <div style={{background:'#fff',padding:10,borderRadius:6,border:'1px solid var(--border)'}}>
                              <div style={{fontWeight:700,marginBottom:6,color:'var(--accent)'}}>📋 Year-wise Programs</div>
                              {colProgs2.sort((a,b)=>b.year-a.year).map(p=>(
                                <div key={p.id} style={{display:'flex',justifyContent:'space-between',padding:'2px 0',borderBottom:'1px solid var(--border)'}}>
                                  <span>{p.year} {p.stream==='Mechanical'?'⚙️':'🏗️'}</span>
                                  <span style={{color:'var(--text3)'}}>{p.students_attended||0}/{p.registered_students||0}</span>
                                  <span className={`badge ${p.status==='Distributed'?'badge-green':p.status==='Pending'?'badge-gray':'badge-yellow'}`} style={{fontSize:9}}>{p.status}</span>
                                </div>
                              ))}
                            </div>
                            {/* Winners */}
                            <div style={{background:'#fff',padding:10,borderRadius:6,border:'1px solid var(--border)'}}>
                              <div style={{fontWeight:700,marginBottom:6,color:'var(--accent)'}}>🏆 Winners ({colWins.length})</div>
                              {colWins.length===0?<div style={{color:'var(--text3)'}}>No winners yet</div>:
                                colWins.map(w=>(
                                  <div key={w.id} style={{padding:'2px 0',borderBottom:'1px solid var(--border)'}}>
                                    <span style={{fontWeight:500}}>{w.year}</span>
                                    <span style={{marginLeft:8,padding:'1px 6px',borderRadius:8,fontSize:10,
                                      background:w.join_status==='Joined'?'var(--green-light)':'var(--amber-light)',
                                      color:w.join_status==='Joined'?'var(--green)':'var(--amber)'}}>
                                      {w.join_status||'Pending'}
                                    </span>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      )}

    </div>
  );
}